import { collection, doc, setDoc, getDoc, updateDoc, getDocs, deleteDoc, query, orderBy, where, addDoc, limit, Timestamp } from 'firebase/firestore';
import { db } from './firebase.js';
import { getCurrentUser } from './auth.js';
import { formatCurrency, formatCurrencyClean, parseCurrencyInput, setupCurrencyInput } from './utils/currency.js';
import { setSynced, setSyncing, setSyncError } from './utils/sync.js';
import achievementEngine, { trackBudgetCompletion, trackBudgetCreation } from './achievements.js';
import { incomeManager } from './incomeManagement.js';
import * as d3 from 'd3';

// Global counter for unique ID generation
let uniqueIdCounter = 0;
function generateUniqueId(prefix) {
  return `${prefix}-${Date.now()}-${++uniqueIdCounter}`;
}

// Cache for recurring expenses calculations
const recurringExpensesCache = new Map();
const RECURRING_CACHE_DURATION = 30000; // 30 seconds

function clearRecurringExpensesCache() {
  recurringExpensesCache.clear();
}

// Export cache clearing function for use when budget changes
window.clearRecurringExpensesCache = clearRecurringExpensesCache;

/**
 * Budget management module for BudgetApp
 */

/**
 * Safely evaluate math expressions
 * Supports basic operations: +, -, *, /, (, )
 * Returns null if invalid expression
 */
function evaluateMathExpression(expression) {
  if (!expression || typeof expression !== 'string') return null;
  
  // Remove spaces and validate characters (only allow numbers, operators, parentheses, decimal points)
  const cleanExpression = expression.replace(/\s/g, '');
  if (!/^[0-9+\-*/().]+$/.test(cleanExpression)) return null;
  
  // Prevent division by zero and other invalid patterns
  if (/\/0(?!\.)/.test(cleanExpression)) return null;
  if (/[+\-*/]{2,}/.test(cleanExpression)) return null; // No consecutive operators
  if (/^[+\-*/]/.test(cleanExpression) && !/^[-]/.test(cleanExpression)) return null; // No leading operators except minus
  if (/[+\-*/]$/.test(cleanExpression)) return null; // No trailing operators
  
  try {
    // Use Function constructor for safe evaluation (better than eval)
    const result = Function(`"use strict"; return (${cleanExpression})`)();
    
    // Validate result is a finite number
    if (typeof result !== 'number' || !isFinite(result)) return null;
    
    return Math.round(result * 100) / 100; // Round to 2 decimal places
  } catch (error) {
    return null;
  }
}

// Global budget state
let budgetVersions = {};
let currentVersionName = null;
let defaultVersionName = null;
let trackedExpenses = [];
let autoSaveTimer = null;
let autoSaveFrequencySetting = '5'; // kept in sync by settings.js via setAutoSaveFrequency()
let isBudgetInitializing = false; // suppresses auto-save during data load

export function setAutoSaveFrequency(value) {
  autoSaveFrequencySetting = value;
  // Cancel any timer that was queued before this setting was known
  if (autoSaveTimer) {
    clearTimeout(autoSaveTimer);
    autoSaveTimer = null;
  }
}

// Clear all budget data (for user switching)
export async function clearBudgetData() {
  console.log('Clearing budget data for user switch');
  budgetVersions = {};
  currentVersionName = null; // Don't set a default for new users
  defaultVersionName = null;
  trackedExpenses = [];
  linkedAccountId = null;
  isLinkedAccount = false;

  // Cancel auto-logging timers so they don't fire for the wrong user
  stopAutoLogging();

  // Clear recurring expenses cache so old user's data isn't used for the new user
  clearRecurringExpensesCache();

  // Clear flexible prompt state so old user's prompts don't leak to new user
  if (typeof window !== 'undefined') {
    window.currentFlexiblePrompt = null;
  }

  // Clear any auto-save timer
  if (autoSaveTimer) {
    clearTimeout(autoSaveTimer);
    autoSaveTimer = null;
  }
  
  // Clear income data
  try {
    const { clearIncomeData } = await import('./incomeManagement.js');
    clearIncomeData();
  } catch (error) {
    console.error('Error clearing income data:', error);
  }

  // Clear expense tracker in-memory state
  try {
    const { clearExpenseTrackerState } = await import('./expenseTracker.js');
    clearExpenseTrackerState();
  } catch (error) {
    console.error('Error clearing expense tracker state:', error);
  }

  // Clear visualization expense cache
  if (typeof window !== 'undefined' && window.clearExpenseCache) {
    window.clearExpenseCache();
  }
  
  // Clear UI elements
  const versionsList = document.getElementById('version-list');
  if (versionsList) {
    versionsList.innerHTML = '';
  }
  
  // Clear current budget display
  const versionNameEl = document.getElementById('current-version-name');
  const lastModifiedEl = document.getElementById('last-modified');
  if (versionNameEl) versionNameEl.textContent = '';
  if (lastModifiedEl) lastModifiedEl.textContent = '';
  
  // Clear the budget interface data
  clearBudgetInterface();
  
  // Ensure welcome screen state is updated
  updateWelcomeScreen();
  
  console.log('Budget data and UI cleared');
}

// Clear budget interface data for new users
function clearBudgetInterface() {
  // Clear income section
  const incomeList = document.getElementById('income-list');
  if (incomeList) {
    incomeList.innerHTML = '';
  }
  
  // Clear all expense categories
  const categoriesContainer = document.querySelector('#budget-form .categories');
  if (categoriesContainer) {
    categoriesContainer.innerHTML = '';
  }
  
  // Reset summary values
  const summaryIncome = document.getElementById('summary-income');
  const summaryExpenses = document.getElementById('summary-expenses');
  const netAmount = document.getElementById('net-amount');
  const savingsRate = document.getElementById('savings-rate');
  
  if (summaryIncome) summaryIncome.textContent = '$0.00';
  if (summaryExpenses) summaryExpenses.textContent = '$0.00';
  if (netAmount) netAmount.textContent = '$0.00';
  if (savingsRate) savingsRate.textContent = '0%';
  
  // Clear any chart
  const chartContainer = document.getElementById('category-donut-chart');
  if (chartContainer) {
    chartContainer.innerHTML = '';
  }
  
  console.log('Budget interface cleared');
}

// Frequency conversion constants
const FREQUENCY_MULTIPLIERS = {
  'monthly': 1,
  'weekly': 4.33, // 52 weeks ÷ 12 months
  'bi-weekly': 2.17, // 26 bi-weekly periods ÷ 12 months
  'quarterly': 1/3, // 4 quarters ÷ 12 months
  'annual': 1/12,
  'one-time': 0, // One-time items don't contribute to monthly totals
  'plan-it': 0 // Plan-it items are calculated separately
};

// Frequency display labels and badges
const FREQUENCY_LABELS = {
  'monthly': { badge: 'Mo', label: 'Monthly' },
  'weekly': { badge: 'Wk', label: 'Weekly' },
  'bi-weekly': { badge: 'Bi', label: 'Bi-weekly' },
  'quarterly': { badge: 'Qt', label: 'Quarterly' },
  'annual': { badge: 'Yr', label: 'Annual' },
  'one-time': { badge: '1x', label: 'One-time' },
  'plan-it': { badge: 'Pl', label: 'Plan It' }
};

// Convert amount to monthly equivalent based on frequency
export function convertToMonthlyAmount(amount, frequency, inputGroup = null) {
  if (frequency === 'plan-it' && inputGroup) {
    // For plan-it, calculate monthly amount based on target date
    const targetDate = inputGroup.getAttribute('data-plan-it-date');
    if (targetDate) {
      const target = new Date(targetDate);
      const today = new Date();
      const monthsUntilTarget = Math.max(1, Math.ceil((target - today) / (1000 * 60 * 60 * 24 * 30.44)));
      return amount / monthsUntilTarget;
    }
    return 0; // If no target date set, don't include in monthly totals
  }
  
  const multiplier = FREQUENCY_MULTIPLIERS[frequency] || 1;
  return amount * multiplier;
}

// Update monthly equivalent indicator for line items
function updateMonthlyIndicator(group, amount, frequency, monthlyAmount) {
  // Remove existing indicator
  const existingIndicator = group.querySelector('.monthly-indicator');
  if (existingIndicator) {
    existingIndicator.remove();
  }
  
  // Only show indicator if frequency is not monthly and amount > 0
  if (frequency !== 'monthly' && amount > 0) {
    const indicator = document.createElement('span');
    indicator.className = 'monthly-indicator';
    
    if (frequency === 'one-time') {
      indicator.textContent = 'One-time';
      indicator.style.color = '#6c757d';
    } else {
      indicator.textContent = `≈ ${formatCurrency(monthlyAmount)}/mo`;
      indicator.style.color = '#28a745';
    }
    
    // Find or create icons wrapper
    const inputOptions = group.querySelector('.input-options');
    if (inputOptions) {
      let iconsWrapper = inputOptions.querySelector('.input-options-icons');
      if (!iconsWrapper) {
        // Create wrapper for icons and move existing icons into it
        iconsWrapper = document.createElement('div');
        iconsWrapper.className = 'input-options-icons';
        
        // Move all existing children (icons) into the wrapper
        while (inputOptions.firstChild) {
          iconsWrapper.appendChild(inputOptions.firstChild);
        }
        
        inputOptions.appendChild(iconsWrapper);
      }
      
      // Insert indicator at the beginning (left side)
      inputOptions.insertBefore(indicator, iconsWrapper);
    }
  }
}

// Toggle recurring configuration panel
export function toggleRecurringPanel(checkbox) {
  const inputGroup = checkbox.closest('.input-group');
  const panel = inputGroup.querySelector('.recurring-panel');
  
  // Check if panel is currently visible
  if (panel.style.display === 'block') {
    // Panel is open - close it without saving
    panel.style.display = 'none';
    panel.innerHTML = '';
    
    // If there's no saved configuration, uncheck the checkbox
    const existingConfig = inputGroup.getAttribute('data-recurring-config');
    if (!existingConfig) {
      checkbox.checked = false;
    } else {
      // Keep checkbox checked if there's saved configuration
      checkbox.checked = true;
    }
  } else {
    // Panel is closed - open it
    checkbox.checked = true;
    panel.style.display = 'block';
    
    // Check if there's existing configuration to restore
    const existingConfig = inputGroup.getAttribute('data-recurring-config');
    if (existingConfig) {
      setupRecurringPanel(panel, JSON.parse(existingConfig));
    } else {
      setupRecurringPanel(panel);
    }
  }
}

// Setup recurring configuration panel
function setupRecurringPanel(panel, existingConfig = null) {
  const timestamp = Date.now();
  const panelHTML = `
    <div class="recurring-config">
      <h4>🔄 Recurring Configuration</h4>
      
      <div class="recurring-type-selection">
        <label>Type:</label>
        <div class="radio-group">
          <label><input type="radio" name="recurring-type-${timestamp}" value="fixed" ${(!existingConfig || existingConfig.type === 'fixed') ? 'checked' : ''}> Fixed Date (15th of each month)</label>
          <label><input type="radio" name="recurring-type-${timestamp}" value="relative" ${existingConfig?.type === 'relative' ? 'checked' : ''}> Relative Date (2nd Tuesday)</label>
          <label><input type="radio" name="recurring-type-${timestamp}" value="flexible" ${existingConfig?.type === 'flexible' ? 'checked' : ''}> Flexible Date (due date ± days)</label>
        </div>
      </div>
      
      <div class="date-config" id="date-config-${timestamp}">
        <!-- Configuration will be set by updateDateConfiguration -->
      </div>
      
      <div class="date-range">
        <div class="date-field">
          <label>Start Date:</label>
          <input type="date" class="start-date" value="${existingConfig?.startDate || new Date().toISOString().split('T')[0]}">
        </div>
        <div class="date-field">
          <label class="end-date-label">
            <input type="checkbox" class="end-date-checkbox" ${existingConfig?.endDate ? 'checked' : ''}>
            End Date
          </label>
          <input type="date" class="end-date" value="${existingConfig?.endDate || ''}" ${existingConfig?.endDate ? '' : 'disabled style="display:none"'}>
        </div>
      </div>
      
      <div class="recurring-description">
        <label>Custom Description:</label>
        <input type="text" class="recurring-desc" placeholder="Optional - override description for recurring instances" value="${existingConfig?.description || ''}">
      </div>
      
      <div class="recurring-actions">
        <button type="button" class="recurring-save-btn" onclick="saveRecurringConfig(this)">💾 Save</button>
        <button type="button" class="recurring-cancel-btn" onclick="cancelRecurringConfig(this)">❌ Cancel</button>
      </div>
    </div>
  `;
  
  panel.innerHTML = panelHTML;
  
  // Setup event listeners for type changes
  const radioButtons = panel.querySelectorAll('input[type="radio"]');
  radioButtons.forEach(radio => {
    radio.addEventListener('change', () => updateDateConfiguration(panel, radio.value));
  });

  // Setup end-date checkbox toggle
  const endDateCheckbox = panel.querySelector('.end-date-checkbox');
  const endDateInput = panel.querySelector('.end-date');
  endDateCheckbox.addEventListener('change', () => {
    if (endDateCheckbox.checked) {
      endDateInput.style.display = '';
      endDateInput.disabled = false;
      if (!endDateInput.value) {
        endDateInput.value = new Date().toISOString().split('T')[0];
      }
    } else {
      endDateInput.style.display = 'none';
      endDateInput.disabled = true;
      endDateInput.value = '';
    }
  });

  // Set up the initial configuration based on selected type
  const selectedType = existingConfig?.type || 'fixed';
  updateDateConfiguration(panel, selectedType, existingConfig);
}

// Update date configuration based on recurring type
function updateDateConfiguration(panel, type, existingConfig = null) {
  const dateConfig = panel.querySelector('.date-config');
  
  switch (type) {
    case 'fixed':
      dateConfig.innerHTML = `
        <div class="fixed-date-config">
          <label>Day of Month:</label>
          <select class="day-select">
            ${Array.from({length: 31}, (_, i) => {
              const day = i + 1;
              const selected = existingConfig?.dayOfMonth == day ? 'selected' : '';
              return `<option value="${day}" ${selected}>${day}</option>`;
            }).join('')}
          </select>
        </div>
      `;
      break;
      
    case 'relative':
      dateConfig.innerHTML = `
        <div class="relative-date-config">
          <label>Week of Month:</label>
          <select class="week-select">
            <option value="1" ${existingConfig?.weekOfMonth == '1' ? 'selected' : ''}>1st</option>
            <option value="2" ${existingConfig?.weekOfMonth == '2' ? 'selected' : ''}>2nd</option>
            <option value="3" ${existingConfig?.weekOfMonth == '3' ? 'selected' : ''}>3rd</option>
            <option value="4" ${existingConfig?.weekOfMonth == '4' ? 'selected' : ''}>4th</option>
            <option value="-1" ${existingConfig?.weekOfMonth == '-1' ? 'selected' : ''}>Last</option>
          </select>
          <label>Day of Week:</label>
          <select class="weekday-select">
            <option value="1" ${existingConfig?.dayOfWeek == '1' ? 'selected' : ''}>Monday</option>
            <option value="2" ${existingConfig?.dayOfWeek == '2' ? 'selected' : ''}>Tuesday</option>
            <option value="3" ${existingConfig?.dayOfWeek == '3' ? 'selected' : ''}>Wednesday</option>
            <option value="4" ${existingConfig?.dayOfWeek == '4' ? 'selected' : ''}>Thursday</option>
            <option value="5" ${existingConfig?.dayOfWeek == '5' ? 'selected' : ''}>Friday</option>
            <option value="6" ${existingConfig?.dayOfWeek == '6' ? 'selected' : ''}>Saturday</option>
            <option value="0" ${existingConfig?.dayOfWeek == '0' ? 'selected' : ''}>Sunday</option>
          </select>
        </div>
      `;
      break;
      
    case 'flexible':
      dateConfig.innerHTML = `
        <div class="flexible-date-config">
          <label>Due Date:</label>
          <select class="due-day-select">
            ${Array.from({length: 31}, (_, i) => {
              const day = i + 1;
              const selected = existingConfig?.dueDay == day ? 'selected' : '';
              return `<option value="${day}" ${selected}>${day}</option>`;
            }).join('')}
          </select>
          <label>Flexibility (± days):</label>
          <select class="flexibility-range">
            <option value="1" ${existingConfig?.flexibility == '1' ? 'selected' : ''}>± 1 day</option>
            <option value="2" ${existingConfig?.flexibility == '2' ? 'selected' : ''}>± 2 days</option>
            <option value="3" ${existingConfig?.flexibility == '3' ? 'selected' : ''}>± 3 days</option>
            <option value="5" ${existingConfig?.flexibility == '5' ? 'selected' : ''}>± 5 days</option>
            <option value="7" ${existingConfig?.flexibility == '7' ? 'selected' : ''}>± 1 week</option>
          </select>
        </div>
      `;
      break;
  }
}

// Save recurring configuration
export function saveRecurringConfig(button) {
  const panel = button.closest('.recurring-panel');
  const inputGroup = panel.closest('.input-group');
  const checkbox = inputGroup.querySelector('.recurring-checkbox');
  
  // Gather configuration data
  const config = {
    type: panel.querySelector('input[name*="recurring-type"]:checked')?.value,
    startDate: panel.querySelector('.start-date')?.value,
    endDate: panel.querySelector('.end-date-checkbox')?.checked ? (panel.querySelector('.end-date')?.value || '') : '',
    description: panel.querySelector('.recurring-desc')?.value
  };
  
  // Gather type-specific configuration
  switch (config.type) {
    case 'fixed':
      config.dayOfMonth = panel.querySelector('.day-select')?.value;
      break;
    case 'relative':
      config.weekOfMonth = panel.querySelector('.week-select')?.value;
      config.dayOfWeek = panel.querySelector('.weekday-select')?.value;
      break;
    case 'flexible':
      config.dueDay = panel.querySelector('.due-day-select')?.value;
      config.flexibility = panel.querySelector('.flexibility-range')?.value;
      break;
  }
  
  // Preserve createdAt from any previously saved config so that editing an
  // existing recurring config doesn't reset the timestamp and accidentally
  // suppress the current month's flexible prompt.
  const existingConfigStr = inputGroup.getAttribute('data-recurring-config');
  const existingConfig = existingConfigStr ? JSON.parse(existingConfigStr) : null;
  config.createdAt = existingConfig?.createdAt ?? new Date().toISOString();
  
  // Store configuration on the input group for later retrieval
  inputGroup.setAttribute('data-recurring-config', JSON.stringify(config));
  
  // Hide the panel and show success feedback
  panel.style.display = 'none';
  
  // Add visual confirmation
  const recurringLabel = inputGroup.querySelector('.recurring-label');
  
  // Show brief success message
  const originalTitle = recurringLabel.title;
  recurringLabel.title = 'Recurring configuration saved ✓';
  setTimeout(() => {
    recurringLabel.title = originalTitle;
  }, 2000);
  
  console.log('Recurring configuration saved:', config);
}

// Cancel recurring configuration
export function cancelRecurringConfig(button) {
  const panel = button.closest('.recurring-panel');
  const inputGroup = panel.closest('.input-group');
  const checkbox = inputGroup.querySelector('.recurring-checkbox');
  
  // Uncheck the recurring checkbox
  checkbox.checked = false;
  
  // Hide the panel and clear its contents
  panel.style.display = 'none';
  panel.innerHTML = '';
  
  // Remove any stored configuration
  inputGroup.removeAttribute('data-recurring-config');
  
  // Reset the recurring label styling
  const recurringLabel = inputGroup.querySelector('.recurring-label');
  recurringLabel.style.opacity = '0.3';
  const recurringIcon = recurringLabel.querySelector('.recurring-icon');
  if (recurringIcon) recurringIcon.style.color = '';
  
  console.log('Recurring configuration cancelled');
}

// Get all recurring expenses from current budget
export function getRecurringExpenses() {
  const recurringExpenses = [];
  
  const categoryContainers = document.querySelectorAll('.category-section');
  categoryContainers.forEach(category => {
    const categoryName = category.querySelector('h3')?.textContent || 'Unknown Category';
    
    const expenseItems = category.querySelectorAll('.input-group');
    expenseItems.forEach(item => {
      const recurringCheckbox = item.querySelector('.recurring-checkbox');
      if (recurringCheckbox && recurringCheckbox.checked) {
        const label = item.querySelector('label')?.textContent || '';
        const input = item.querySelector('.expense-input');
        const amount = parseCurrencyInput(input?.value || '0');
        const configData = item.getAttribute('data-recurring-config');
        
        if (configData && amount > 0) {
          try {
            const config = JSON.parse(configData);
            recurringExpenses.push({
              id: `${getCurrentBudgetId()}-${categoryName}-${label}`,
              categoryName,
              label,
              amount,
              config,
              originalElement: item
            });
          } catch (e) {
            console.warn('Failed to parse recurring config for:', label, e);
          }
        }
      }
    });
  });
  
  return recurringExpenses;
}


// Recurring expense generation algorithms
export function generateRecurringExpenseInstances(recurringExpense, startDate, endDate) {
  const instances = [];
  const config = recurringExpense.config;
  
  if (!config || !config.type) {
    console.warn('Invalid recurring configuration:', config);
    return instances;
  }
  
  const configStart = config.startDate ? new Date(config.startDate) : new Date(startDate);
  const start = new Date(Math.max(configStart.getTime(), new Date(startDate).getTime()));
  const end = config.endDate ? new Date(config.endDate) : new Date(endDate);
  
  switch (config.type) {
    case 'fixed':
      return generateFixedDateInstances(recurringExpense, start, end);
    case 'relative':
      return generateRelativeDateInstances(recurringExpense, start, end);
    case 'flexible':
      return generateFlexibleDateInstances(recurringExpense, start, end);
    default:
      console.warn('Unknown recurring type:', config.type);
      return instances;
  }
}

// Generate instances for fixed date recurring (e.g., 15th of each month)
function generateFixedDateInstances(recurringExpense, startDate, endDate) {
  const instances = [];
  const config = recurringExpense.config;
  const dayOfMonth = parseInt(config.dayOfMonth) || 1;
  
  // Respect the recurring expense's own start date
  const recurringStartDate = config.startDate ? new Date(config.startDate) : new Date(startDate);
  const effectiveStartDate = new Date(Math.max(recurringStartDate.getTime(), startDate.getTime()));
  
  const current = new Date(effectiveStartDate);
  current.setDate(1); // Start at beginning of month
  
  while (current <= endDate) {
    // Set to the target day of month
    const instanceDate = new Date(current);
    instanceDate.setDate(Math.min(dayOfMonth, getDaysInMonth(current)));
    instanceDate.setHours(0, 0, 0, 0); // Normalize to start of day
    
    // Only include if within range and after the recurring expense's start date
    if (instanceDate >= effectiveStartDate && instanceDate <= endDate) {
      instances.push(createExpenseInstance(recurringExpense, instanceDate));
    }
    
    // Move to next month
    current.setMonth(current.getMonth() + 1);
  }
  
  return instances;
}

// Generate instances for relative date recurring (e.g., 2nd Tuesday of month)
function generateRelativeDateInstances(recurringExpense, startDate, endDate) {
  const instances = [];
  const config = recurringExpense.config;
  const weekOfMonth = parseInt(config.weekOfMonth);
  const dayOfWeek = parseInt(config.dayOfWeek);
  
  const current = new Date(startDate);
  current.setDate(1); // Start at beginning of month
  
  while (current <= endDate) {
    const instanceDate = getRelativeDate(current, weekOfMonth, dayOfWeek);
    
    // Only include if within range
    if (instanceDate >= startDate && instanceDate <= endDate) {
      instances.push(createExpenseInstance(recurringExpense, instanceDate));
    }
    
    // Move to next month
    current.setMonth(current.getMonth() + 1);
  }
  
  return instances;
}

// Generate instances for flexible date recurring (e.g., around the 15th ± 3 days)
function generateFlexibleDateInstances(recurringExpense, startDate, endDate) {
  const instances = [];
  const config = recurringExpense.config;
  const dueDay = parseInt(config.dueDay) || 1;
  const flexibility = parseInt(config.flexibility) || 1;
  
  const current = new Date(startDate);
  current.setDate(1); // Start at beginning of month
  
  while (current <= endDate) {
    // Calculate the target date
    const targetDate = new Date(current);
    targetDate.setDate(Math.min(dueDay, getDaysInMonth(current)));
    
    // Create a flexible window
    const earliestDate = new Date(targetDate);
    earliestDate.setDate(targetDate.getDate() - flexibility);
    
    const latestDate = new Date(targetDate);
    latestDate.setDate(targetDate.getDate() + flexibility);
    
    // For now, use the target date (in future could be random within window)
    if (targetDate >= startDate && targetDate <= endDate) {
      const instance = createExpenseInstance(recurringExpense, targetDate);
      instance.flexibleWindow = {
        earliest: earliestDate,
        latest: latestDate,
        target: targetDate
      };
      instances.push(instance);
    }
    
    // Move to next month
    current.setMonth(current.getMonth() + 1);
  }
  
  return instances;
}

// Helper function to create expense instance
function createExpenseInstance(recurringExpense, date) {
  // Create a local date string to avoid timezone issues with toISOString()
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const localDateString = `${year}-${month}-${day}`;
  
  return {
    id: `${recurringExpense.id}-${localDateString}`,
    originalId: recurringExpense.id,
    categoryName: recurringExpense.categoryName,
    label: recurringExpense.config.description || recurringExpense.label,
    amount: recurringExpense.amount,
    dueDate: (() => {
      const dueDate = new Date(date);
      dueDate.setHours(0, 0, 0, 0); // Set to start of day
      return dueDate;
    })(),
    status: 'pending', // pending, completed, skipped
    type: 'recurring',
    recurringType: recurringExpense.config.type,
    originalExpense: recurringExpense
  };
}

// Helper function to get relative date (e.g., 2nd Tuesday)
function getRelativeDate(monthStart, weekOfMonth, dayOfWeek) {
  const firstDay = new Date(monthStart);
  firstDay.setDate(1);
  
  // Find first occurrence of target day
  const firstOccurrence = new Date(firstDay);
  const daysToAdd = (dayOfWeek - firstDay.getDay() + 7) % 7;
  firstOccurrence.setDate(firstDay.getDate() + daysToAdd);
  
  // Calculate target week
  if (weekOfMonth === -1) {
    // Last occurrence of the day in the month
    const lastDay = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0);
    const lastOccurrence = new Date(lastDay);
    const daysToSubtract = (lastDay.getDay() - dayOfWeek + 7) % 7;
    lastOccurrence.setDate(lastDay.getDate() - daysToSubtract);
    return lastOccurrence;
  } else {
    // Nth occurrence
    const targetDate = new Date(firstOccurrence);
    targetDate.setDate(firstOccurrence.getDate() + (weekOfMonth - 1) * 7);
    return targetDate;
  }
}

// Helper function to get days in month
function getDaysInMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
}

// Generate all recurring expenses for a date range
export async function generateAllRecurringExpenses(startDate, endDate) {
  const recurringExpenses = getRecurringExpenses();
  const allInstances = [];
  
  // OPTIMIZATION: Make one batch query instead of N individual queries
  console.log(`🚀 [Optimization Debug] Starting batch query for logged recurring expenses...`);
  const loggedExpenses = await getLoggedRecurringExpensesForPeriod(startDate, endDate);
  console.log(`🚀 [Optimization Debug] Batch query completed. Now processing ${recurringExpenses.length} recurring expense definitions...`);
  
  const statusCheckStart = performance.now();
  
  for (const recurringExpense of recurringExpenses) {
    const instances = generateRecurringExpenseInstances(recurringExpense, startDate, endDate);
    
    // Check completion status for each instance using batch data
    for (const instance of instances) {
      const isLogged = checkIfExpenseAlreadyLoggedFromBatch(recurringExpense, instance.dueDate, loggedExpenses);
      console.log(`[Status Update Debug] ${recurringExpense.label} due ${instance.dueDate.toDateString()}: isLogged=${isLogged}, updating status to ${isLogged ? 'paid' : 'pending'}`);
      if (isLogged) {
        instance.status = 'paid';
      }
    }
    
    allInstances.push(...instances);
  }
  
  const statusCheckEnd = performance.now();
  console.log(`🚀 [Optimization Debug] Status checking (in-memory) took ${(statusCheckEnd - statusCheckStart).toFixed(2)}ms for ${allInstances.length} instances`);
  
  // Sort by due date
  allInstances.sort((a, b) => a.dueDate - b.dueDate);
  
  return allInstances;
}

// Generate recurring expenses for current month
export async function getCurrentMonthRecurringExpenses() {
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  
  // Create cache key for current month
  const cacheKey = `currentMonth_${now.getFullYear()}_${now.getMonth()}`;
  
  // Always show the date range we're supposed to be using
  console.log(`📅 [Date Range Debug] getCurrentMonthRecurringExpenses should use range: ${startOfMonth.toISOString()} to ${endOfMonth.toISOString()}`);
  
  // Check cache first
  if (recurringExpensesCache.has(cacheKey)) {
    const cached = recurringExpensesCache.get(cacheKey);
    if (Date.now() - cached.timestamp < RECURRING_CACHE_DURATION) {
      
      // Debug: Show date range of cached expenses
      const dateRange = cached.value.map(e => e.dueDate).filter(d => d);
      if (dateRange.length > 0) {
        const minDate = new Date(Math.min(...dateRange));
        const maxDate = new Date(Math.max(...dateRange));
        console.log(`📅 [Cached Data Debug] Cached expenses date range: ${minDate.toISOString()} to ${maxDate.toISOString()}`);
      }
      
      // Filter cached data to only include current month to fix multi-month issue
      const filteredCachedData = cached.value.filter(expense => {
        if (!expense.dueDate) return false;
        const dueDate = new Date(expense.dueDate);
        return dueDate >= startOfMonth && dueDate <= endOfMonth;
      });
      
      console.log(`🔧 [Fix Debug] Filtered cached data from ${cached.value.length} to ${filteredCachedData.length} expenses for current month`);
      
      return filteredCachedData;
    }
  }
  
  console.log(`📅 [Date Range Debug] Using date range: ${startOfMonth.toISOString()} to ${endOfMonth.toISOString()}`);
  const computeStart = performance.now();
  
  const result = await generateAllRecurringExpenses(startOfMonth, endOfMonth);
  
  const computeEnd = performance.now();
  
  // Cache the result
  recurringExpensesCache.set(cacheKey, {
    value: result,
    timestamp: Date.now()
  });
  
  return result;
}

// Generate recurring expenses for next 3 months
export function getUpcomingRecurringExpenses() {
  const now = new Date();
  const startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const endDate = new Date(now.getFullYear(), now.getMonth() + 3, 0);
  
  return generateAllRecurringExpenses(startDate, endDate);
}

// Test function for recurring generation algorithms
export function testRecurringGeneration() {
  console.log('=== Recurring Expense Generation Test ===');
  
  const recurringExpenses = getRecurringExpenses();
  console.log(`Found ${recurringExpenses.length} recurring expenses:`, recurringExpenses);
  
  if (recurringExpenses.length === 0) {
    console.log('No recurring expenses found. Please configure some first.');
    return;
  }
  
  // Test current month
  const currentMonth = getCurrentMonthRecurringExpenses();
  console.log(`Current month instances (${currentMonth.length}):`, currentMonth);
  
  // Test upcoming 3 months
  const upcoming = getUpcomingRecurringExpenses();
  console.log(`Upcoming 3 months instances (${upcoming.length}):`, upcoming);
  
  // Test each type individually
  recurringExpenses.forEach(expense => {
    console.log(`\nTesting ${expense.label} (${expense.config.type}):`);
    const testStart = new Date();
    const testEnd = new Date();
    testEnd.setMonth(testEnd.getMonth() + 2);
    
    const instances = generateRecurringExpenseInstances(expense, testStart, testEnd);
    console.log(`Generated ${instances.length} instances:`, instances.map(i => ({
      date: i.dueDate.toDateString(),
      label: i.label,
      amount: i.amount
    })));
  });
  
  console.log('Generation test complete!');
}

// Calendar view functionality
let currentCalendarDate = new Date();

export async function initializeRecurringCalendar() {
  currentCalendarDate = new Date();
  await renderCalendar();
  
  // Add resize listener to re-render when switching between mobile/desktop
  let resizeTimeout;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(async () => {
      await renderCalendar();
    }, 250); // Debounce resize events
  });
}

export async function navigateCalendar(direction) {
  currentCalendarDate.setMonth(currentCalendarDate.getMonth() + direction);
  await renderCalendar();
}

export async function renderCalendar() {
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  
  // Update month/year header
  const monthYearEl = document.getElementById('calendar-month-year');
  if (monthYearEl) {
    monthYearEl.textContent = `${monthNames[currentCalendarDate.getMonth()]} ${currentCalendarDate.getFullYear()}`;
  }
  
  // Get calendar data
  const startOfMonth = new Date(currentCalendarDate.getFullYear(), currentCalendarDate.getMonth(), 1);
  const endOfMonth = new Date(currentCalendarDate.getFullYear(), currentCalendarDate.getMonth() + 1, 0);
  
  // Generate recurring expenses for this month
  const allRecurringExpenses = await generateAllRecurringExpenses(startOfMonth, endOfMonth);
  
  // Filter to only include this month to fix multi-month cache issue
  const monthlyExpenses = allRecurringExpenses.filter(expense => {
    if (!expense.dueDate) return false;
    const dueDate = new Date(expense.dueDate);
    return dueDate >= startOfMonth && dueDate <= endOfMonth;
  });
  
  console.log('📅 [Calendar Debug] Filtered calendar expenses:', {
    totalFromGenerate: allRecurringExpenses.length,
    filteredToMonth: monthlyExpenses.length,
    monthRange: `${startOfMonth.toISOString()} to ${endOfMonth.toISOString()}`,
    totalAmount: monthlyExpenses.reduce((sum, exp) => sum + exp.amount, 0)
  });
  
  // Update summary stats
  updateCalendarSummary(monthlyExpenses);
  
  // Render calendar grid
  renderCalendarGrid(startOfMonth, endOfMonth, monthlyExpenses, dayNames);
}

function updateCalendarSummary(expenses) {
  const totalAmount = expenses.reduce((sum, expense) => sum + expense.amount, 0);
  const recurringCount = expenses.length;
  
  const totalEl = document.getElementById('current-month-total');
  const countEl = document.getElementById('recurring-count');
  
  if (totalEl) {
    // Round to nearest dollar for cleaner display
    const roundedAmount = Math.round(totalAmount);
    totalEl.textContent = formatCurrencyClean(roundedAmount);
  }
  
  if (countEl) {
    countEl.textContent = recurringCount;
  }
}

function renderCalendarGrid(startOfMonth, endOfMonth, expenses, dayNames) {
  const gridEl = document.getElementById('calendar-grid');
  if (!gridEl) return;
  
  // Clear existing content
  gridEl.innerHTML = '';
  
  // Add day headers
  dayNames.forEach(dayName => {
    const headerCell = document.createElement('div');
    headerCell.className = 'calendar-header-cell';
    headerCell.textContent = dayName;
    gridEl.appendChild(headerCell);
  });
  
  // Calculate calendar boundaries
  const firstDay = new Date(startOfMonth);
  const lastDay = new Date(endOfMonth);
  
  // Start from Sunday of the week containing the first day
  const calendarStart = new Date(firstDay);
  calendarStart.setDate(firstDay.getDate() - firstDay.getDay());
  
  // End on Saturday of the week containing the last day
  const calendarEnd = new Date(lastDay);
  calendarEnd.setDate(lastDay.getDate() + (6 - lastDay.getDay()));
  
  // Group expenses by date for quick lookup
  const expensesByDate = {};
  expenses.forEach(expense => {
    const dateKey = expense.dueDate.toDateString();
    if (!expensesByDate[dateKey]) {
      expensesByDate[dateKey] = [];
    }
    expensesByDate[dateKey].push(expense);
  });
  
  // Generate calendar days
  for (let date = new Date(calendarStart); date <= calendarEnd; date.setDate(date.getDate() + 1)) {
    const dayEl = createCalendarDay(date, startOfMonth, endOfMonth, expensesByDate);
    gridEl.appendChild(dayEl);
  }
}

function createCalendarDay(date, startOfMonth, endOfMonth, expensesByDate) {
  const dayEl = document.createElement('div');
  dayEl.className = 'calendar-day';
  
  const dateKey = date.toDateString();
  const isCurrentMonth = date >= startOfMonth && date <= endOfMonth;
  const isToday = date.toDateString() === new Date().toDateString();
  
  // Add CSS classes
  if (!isCurrentMonth) {
    dayEl.classList.add('other-month');
  }
  if (isToday) {
    dayEl.classList.add('today');
  }
  
  // Add day number
  const dayNumber = document.createElement('div');
  dayNumber.className = 'day-number';
  dayNumber.textContent = date.getDate();
  dayEl.appendChild(dayNumber);
  
  // Add expenses for this day
  const dayExpenses = expensesByDate[dateKey] || [];
  if (dayExpenses.length > 0) {
    // Check if mobile view
    const isMobile = window.innerWidth <= 768;
    
    if (isMobile) {
      // Mobile: Show badge with count
      const badge = createExpenseBadge(dayExpenses, date);
      dayEl.appendChild(badge);
    } else {
      // Desktop: Show full expense details
      const expensesContainer = document.createElement('div');
      expensesContainer.className = 'day-expenses';
      
      dayExpenses.forEach(expense => {
        const expenseEl = createExpenseElement(expense);
        expensesContainer.appendChild(expenseEl);
      });
      
      dayEl.appendChild(expensesContainer);
    }
  }
  
  return dayEl;
}

function createExpenseBadge(expenses, date) {
  const badge = document.createElement('div');
  badge.className = 'expense-badge';
  badge.textContent = expenses.length;
  
  // Determine badge color based on predominant recurring type
  const typeCounts = expenses.reduce((counts, expense) => {
    counts[expense.recurringType] = (counts[expense.recurringType] || 0) + 1;
    return counts;
  }, {});
  
  // Find the most common recurring type
  const predominantType = Object.keys(typeCounts).reduce((a, b) => 
    typeCounts[a] > typeCounts[b] ? a : b
  );
  
  // Add color class based on predominant type
  badge.classList.add(predominantType);
  
  // Add click handler to toggle tooltip
  badge.addEventListener('click', (e) => {
    e.stopPropagation();
    
    // Check if tooltip is already open for this badge
    const existingTooltip = document.querySelector('.expense-tooltip');
    if (existingTooltip && existingTooltip.dataset.badgeId === badge.dataset.badgeId) {
      // Close if clicking the same badge
      existingTooltip.remove();
    } else {
      // Show tooltip (this will close any existing tooltip first)
      showExpenseTooltip(expenses, badge, date);
    }
  });
  
  // Add unique ID for toggle tracking
  badge.dataset.badgeId = `badge-${date.getTime()}-${Math.random().toString(36).substr(2, 9)}`;
  
  return badge;
}

function createExpenseElement(expense, isTooltip = false) {
  const expenseEl = document.createElement('div');
  expenseEl.className = `expense-item ${expense.recurringType}`;
  expenseEl.title = `${expense.label} - ${formatCurrency(expense.amount)} (${expense.recurringType})`;
  
  if (isTooltip) {
    // Clean format for tooltip: "Label: $Amount"
    expenseEl.innerHTML = `${expense.label}: ${formatCurrency(expense.amount)}`;
    expenseEl.style.fontSize = 'var(--font-sm)';
    expenseEl.style.padding = 'var(--spacing-xs) 0';
  } else {
    // Truncated for desktop calendar view
    const label = expense.label.length > 12 ? expense.label.substring(0, 10) + '...' : expense.label;
    expenseEl.innerHTML = `
      <div>${label}</div>
      <div class="expense-amount">${formatCurrency(expense.amount)}</div>
    `;
    
    // Add click handler for desktop
    expenseEl.addEventListener('click', () => {
      console.log('Expense clicked:', expense);
      // Future: Show expense details modal for desktop
    });
  }
  
  return expenseEl;
}

function showExpenseTooltip(expenses, badge, date) {
  // Remove any existing tooltips
  const existingTooltip = document.querySelector('.expense-tooltip');
  if (existingTooltip) {
    existingTooltip.remove();
  }
  
  // Create tooltip container
  const tooltip = document.createElement('div');
  tooltip.className = 'expense-tooltip';
  tooltip.dataset.badgeId = badge.dataset.badgeId;
  
  // Add header with date
  const header = document.createElement('div');
  header.className = 'tooltip-header';
  header.textContent = date.toLocaleDateString('en-US', { 
    weekday: 'short', 
    month: 'short', 
    day: 'numeric' 
  });
  tooltip.appendChild(header);
  
  // Add expenses
  const expenseList = document.createElement('div');
  expenseList.className = 'tooltip-expenses';
  
  expenses.forEach(expense => {
    const expenseEl = createExpenseElement(expense, true); // true = isTooltip
    expenseList.appendChild(expenseEl);
  });
  
  tooltip.appendChild(expenseList);
  
  // Add close button
  const closeBtn = document.createElement('button');
  closeBtn.className = 'tooltip-close';
  closeBtn.innerHTML = '×';
  closeBtn.addEventListener('click', () => tooltip.remove());
  tooltip.appendChild(closeBtn);
  
  // Position tooltip near the badge
  document.body.appendChild(tooltip);
  positionTooltip(tooltip, badge);
  
  // Close tooltip when clicking outside
  setTimeout(() => {
    document.addEventListener('click', function closeTooltip(e) {
      if (!tooltip.contains(e.target)) {
        tooltip.remove();
        document.removeEventListener('click', closeTooltip);
      }
    });
  }, 100);
}

function positionTooltip(tooltip, badge) {
  const badgeRect = badge.getBoundingClientRect();
  
  // Force a layout recalculation to get accurate tooltip dimensions
  tooltip.style.visibility = 'hidden';
  tooltip.style.display = 'block';
  const tooltipRect = tooltip.getBoundingClientRect();
  tooltip.style.visibility = 'visible';
  
  // Position above the badge by default, closer distance
  let top = badgeRect.top - tooltipRect.height - 5;
  let left = badgeRect.left + (badgeRect.width / 2) - (tooltipRect.width / 2);
  
  // Adjust if tooltip would go off screen
  if (top < 10) {
    // Position below instead, closer distance
    top = badgeRect.bottom + 5;
  }
  
  // Ensure minimum margins from screen edges
  const minMargin = 20;
  if (left < minMargin) {
    left = minMargin;
  } else if (left + tooltipRect.width > window.innerWidth - minMargin) {
    left = window.innerWidth - tooltipRect.width - minMargin;
  }
  
  // Apply positioning
  tooltip.style.position = 'fixed';
  tooltip.style.top = `${Math.max(top, 10)}px`;
  tooltip.style.left = `${left}px`;
  tooltip.style.zIndex = '1000';
  
  console.log('Tooltip positioned at:', { top, left, badgeRect, tooltipRect }); // Debug log
}

// Account linking state
let linkedAccountId = null;
let isLinkedAccount = false;

// Get current total income from DOM
export function getTotalIncome() {
  const incomeInputs = document.querySelectorAll('.income-input');
  let totalIncome = 0;
  incomeInputs.forEach(input => {
    const inputGroup = input.closest('.input-group');
    const frequencySelect = inputGroup.querySelector('.frequency-select');
    const amount = parseCurrencyInput(input.value);
    const frequency = frequencySelect ? frequencySelect.value : 'monthly';
    
    // Convert to monthly amount using the same logic as expenses
    let monthlyAmount = convertToMonthlyAmount(amount, frequency, inputGroup);
    
    // Apply tax calculations if this is pre-tax income
    const taxType = inputGroup.getAttribute('data-tax-type') || 'post-tax';
    if (taxType === 'pre-tax') {
      const federalRate = parseFloat(inputGroup.getAttribute('data-federal-tax')) || 22;
      const stateRate = parseFloat(inputGroup.getAttribute('data-state-tax')) || 5;
      const ficaRate = 7.65; // Fixed FICA rate
      const totalTaxRate = federalRate + stateRate + ficaRate;
      
      // Calculate net amount (after taxes)
      monthlyAmount = monthlyAmount * (1 - totalTaxRate / 100);
    }
    
    totalIncome += monthlyAmount;
  });
  return totalIncome;
}

// Calculate totals for current budget
export function calculateTotals() {
  // Calculate income total using frequency and tax adjustments
  const totalIncome = getTotalIncome();

  // Calculate expense totals by category
  const categoryTotals = {};
  let totalExpenses = 0;
  let totalTaxDeductible = 0;
  
  const categoryContainers = document.querySelectorAll('.category-section');
  categoryContainers.forEach(category => {
    const categoryName = category.querySelector('h3')?.textContent || 'Unknown';
    const inputGroups = category.querySelectorAll('.input-group');
    let categoryTotal = 0;
    let categoryTaxDeductible = 0;
    
    inputGroups.forEach(group => {
      const input = group.querySelector('.expense-input');
      const frequencySelect = group.querySelector('.frequency-select');
      const taxCheckbox = group.querySelector('.tax-deductible-checkbox');
      const amount = parseCurrencyInput(input?.value || '0');
      const frequency = frequencySelect?.value || 'monthly';
      
      // Convert to monthly equivalent for budget calculations
      const monthlyAmount = convertToMonthlyAmount(amount, frequency, group);
      
      categoryTotal += monthlyAmount;
      
      if (taxCheckbox?.checked) {
        categoryTaxDeductible += monthlyAmount;
        totalTaxDeductible += monthlyAmount;
      }
      
      // Show monthly equivalent tooltip/indicator if different from original
      updateMonthlyIndicator(group, amount, frequency, monthlyAmount);
    });
    
    categoryTotals[categoryName] = categoryTotal;
    totalExpenses += categoryTotal;
    
    // Update category subtotal display (showing monthly equivalent)
    const subtotalElement = category.querySelector('.category-total');
    if (subtotalElement) {
      subtotalElement.textContent = `${formatCurrencyClean(categoryTotal)}/mo`;
    }
    
    // Update target vs actual if targets are set
    updateCategoryTargetDisplay(category, categoryTotal);
    
    // Update target icon state
    updateTargetIconState(category);
  });

  // Calculate net amount and savings rate (including savings categories)
  const netAmount = totalIncome - totalExpenses;
  
  // Calculate total savings (net amount + any expense items marked as savings)
  let totalSavings = Math.max(0, netAmount); // Only positive net amount counts as savings
  const savingsBreakdown = [];
  
  // Add net amount to breakdown if positive
  if (netAmount > 0) {
    savingsBreakdown.push({ name: 'Amount under budget', amount: netAmount });
  }
  
  categoryContainers.forEach(category => {
    const inputGroups = category.querySelectorAll('.input-group');
    inputGroups.forEach(group => {
      const input = group.querySelector('.expense-input');
      const labelElement = group.querySelector('label[contenteditable]');
      const savingsCheckbox = group.querySelector('.savings-checkbox');
      const itemName = labelElement?.textContent || '';
      
      // Check if savings checkbox is checked
      if (savingsCheckbox && savingsCheckbox.checked) {
        const frequencySelect = group.querySelector('.frequency-select');
        const amount = parseCurrencyInput(input?.value || '0');
        const frequency = frequencySelect?.value || 'monthly';
        const monthlyAmount = convertToMonthlyAmount(amount, frequency, group);
        
        if (monthlyAmount > 0) {
          totalSavings += monthlyAmount;
          savingsBreakdown.push({ name: itemName, amount: monthlyAmount });
        }
      }
    });
  });
  
  const savingsRate = totalIncome > 0 ? (totalSavings / totalIncome) * 100 : 0;

  // Update summary displays
  updateSummaryDisplay(totalIncome, totalExpenses, netAmount, savingsRate, totalTaxDeductible, savingsBreakdown);
  
  // Track achievements based on budget calculations
  trackBudgetAchievements(totalIncome, totalExpenses, netAmount);
  
  // Update donut chart
  createCategoryDonutChart(categoryTotals, totalExpenses);
  
  // Update category targets (for percentage-based targets when income changes)
  updateAllCategoryTargets();
  
  // Clear recurring expenses cache since budget data may have changed
  if (typeof window !== 'undefined' && window.clearRecurringExpensesCache) {
    window.clearRecurringExpensesCache();
  }
  
  // Auto-save if enabled
  scheduleAutoSave();
}

/**
 * Track budget-related achievements
 */
async function trackBudgetAchievements(totalIncome, totalExpenses, netAmount) {
  try {
    const user = getCurrentUser();
    if (!user) return;

    // Check if user is under budget (has money left over)
    const isUnderBudget = netAmount > 0;
    const savedAmount = Math.max(0, netAmount);
    const budgetVariancePercentage = totalIncome > 0 ? Math.abs(netAmount) / totalIncome * 100 : 0;

    // Only track achievements for meaningful budget data
    if (totalIncome > 0 && totalExpenses > 0) {
      
      // Track if this is close to end of month to trigger monthly achievements
      const now = new Date();
      const isEndOfMonth = now.getDate() >= 28; // Consider last few days of month
      
      if (isEndOfMonth && isUnderBudget) {
        // This represents a completed month under budget
        const budgetData = {
          totalIncome,
          totalExpenses,
          netAmount,
          savedAmount,
          budgetVariancePercentage
        };
        
        const monthKey = `${now.getFullYear()}-${now.getMonth() + 1}`;
        await trackBudgetCompletion(user.uid, budgetData, monthKey);
      }

      // For precision spender achievement (within 5% of budget)
      if (isUnderBudget && budgetVariancePercentage <= 5) {
        // Mark precision spender progress
        await achievementEngine.updateProgress('cumulativeStats.precisionMonths', 
          (achievementEngine.progressData?.cumulativeStats?.precisionMonths || 0) + 1);
      }

      // Track total savings for savings milestones
      if (savedAmount > 0) {
        // Update cumulative savings (this is separate from monthly completion tracking)
        const currentSavings = achievementEngine.progressData?.cumulativeStats?.totalSavedUnderBudget || 0;
        await achievementEngine.updateProgress('cumulativeStats.totalSavedUnderBudget', currentSavings + savedAmount);
      }
    }

  } catch (error) {
    console.error('Error tracking budget achievements:', error);
  }
}

// Calculate totals for specific budget data (used for comparisons)
export function calculateDataTotals(data) {
  if (!data || !data.income || !data.categories) {
    return { income: 0, expenses: 0, net: 0, savingsRate: 0 };
  }

  const income = data.income.reduce((sum, item) => sum + (item.amount || 0), 0);
  const expenses = data.categories.reduce((sum, category) => {
    return sum + category.items.reduce((catSum, item) => catSum + (item.amount || 0), 0);
  }, 0);
  
  const net = income - expenses;
  
  // Calculate total savings (net amount + any expense items with "savings" in the name)
  let totalSavings = Math.max(0, net); // Only positive net amount counts as savings
  data.categories.forEach(category => {
    category.items.forEach(item => {
      if (item.label && item.label.toLowerCase().includes('savings')) {
        totalSavings += (item.amount || 0);
      }
    });
  });
  
  const savingsRate = income > 0 ? (totalSavings / income) * 100 : 0;

  return { income, expenses, net, savingsRate };
}

// Update summary display elements
function updateSummaryDisplay(income, expenses, net, savingsRate, taxDeductible = 0, savingsBreakdown = []) {
  // Update total displays
  const totalIncomeEl = document.getElementById('total-income');
  const totalExpensesEl = document.getElementById('total-expenses');
  const summaryIncomeEl = document.getElementById('summary-income');
  const summaryExpensesEl = document.getElementById('summary-expenses');
  const netAmountEl = document.getElementById('net-amount');
  const savingsRateEl = document.getElementById('savings-rate');
  const taxDeductibleEl = document.getElementById('tax-deductible-total');

  if (totalIncomeEl) totalIncomeEl.textContent = formatCurrency(income);
  if (totalExpensesEl) totalExpensesEl.textContent = formatCurrency(expenses);
  if (summaryIncomeEl) summaryIncomeEl.textContent = formatCurrency(income);
  if (summaryExpensesEl) summaryExpensesEl.textContent = formatCurrency(expenses);
  
  if (netAmountEl) {
    netAmountEl.textContent = formatCurrency(net);
    netAmountEl.className = net >= 0 ? 'amount positive' : 'amount negative';
  }
  
  // Update tax deductible total and show/hide section
  if (taxDeductibleEl) {
    taxDeductibleEl.textContent = formatCurrency(taxDeductible);
    const taxSummarySection = document.querySelector('.tax-deductible-summary');
    if (taxSummarySection) {
      taxSummarySection.style.display = taxDeductible > 0 ? 'block' : 'none';
    }
  }
  
  if (savingsRateEl) {
    savingsRateEl.textContent = `${savingsRate.toFixed(1)}%`;
    savingsRateEl.className = savingsRate >= 0 ? 'amount positive' : 'amount negative';
    
    // Create savings breakdown tooltip
    if (savingsBreakdown && savingsBreakdown.length > 0) {
      const tooltipText = savingsBreakdown
        .map(item => `${item.name}: ${formatCurrency(item.amount)}`)
        .join('\n');
      savingsRateEl.title = `Savings Breakdown:\n${tooltipText}`;
    } else {
      savingsRateEl.title = 'No savings items found';
    }
  }
}

// Create donut chart for category allocation
function createCategoryDonutChart(categoryTotals, totalExpenses) {
  // Get current total income to display in center
  const totalIncome = getTotalIncome();
  const container = d3.select('#category-donut-chart');
  
  // Clear existing chart
  container.selectAll('*').remove();
  
  // Show loading animation first
  const loadingContainer = container.append('div')
    .style('display', 'flex')
    .style('flex-direction', 'column')
    .style('align-items', 'center')
    .style('justify-content', 'center')
    .style('height', '200px')
    .style('width', '200px');
    
  const loadingSpinner = loadingContainer.append('div')
    .attr('class', 'chart-loading-spinner')
    .style('width', '30px')
    .style('height', '30px')
    .style('border', '3px solid rgba(246, 143, 76, 0.2)')
    .style('border-top', '3px solid #f68f4c')
    .style('border-radius', '50%')
    .style('animation', 'spin 1s linear infinite')
    .style('margin-bottom', '10px');
    
  loadingContainer.append('div')
    .style('color', '#6c757d')
    .style('font-size', '12px')
    .style('text-align', 'center')
    .text('Loading chart...');
  
  // Delay to show loading animation, then create chart
  setTimeout(() => {
    container.selectAll('*').remove();
    
    if (totalExpenses === 0) {
      container.append('div')
        .style('text-align', 'center')
        .style('padding', '60px 0')
        .style('color', '#6c757d')
        .style('font-size', '14px')
        .text('No expenses to display');
      return;
    }
    
    const width = 200;
    const height = 200;
    const radius = Math.min(width, height) / 2 - 10;
    
    const svg = container.append('svg')
      .attr('width', width)
      .attr('height', height)
      .style('opacity', 0); // Start invisible for fade-in animation
      
    const g = svg.append('g')
      .attr('transform', `translate(${width / 2}, ${height / 2})`);
    
    // Convert category data to chart format
    const categoryData = Object.entries(categoryTotals)
      .filter(([_, total]) => total > 0)
      .map(([category, total]) => ({
        category,
        amount: total,
        percentage: ((total / totalIncome) * 100).toFixed(1)
      }))
      .sort((a, b) => b.amount - a.amount); // Sort by amount descending
    
    // Add unallocated amount (remaining income) as a green slice
    const unallocatedAmount = Math.max(0, totalIncome - totalExpenses);
    let data = [...categoryData]; // Start with sorted categories
    
    if (unallocatedAmount > 0) {
      data.push({
        category: 'Unallocated',
        amount: unallocatedAmount,
        percentage: ((unallocatedAmount / totalIncome) * 100).toFixed(1)
      });
    }
    
    // Color scale - custom color palette with green for unallocated
    const colors = [
      '#B85D2A', '#F68F4C', '#F6C44C', '#636E72', '#F6574C',
      // Lighter versions of the same colors
      '#D17A4A', '#F8A16F', '#F8D26F', '#8A9499', '#F8796F'
    ];
    
    // Create color function that handles unallocated specially
    const color = (category) => {
      if (category === 'Unallocated') {
        return '#28a745'; // Green for unallocated
      }
      // For other categories, use the color scale
      const colorScale = d3.scaleOrdinal()
        .domain(data.filter(d => d.category !== 'Unallocated').map(d => d.category))
        .range(colors);
      return colorScale(category);
    };
    
    // Create arc generator
    const arc = d3.arc()
      .outerRadius(radius)
      .innerRadius(radius * 0.6); // Makes it a donut
    
    // Create arc generator for animation (starts from 0)
    const arcAnimation = d3.arc()
      .outerRadius(radius)
      .innerRadius(radius * 0.6);
    
    // Create pie layout
    const pie = d3.pie()
      .value(d => d.amount)
      .sort(null);
    
    // Create arcs
    const arcs = g.selectAll('.arc')
      .data(pie(data))
      .enter().append('g')
      .attr('class', 'arc');
    
    // Add paths with animation
    const paths = arcs.append('path')
      .attr('fill', d => color(d.data.category))
      .attr('stroke', 'white')
      .attr('stroke-width', 2)
      .style('cursor', 'pointer')
      .each(function(d) {
        this._current = { startAngle: 0, endAngle: 0 }; // Store initial angles
      })
      .on('mouseover', function(event, d) {
        d3.select(this).style('opacity', 0.8);
        
        // Show tooltip
        const tooltip = d3.select('body').append('div')
          .attr('class', 'donut-tooltip')
          .style('position', 'absolute')
          .style('background', 'rgba(0, 0, 0, 0.8)')
          .style('color', 'white')
          .style('padding', '8px')
          .style('border-radius', '4px')
          .style('font-size', '12px')
          .style('pointer-events', 'none')
          .style('z-index', '1000')
          .html(`<strong>${d.data.category}</strong><br/>${formatCurrency(d.data.amount)} (${d.data.percentage}%)`);
        
        tooltip.style('left', (event.pageX + 10) + 'px')
          .style('top', (event.pageY - 10) + 'px');
      })
      .on('mouseout', function() {
        d3.select(this).style('opacity', 1);
        d3.selectAll('.donut-tooltip').remove();
      });
    
    // Animate the arcs growing from 0 to full size
    paths.transition()
      .duration(1500)
      .ease(d3.easeQuadOut)
      .attrTween('d', function(d) {
        const interpolate = d3.interpolate(this._current, d);
        this._current = interpolate(0);
        return function(t) {
          return arc(interpolate(t));
        };
      });
    
    // Calculate percent budgeted
    const percentBudgeted = totalIncome > 0 ? ((totalExpenses / totalIncome) * 100) : 0;
    
    // Determine color based on percentage
    let percentageColor;
    if (percentBudgeted < 95) {
      percentageColor = '#28a745'; // Green
    } else if (percentBudgeted <= 100) {
      percentageColor = '#ffc107'; // Orange
    } else {
      percentageColor = '#dc3545'; // Red
    }
    
    // Add center text showing percent budgeted with fade-in animation
    const centerTextGroup = g.append('g')
      .style('opacity', 0);
    
    // Percentage metric (larger, on top)
    centerTextGroup.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', '-0.2em')
      .style('font-size', '20px')
      .style('font-weight', '700')
      .style('fill', percentageColor)
      .text(percentBudgeted.toFixed(1) + '%');
      
    // Label (smaller, underneath)
    centerTextGroup.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', '1.2em')
      .style('font-size', '12px')
      .style('font-weight', '600')
      .style('fill', '#333')
      .text('Budgeted');
    
    // Fade in the entire chart
    svg.transition()
      .duration(300)
      .style('opacity', 1);
      
    // Fade in center text after arcs animation
    centerTextGroup.transition()
      .delay(400)
      .duration(400)
      .style('opacity', 1);
      
  }, 500); // Show loading for 500ms
}

// Render donut chart into an arbitrary DOM element (no animation, for offscreen/PDF use)
export function renderDonutChartToElement(containerEl, categoryTotals, totalExpenses, totalIncome) {
  const container = d3.select(containerEl);
  container.selectAll('*').remove();

  const width = 200, height = 200;
  const radius = Math.min(width, height) / 2 - 10;

  if (totalExpenses === 0) {
    container.append('div')
      .style('text-align', 'center')
      .style('padding', '60px 0')
      .style('color', '#6c757d')
      .style('font-size', '14px')
      .text('No expenses to display');
    return;
  }

  const svg = container.append('svg')
    .attr('width', width)
    .attr('height', height)
    .attr('viewBox', `0 0 ${width} ${height}`);

  const g = svg.append('g')
    .attr('transform', `translate(${width / 2}, ${height / 2})`);

  const colors = [
    '#B85D2A', '#F68F4C', '#F6C44C', '#636E72', '#F6574C',
    '#D17A4A', '#F8A16F', '#F8D26F', '#8A9499', '#F8796F'
  ];

  const categoryData = Object.entries(categoryTotals)
    .filter(([_, total]) => total > 0)
    .map(([category, total]) => ({ category, amount: total }))
    .sort((a, b) => b.amount - a.amount);

  const unallocated = Math.max(0, totalIncome - totalExpenses);
  if (unallocated > 0) categoryData.push({ category: 'Unallocated', amount: unallocated });

  const colorScale = d3.scaleOrdinal()
    .domain(categoryData.filter(d => d.category !== 'Unallocated').map(d => d.category))
    .range(colors);
  const color = d => d.category === 'Unallocated' ? '#28a745' : colorScale(d.category);

  const arc = d3.arc().outerRadius(radius).innerRadius(radius * 0.6);
  const pie = d3.pie().value(d => d.amount).sort(null);

  g.selectAll('.arc')
    .data(pie(categoryData))
    .enter().append('path')
    .attr('d', arc)
    .attr('fill', d => color(d.data))
    .attr('stroke', 'white')
    .attr('stroke-width', 2);

  const percentBudgeted = totalIncome > 0 ? (totalExpenses / totalIncome) * 100 : 0;
  const percentColor = percentBudgeted < 95 ? '#28a745' : percentBudgeted <= 100 ? '#ffc107' : '#dc3545';

  g.append('text').attr('text-anchor', 'middle').attr('dy', '-0.2em')
    .style('font-size', '20px').style('font-weight', '700').style('fill', percentColor)
    .text(percentBudgeted.toFixed(1) + '%');

  g.append('text').attr('text-anchor', 'middle').attr('dy', '1.2em')
    .style('font-size', '12px').style('font-weight', '600').style('fill', '#333')
    .text('Budgeted');
}

// Capture current budget data from DOM
export function captureBudgetData() {
  const data = {
    income: [],
    categories: []
  };

  // Capture income items
  const incomeItems = document.querySelectorAll('#income-items .input-group');
  incomeItems.forEach(item => {
    const label = item.querySelector('label')?.textContent || '';
    const input = item.querySelector('.income-input');
    const frequencySelect = item.querySelector('.frequency-select');
    const amount = parseCurrencyInput(input?.value);
    const frequency = frequencySelect ? frequencySelect.value : 'monthly';
    
    // Capture timing/schedule data
    const timingPattern = item.getAttribute('data-timing-pattern');
    const dayOfWeek = item.getAttribute('data-day-of-week');
    const dayOfMonth = item.getAttribute('data-day-of-month');
    const startDate = item.getAttribute('data-start-date');
    
    if (label && amount > 0) {
      const incomeItem = { label, amount, frequency };
      
      // Add timing data if it exists
      if (timingPattern) {
        incomeItem.timingPattern = timingPattern;
      }
      if (dayOfWeek) {
        incomeItem.dayOfWeek = parseInt(dayOfWeek);
      }
      if (dayOfMonth) {
        incomeItem.dayOfMonth = dayOfMonth;
      }
      if (startDate) {
        incomeItem.startDate = startDate;
      }
      
      data.income.push(incomeItem);
    }
  });

  // Capture expense categories and items
  const categoryContainers = document.querySelectorAll('.category-section');
  categoryContainers.forEach(category => {
    const categoryName = category.querySelector('h3')?.textContent || '';
    const items = [];
    
    const expenseItems = category.querySelectorAll('.input-group');
    expenseItems.forEach(item => {
      const label = item.querySelector('label')?.textContent || '';
      const input = item.querySelector('.expense-input');
      const frequencySelect = item.querySelector('.frequency-select');
      const taxCheckbox = item.querySelector('.tax-deductible-checkbox');
      const recurringCheckbox = item.querySelector('.recurring-checkbox');
      const savingsCheckbox = item.querySelector('.savings-checkbox');
      const amount = parseCurrencyInput(input?.value);
      const frequency = frequencySelect?.value || 'monthly';
      const isTaxDeductible = taxCheckbox?.checked || false;
      const isRecurring = recurringCheckbox?.checked || false;
      const isSavings = savingsCheckbox?.checked || false;
      
      // Capture recurring configuration if it exists
      let recurringConfig = null;
      if (isRecurring) {
        const configData = item.getAttribute('data-recurring-config');
        if (configData) {
          try {
            recurringConfig = JSON.parse(configData);
          } catch (e) {
            console.warn('Failed to parse recurring config for item:', label, e);
          }
        }
      }
      
      if (label && amount > 0) {
        const itemData = { label, amount, frequency, isTaxDeductible, isRecurring, isSavings };
        if (recurringConfig) {
          itemData.recurringConfig = recurringConfig;
        }
        
        // Capture plan-it data if applicable
        if (frequency === 'plan-it') {
          const planItDate = item.getAttribute('data-plan-it-date');
          if (planItDate) {
            itemData.planItDate = planItDate;
          }
        }
        
        items.push(itemData);
      }
    });
    
    if (categoryName && items.length > 0) {
      const categoryData = { name: categoryName, items };
      
      // Capture target data if set
      const targetType = category.dataset.targetType;
      const targetValue = category.dataset.targetValue;
      if (targetType && targetType !== 'none' && targetValue) {
        categoryData.targetType = targetType;
        categoryData.targetValue = parseFloat(targetValue);
      }
      
      data.categories.push(categoryData);
    }
  });

  return data;
}

// Restore budget data to DOM
export function restoreBudgetData(data) {
  if (!data) {
    console.warn('No data provided to restoreBudgetData');
    return;
  }

  console.log('Restoring budget data:', data);
  console.log('Income data:', data.income);
  console.log('Categories data:', data.categories);

  // Clear existing data
  clearBudgetFormData();

  // Restore income items
  const incomeContainer = document.getElementById('income-items');
  if (incomeContainer) {
    incomeContainer.innerHTML = '';
    if (data.income && data.income.length > 0) {
      console.log('Restoring income items');
      data.income.forEach((item, index) => {
        console.log(`Adding income item ${index}:`, item);
        const timingData = {};
        if (item.timingPattern) timingData.timingPattern = item.timingPattern;
        if (item.dayOfWeek) timingData.dayOfWeek = item.dayOfWeek;
        if (item.dayOfMonth) timingData.dayOfMonth = item.dayOfMonth;
        if (item.startDate) timingData.startDate = item.startDate;
        addIncomeItemWithData(
          item.label || item.name || 'Income Item',
          item.value || item.amount || 0,
          item.frequency || 'monthly',
          timingData
        );
      });
    } else {
      console.log('No income data to restore');
    }
  }

  // Restore expense categories
  const expenseContainer = document.getElementById('expense-items');
  if (expenseContainer) {
    expenseContainer.innerHTML = '';
    if (data.categories && data.categories.length > 0) {
      console.log('Restoring expense categories');
      data.categories.forEach((category, index) => {
        console.log(`Adding category ${index}:`, category);
        addExpenseCategoryWithData(
          category.name || category.categoryName || 'Category',
          category.items || category.expenses || [],
          category.targetType,
          category.targetValue
        );
      });
    } else {
      console.log('No categories data to restore');
    }
  }

  // Update frequency badges and recalculate totals
  updateAllFrequencyBadges();
  updateAllTimingBadges();
  calculateTotals();
  console.log('Budget data restoration completed');
}

// Clear all budget form inputs
function clearBudgetFormData() {
  // Clear income items
  const incomeInputs = document.querySelectorAll('.income-input');
  incomeInputs.forEach(input => input.value = '');

  // Clear expense items
  const expenseInputs = document.querySelectorAll('.expense-input');
  expenseInputs.forEach(input => input.value = '');
}

// Add income item with data
function addIncomeItemWithData(label, amount, frequency = 'monthly', timingData = {}) {
  const incomeContainer = document.getElementById('income-items');
  if (!incomeContainer) {
    console.error('Income container not found');
    return;
  }

  console.log(`Creating income item: "${label}" = ${amount}, frequency: ${frequency}`);
  
  const itemHTML = `
    <div class="input-group" draggable="true">
      <div class="input-group-main">
        <span class="drag-handle">⋮⋮</span>
        <label contenteditable="true">${label || 'Income Item'}</label>
        <div class="input-controls">
          <input type="text" class="income-input" value="${amount || 0}">
          <select class="frequency-select" onchange="calculateTotals()" style="display: none;">
            <option value="monthly" ${frequency === 'monthly' ? 'selected' : ''}>Monthly</option>
            <option value="weekly" ${frequency === 'weekly' ? 'selected' : ''}>Weekly</option>
            <option value="bi-weekly" ${frequency === 'bi-weekly' ? 'selected' : ''}>Bi-weekly</option>
            <option value="quarterly" ${frequency === 'quarterly' ? 'selected' : ''}>Quarterly</option>
            <option value="annual" ${frequency === 'annual' ? 'selected' : ''}>Annual</option>
            <option value="one-time" ${frequency === 'one-time' ? 'selected' : ''}>One-time</option>
          </select>
          <button class="remove-item" onclick="removeItem(this)">×</button>
        </div>
      </div>
      <div class="input-options">
        <div class="tax-container">
          <button type="button" class="tax-indicator" onclick="toggleIncomeTaxPanel(this)" title="Income Tax Settings">
            <span class="tax-badge"><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 12h-5"/><path d="M15 8h-5"/><path d="M19 17V5a2 2 0 0 0-2-2H4"/><path d="M8 21h12a2 2 0 0 0 2-2v-1a1 1 0 0 0-1-1H11a1 1 0 0 0-1 1v1a2 2 0 1 1-4 0V5a2 2 0 1 0-4 0v2a1 1 0 0 0 1 1h3"/></svg></span>
          </button>
        </div>
        <div class="timing-container">
          <button type="button" class="timing-indicator" onclick="toggleIncomeTimingPanel(this)" title="Payment Schedule">
            <span class="timing-badge"><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2v4"/><path d="M16 2v4"/><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M3 10h18"/><path d="M8 14h.01"/><path d="M12 14h.01"/><path d="M16 14h.01"/><path d="M8 18h.01"/><path d="M12 18h.01"/><path d="M16 18h.01"/></svg></span>
          </button>
        </div>
        <div class="frequency-container">
          <button type="button" class="frequency-indicator" onclick="toggleIncomeFrequencyPanel(this)" title="Income Frequency Settings">
            <span class="frequency-badge">Mo</span>
          </button>
        </div>
      </div>
      <div class="income-frequency-panel" style="display: none;"></div>
      <div class="income-tax-panel" style="display: none;"></div>
      <div class="income-timing-panel" style="display: none;"></div>
    </div>
  `;
  
  incomeContainer.insertAdjacentHTML('beforeend', itemHTML);
  
  // Setup currency formatting for new input
  const newInput = incomeContainer.lastElementChild.querySelector('.income-input');
  if (newInput) {
    setupCurrencyInput(newInput, calculateTotals);
    
    // Setup drag and drop for new element
    const newElement = incomeContainer.lastElementChild;
    newElement.draggable = true;
    newElement.addEventListener('dragstart', handleDragStart);
    
    // Set timing data attributes if provided
    if (timingData.timingPattern) {
      newElement.setAttribute('data-timing-pattern', timingData.timingPattern);
    }
    if (timingData.dayOfWeek) {
      newElement.setAttribute('data-day-of-week', timingData.dayOfWeek.toString());
    }
    if (timingData.dayOfMonth) {
      newElement.setAttribute('data-day-of-month', timingData.dayOfMonth);
    }
    if (timingData.startDate) {
      newElement.setAttribute('data-start-date', timingData.startDate);
    }
    
    // Initialize frequency badge with the correct frequency
    const frequencyBadge = newElement.querySelector('.frequency-badge');
    if (frequencyBadge) {
      updateFrequencyBadge(frequencyBadge, frequency);
    }
    
    console.log(`Income item created successfully: ${newInput.value}`);
  } else {
    console.error('Failed to find new income input');
  }
}

// Add expense category with data
function addExpenseCategoryWithData(categoryName, items, targetType = null, targetValue = null) {
  const expenseContainer = document.getElementById('expense-items');
  if (!expenseContainer) {
    console.error('Expense container not found');
    return;
  }

  console.log(`Creating category: "${categoryName}" with ${items.length} items:`, items);

  let itemsHTML = '';
  if (items && items.length > 0) {
    items.forEach((item, index) => {
      console.log(`  Item ${index}:`, item);
      const label = item.label || item.name || item.description || 'Expense Item';
      const amount = item.value || item.amount || item.cost || 0;
      const frequency = item.frequency || 'monthly';
      
      // Prepare data attributes for recurring config and plan-it
      const recurringConfigAttr = item.recurringConfig ? `data-recurring-config='${JSON.stringify(item.recurringConfig)}'` : '';
      const planItDateAttr = item.planItDate ? `data-plan-it-date="${item.planItDate}"` : '';
      
      // Generate unique IDs for this item
      const savingsId = generateUniqueId('savings');
      const taxId = generateUniqueId('tax');
      const recurringId = generateUniqueId('recurring');
      
      itemsHTML += `
        <div class="input-group" draggable="true" ${recurringConfigAttr} ${planItDateAttr}>
          <div class="input-group-main">
            <span class="drag-handle">⋮⋮</span>
            <label contenteditable="true">${label}</label>
            <div class="input-controls">
              <input type="text" class="expense-input" value="${amount}">
              <select class="frequency-select" onchange="calculateTotals()">
                <option value="monthly" ${frequency === 'monthly' ? 'selected' : ''}>Monthly</option>
                <option value="weekly" ${frequency === 'weekly' ? 'selected' : ''}>Weekly</option>
                <option value="bi-weekly" ${frequency === 'bi-weekly' ? 'selected' : ''}>Bi-weekly</option>
                <option value="quarterly" ${frequency === 'quarterly' ? 'selected' : ''}>Quarterly</option>
                <option value="annual" ${frequency === 'annual' ? 'selected' : ''}>Annual</option>
                <option value="one-time" ${frequency === 'one-time' ? 'selected' : ''}>One-time</option>
                <option value="plan-it" ${frequency === 'plan-it' ? 'selected' : ''}>Plan It</option>
              </select>
              <button class="remove-item" onclick="removeItem(this)">×</button>
            </div>
          </div>
          <div class="input-options">
            <div class="savings-container">
              <input type="checkbox" class="savings-checkbox" id="${savingsId}" onchange="calculateTotals()" ${item.isSavings ? 'checked' : ''}>
              <button type="button" class="savings-indicator" onclick="document.getElementById('${savingsId}').click()" title="Savings Item">
                <span class="savings-badge">$</span>
              </button>
            </div>
            <div class="frequency-container">
              <button type="button" class="frequency-indicator" onclick="toggleFrequencyPanel(this)" title="Frequency Settings">
                <span class="frequency-badge">${FREQUENCY_LABELS[frequency]?.badge || 'Mo'}</span>
              </button>
            </div>
            <div class="tax-deductible-container">
              <input type="checkbox" class="tax-deductible-checkbox" id="${taxId}" ${item.isTaxDeductible ? 'checked' : ''}>
              <label for="${taxId}" class="tax-deductible-label" title="Tax Deductible">
                <span class="tax-icon"><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 12h-5"/><path d="M15 8h-5"/><path d="M19 17V5a2 2 0 0 0-2-2H4"/><path d="M8 21h12a2 2 0 0 0 2-2v-1a1 1 0 0 0-1-1H11a1 1 0 0 0-1 1v1a2 2 0 1 1-4 0V5a2 2 0 1 0-4 0v2a1 1 0 0 0 1 1h3"/></svg></span>
              </label>
            </div>
            <div class="recurring-container">
              <input type="checkbox" class="recurring-checkbox" id="${recurringId}" onchange="toggleRecurringPanel(this)" ${item.isRecurring ? 'checked' : ''}>
              <label for="${recurringId}" class="recurring-label" title="Recurring Expense">
                <span class="recurring-icon"><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/><path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"/><path d="M16 16h5v5"/></svg></span>
              </label>
            </div>
          </div>
          <div class="frequency-panel" style="display: none;">
            <!-- Frequency configuration will be inserted here -->
          </div>
          <div class="recurring-panel" style="display: none;">
            <!-- Recurring configuration will be set up after DOM insertion -->
          </div>
        </div>
      `;
    });
  } else {
    // Add a default empty item if no items provided
    const savingsId = generateUniqueId('savings');
    const taxId = generateUniqueId('tax');
    const recurringId = generateUniqueId('recurring');
    
    itemsHTML = `
      <div class="input-group" draggable="true">
        <div class="input-group-main">
          <span class="drag-handle">⋮⋮</span>
          <label contenteditable="true">New Item</label>
          <div class="input-controls">
            <input type="number" class="expense-input" value="0" step="0.01">
            <select class="frequency-select" onchange="calculateTotals()">
              <option value="monthly">Monthly</option>
              <option value="weekly">Weekly</option>
              <option value="annual">Annual</option>
              <option value="one-time">One-time</option>
            </select>
            <button class="remove-item" onclick="removeItem(this)">×</button>
          </div>
        </div>
        <div class="input-options">
          <div class="savings-container">
            <input type="checkbox" class="savings-checkbox" id="${savingsId}" onchange="calculateTotals()">
            <button type="button" class="savings-indicator" onclick="document.getElementById('${savingsId}').click()" title="Savings Item">
              <span class="savings-badge">$</span>
            </button>
          </div>
          <div class="frequency-container">
            <button type="button" class="frequency-indicator" onclick="toggleFrequencyPanel(this)" title="Frequency Settings">
              <span class="frequency-badge">Mo</span>
            </button>
          </div>
          <div class="tax-deductible-container">
            <input type="checkbox" class="tax-deductible-checkbox" id="${taxId}">
            <label for="${taxId}" class="tax-deductible-label" title="Tax Deductible">
              <span class="tax-icon"><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 12h-5"/><path d="M15 8h-5"/><path d="M19 17V5a2 2 0 0 0-2-2H4"/><path d="M8 21h12a2 2 0 0 0 2-2v-1a1 1 0 0 0-1-1H11a1 1 0 0 0-1 1v1a2 2 0 1 1-4 0V5a2 2 0 1 0-4 0v2a1 1 0 0 0 1 1h3"/></svg></span>
            </label>
          </div>
          <div class="recurring-container">
            <input type="checkbox" class="recurring-checkbox" id="${recurringId}" onchange="toggleRecurringPanel(this)">
            <label for="${recurringId}" class="recurring-label" title="Recurring Expense">
              <span class="recurring-icon"><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/><path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"/><path d="M16 16h5v5"/></svg></span>
            </label>
          </div>
        </div>
        <div class="frequency-panel" style="display: none;">
          <!-- Frequency configuration will be inserted here -->
        </div>
        <div class="recurring-panel" style="display: none;">
          <!-- Recurring configuration will be inserted here -->
        </div>
      </div>
    `;
  }

  const categoryHTML = `
    <div class="category-section" draggable="true">
      <div class="category-header">
        <h3 contenteditable="true">${categoryName}</h3>
        <div class="category-controls">
          <button class="add-to-category" onclick="addItemToCategory(this)">+ Add Item</button>
          <button class="remove-category" onclick="removeCategory(this)">Remove</button>
        </div>
      </div>
      ${itemsHTML}
      <div class="category-subtotal">
        <div class="category-subtotal-header">
          <span class="target-icon" onclick="openTargetModal(this)" title="Set Target"><svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg></span>
          <span class="category-total">$0.00</span><span class="category-target-summary" style="display: none;"> planned of <span class="category-target-display">$0</span> (<span class="category-difference">$0</span>)</span>
        </div>
        <div class="target-modal">
          <div class="target-modal-content">
            <div class="target-modal-header">Set Category Target</div>
            <div class="target-modal-controls">
            <select class="target-type-select" onchange="updateModalInputRow(this)">
              <option value="none">No Target</option>
              <option value="percentage">% of Income</option>
              <option value="fixed">Fixed Amount</option>
            </select>
            <div class="target-input-row" style="display: none;">
              <input type="text" class="target-input-field" placeholder="0">
              <span class="target-suffix-text">%</span>
            </div>
          </div>
          <div class="target-modal-buttons">
            <button class="target-modal-btn primary" onclick="saveTargetFromModal(this)">Save</button>
            <button class="target-modal-btn secondary" onclick="closeTargetModal(this)">Cancel</button>
          </div>
          </div>
        </div>
      </div>
    </div>
  `;
  
  expenseContainer.insertAdjacentHTML('beforeend', categoryHTML);
  
  // Setup currency formatting for new inputs
  const newCategory = expenseContainer.lastElementChild;
  const newInputs = newCategory.querySelectorAll('.expense-input');
  newInputs.forEach(input => {
    setupCurrencyInput(input, calculateTotals);
    console.log(`Category input setup: ${input.value}`);
  });
  
  // Setup tax deductible checkbox event listeners
  const taxCheckboxes = newCategory.querySelectorAll('.tax-deductible-checkbox');
  taxCheckboxes.forEach(checkbox => {
    checkbox.addEventListener('change', calculateTotals);
  });
  
  // Setup savings checkbox event listeners
  const savingsCheckboxes = newCategory.querySelectorAll('.savings-checkbox');
  savingsCheckboxes.forEach(checkbox => {
    checkbox.addEventListener('change', calculateTotals);
  });
  
  // Setup drag and drop for new category and its items
  newCategory.draggable = true;
  newCategory.addEventListener('dragstart', handleDragStart);
  
  const newItems = newCategory.querySelectorAll('.input-group');
  newItems.forEach(item => {
    item.draggable = true;
    item.addEventListener('dragstart', handleDragStart);
    
    // Setup recurring configuration if it exists
    const recurringConfig = item.getAttribute('data-recurring-config');
    if (recurringConfig) {
      const recurringCheckbox = item.querySelector('.recurring-checkbox');
      if (recurringCheckbox && recurringCheckbox.checked) {
        // Apply saved configuration styling to the recurring label
        const recurringLabel = item.querySelector('.recurring-label');
        if (recurringLabel) {
          recurringLabel.title = 'Recurring expense configured ✓';
        }
      }
    }
  });
  
  // Restore target settings if provided
  if (targetType && targetType !== 'none' && targetValue) {
    
    // Store on element for persistence
    newCategory.dataset.targetType = targetType;
    newCategory.dataset.targetValue = targetValue;
    
    // Update target icon state and display
    updateTargetIconState(newCategory);
    updateCategoryTargetFromData(newCategory);
  }
  
  console.log(`Category "${categoryName}" created successfully`);
}

/**
 * Create a budget snapshot for historical tracking
 * @param {Object} budgetData - Budget data from captureBudgetData()
 * @param {string} month - Month in YYYY-MM format
 * @param {string} budgetId - Budget ID (defaults to current budget)
 * @returns {Promise<string>} - Snapshot ID
 */
export async function createBudgetSnapshot(budgetData, month, budgetId = null) {
  const currentUser = getCurrentUser();
  if (!currentUser) {
    throw new Error('No user authenticated');
  }

  try {
    console.log(`Creating budget snapshot for ${month}`);

    // Use provided budgetData or capture current budget
    const dataToSnapshot = budgetData || captureBudgetData();

    // Calculate totals
    const totals = calculateDataTotals(dataToSnapshot);

    // Get budget name from DOM or use default
    const budgetNameEl = document.querySelector('.budget-name-display');
    const budgetName = budgetNameEl?.textContent?.trim() || 'My Budget';

    // Create snapshot document
    const snapshotData = {
      userId: currentUser.uid,
      budgetId: budgetId || getCurrentBudgetId(),
      month: month,
      snapshotDate: Timestamp.now(),
      budgetName: budgetName,
      categories: dataToSnapshot.categories || [],
      income: dataToSnapshot.income || [],
      totals: {
        income: totals.totalIncome,
        expenses: totals.totalExpenses,
        net: totals.netAmount
      }
    };

    // Save to Firestore
    const snapshotsRef = collection(db, `users/${currentUser.uid}/budgetSnapshots`);
    const snapshotDoc = await addDoc(snapshotsRef, snapshotData);

    console.log(`✅ Budget snapshot created: ${snapshotDoc.id} for month ${month}`);
    return snapshotDoc.id;

  } catch (error) {
    console.error('Failed to create budget snapshot:', error);
    throw error;
  }
}

/**
 * Get budget snapshot for a specific month
 * @param {string} month - Month in YYYY-MM format
 * @param {string} userId - User ID (defaults to current user)
 * @returns {Promise<Object|null>} - Budget snapshot data or null if not found
 */
export async function getBudgetSnapshotForMonth(month, userId = null) {
  const currentUser = getCurrentUser();
  if (!currentUser && !userId) {
    throw new Error('No user authenticated');
  }

  const targetUserId = userId || currentUser.uid;

  try {
    console.log(`Fetching budget snapshot for ${month}`);

    // Query for snapshot matching the month
    const snapshotsRef = collection(db, `users/${targetUserId}/budgetSnapshots`);
    const q = query(snapshotsRef, where('month', '==', month), orderBy('snapshotDate', 'desc'), limit(1));

    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      console.log(`No budget snapshot found for ${month}`);
      return null;
    }

    const snapshotDoc = snapshot.docs[0];
    return {
      id: snapshotDoc.id,
      ...snapshotDoc.data()
    };

  } catch (error) {
    console.error('Failed to get budget snapshot:', error);
    return null;
  }
}

// Save current budget (without prompting for new version name)
export async function saveCurrentBudget() {
  const currentUser = getCurrentUser();
  if (!currentUser) return;
  if (!currentVersionName) return; // no budget created yet — nothing to save

  try {
    setSyncing('Saving budget...');
    
    const budgetData = captureBudgetData();
    
    // Update the current version with latest data
    if (budgetVersions[currentVersionName]) {
      budgetVersions[currentVersionName].data = budgetData;
      budgetVersions[currentVersionName].lastModified = new Date().toISOString();
    } else {
      // If somehow the current version doesn't exist, create it
      budgetVersions[currentVersionName] = {
        name: currentVersionName,
        data: budgetData,
        lastModified: new Date().toISOString(),
        isDefault: false
      };
    }
    
    // Save to user-scoped collection (new structure)
    await saveBudgetVersionToCollection(currentVersionName, budgetData);

    // Clear recurring expenses cache since budget data changed
    if (typeof window !== 'undefined' && window.clearRecurringExpensesCache) {
      window.clearRecurringExpensesCache();
    }
    
    setSynced('Budget saved');
    updateVersionsList();
    
    // Track budget creation achievement (only for first budget)
    const totalBudgets = Object.keys(budgetVersions).length;
    if (totalBudgets === 1) {
      // This is their first budget
      trackBudgetCreation(currentUser.uid);
    }
    
    // Show success notification to user
    showSaveSuccessNotification();
  } catch (error) {
    console.error('Error saving budget:', error);
    setSyncError('Failed to save budget');
  }
}

// Save current version (creates new version with prompt)
export async function saveCurrentVersion() {
  const currentUser = getCurrentUser();
  if (!currentUser) return;

  const versionName = prompt('Enter a name for this budget version:', currentVersionName);
  if (!versionName) return;

  try {
    setSyncing('Saving budget version...');
    
    const budgetData = captureBudgetData();
    budgetVersions[versionName] = {
      data: budgetData,
      lastModified: new Date().toISOString(),
      isDefault: false
    };

    // Update current version name
    currentVersionName = versionName;
    updateVersionDisplay();

    // Save version data to collection, then persist updated currentVersionName
    await saveBudgetVersionToCollection(versionName, budgetData);
    await saveUserData();

    setSynced('Budget version saved');
    updateVersionsList();
  } catch (error) {
    console.error('Error saving version:', error);
    setSyncError('Failed to save version');
  }
}

// Load budget version
export function loadBudgetVersion(versionName, showConfirmation = true) {
  if (!budgetVersions[versionName]) {
    console.warn(`Budget version "${versionName}" not found`);
    return;
  }

  if (showConfirmation && !confirm(`Load "${versionName}"? This will replace your current budget.`)) {
    return;
  }

  try {
    const version = budgetVersions[versionName];
    console.log(`Loading budget version "${versionName}" with data:`, version.data);
    restoreBudgetData(version.data);
    currentVersionName = versionName;
    updateVersionDisplay();
    
    console.log(`Successfully loaded budget version: ${versionName}`);
  } catch (error) {
    console.error('Error loading version:', error);
  }
}

// Update version display
function updateVersionDisplay() {
  const versionNameEl = document.getElementById('current-version-name');
  const lastModifiedEl = document.getElementById('last-modified');
  
  if (versionNameEl) {
    versionNameEl.textContent = currentVersionName;
  }
  
  if (lastModifiedEl) {
    const now = new Date();
    lastModifiedEl.textContent = `Last modified: ${now.toLocaleString()}`;
  }
  
  // Update Default button styling based on whether current budget is default
  updateDefaultButtonStyling();
}

// Update Default button styling based on default status
function updateDefaultButtonStyling() {
  const defaultBtn = document.querySelector('.version-btn.default-btn');
  if (!defaultBtn) return;
  
  const isCurrentDefault = (currentVersionName === defaultVersionName) || 
                          (budgetVersions[currentVersionName] && budgetVersions[currentVersionName].isDefault);
  
  if (isCurrentDefault) {
    defaultBtn.classList.add('is-default');
    defaultBtn.title = 'This budget is currently the default';
  } else {
    defaultBtn.classList.remove('is-default');
    defaultBtn.title = 'Set current budget as default';
  }
}

// Show/hide welcome screen based on budget existence
export function updateWelcomeScreen() {
  const welcomeScreen = document.getElementById('welcome-screen');
  const singleMode = document.getElementById('single-mode');
  
  if (!welcomeScreen || !singleMode) return;
  
  const budgetCount = Object.keys(budgetVersions).length;
  
  // For new users, currentVersionName should be null or empty
  const hasCurrentBudget = currentVersionName && currentVersionName.trim() !== '';
  
  // Check if there are any saved budget versions
  const hasSavedBudgets = budgetCount > 0;
  
  // A user has budgets if they have a current budget name OR saved versions
  const hasBudgets = hasCurrentBudget || hasSavedBudgets;
  
  console.log(`updateWelcomeScreen: budgetCount: ${budgetCount}, currentVersion: "${currentVersionName}", hasBudgets: ${hasBudgets}`);
  
  if (hasBudgets) {
    // User has budgets - show normal budget interface
    console.log('Hiding welcome screen, showing budget interface');
    welcomeScreen.style.display = 'none';
    singleMode.style.display = 'block';
  } else {
    // New user - show welcome screen with proper flex display
    console.log('Showing welcome screen, hiding budget interface');
    welcomeScreen.style.display = 'flex';
    singleMode.style.display = 'none';
  }
}

// Update versions list
function getBudgetSummary(data) {
  if (!data) return { income: 0, budgeted: 0 };

  const income = (data.income || []).reduce((sum, item) => {
    return sum + convertToMonthlyAmount(item.amount || 0, item.frequency || 'monthly');
  }, 0);

  // categories may be an array or a legacy plain object — normalize to array
  const rawCategories = data.categories || [];
  const categories = Array.isArray(rawCategories) ? rawCategories : Object.values(rawCategories);

  const budgeted = categories.reduce((sum, cat) => {
    return sum + (cat.items || []).reduce((s, item) => {
      return s + convertToMonthlyAmount(item.amount || 0, item.frequency || 'monthly');
    }, 0);
  }, 0);

  return { income, budgeted };
}

export function updateVersionsList() {
  const versionsList = document.getElementById('version-list');
  if (!versionsList) return;

  versionsList.innerHTML = '';

  Object.keys(budgetVersions).forEach(versionName => {
    const version = budgetVersions[versionName];
    const isDefault = versionName === defaultVersionName;
    // JSON.stringify produces a double-quoted JS string literal that handles apostrophes and other
    // special chars. Replace " with &quot; so it's safe inside the HTML onclick="..." attribute.
    const jsName = JSON.stringify(versionName).replace(/"/g, '&quot;');

    const { income, budgeted } = getBudgetSummary(version.data);
    const summaryHTML = (income > 0 || budgeted > 0)
      ? `<span class="version-summary">${formatCurrencyClean(budgeted)} of ${formatCurrencyClean(income)} budgeted</span>`
      : '';

    const versionHTML = `
      <div class="version-item">
        <div class="version-info">
          <span class="version-name">
            ${versionName}
            ${isDefault ? '<span class="default-indicator">Default</span>' : ''}
          </span>
          <div class="version-meta">
            <span class="version-date">${new Date(version.lastModified).toLocaleDateString()}</span>
            ${summaryHTML}
          </div>
        </div>
        <div class="version-actions">
          <button onclick="loadBudgetVersion(${jsName})" class="version-btn">Load</button>
          <button onclick="duplicateVersion(${jsName})" class="version-btn">Copy</button>
          <button onclick="toggleDefaultVersion(${jsName})" class="version-btn ${isDefault ? 'active' : ''}">
            ${isDefault ? '⭐' : '☆'}
          </button>
          <button onclick="deleteVersion(${jsName})" class="version-btn danger">Delete</button>
        </div>
      </div>
    `;

    versionsList.insertAdjacentHTML('beforeend', versionHTML);
  });
}

// Initialize budget name editing
export function initializeBudgetNameEditing() {
  const nameSpan = document.getElementById('current-version-name');
  if (!nameSpan) return;
  
  let originalName = '';
  
  // Store original name when editing starts
  nameSpan.addEventListener('focus', () => {
    originalName = nameSpan.textContent.trim();
  });
  
  // Handle name changes when editing finishes
  nameSpan.addEventListener('blur', () => {
    const newName = nameSpan.textContent.trim();
    if (newName && newName !== originalName) {
      performRename(newName);
    } else if (!newName) {
      // Restore original name if empty
      nameSpan.textContent = originalName;
    }
  });
  
  // Handle Enter and Escape keys
  nameSpan.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      nameSpan.blur(); // This will trigger the blur event
    } else if (e.key === 'Escape') {
      e.preventDefault();
      nameSpan.textContent = originalName;
      nameSpan.blur();
    }
  });
}

// Debounced save function to avoid excessive saves during editing
let saveTimeout = null;
function debouncedSave() {
  if (saveTimeout) {
    clearTimeout(saveTimeout);
  }
  saveTimeout = setTimeout(() => {
    // Only calculate totals and save, no need for full budget save
    calculateTotals();
  }, 300); // 300ms delay
}

// Detect Safari browser
function isSafari() {
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
}

// Initialize inline editing for all contenteditable elements
export function initializeInlineEditing() {
  // Use event delegation to handle all contenteditable elements
  document.addEventListener('focus', handleInlineEditFocus, true);
  document.addEventListener('blur', handleInlineEditBlur, true);
  document.addEventListener('keydown', handleInlineEditKeydown, true);
  
  // Additional Safari-specific fixes
  if (isSafari()) {
    // Add click handler for Safari contenteditable elements
    document.addEventListener('click', handleSafariClick, true);
    // Add mousedown handler as backup for Safari
    document.addEventListener('mousedown', handleSafariMousedown, true);
    // Add input handler to ensure changes are detected
    document.addEventListener('input', handleSafariInput, true);
  }
}

// Safari-specific click handler
function handleSafariClick(e) {
  const element = e.target;
  if (!element.hasAttribute('contenteditable') || element.id === 'current-version-name') {
    return;
  }

  // Force editable state so Safari recognises the element
  element.setAttribute('contenteditable', 'true');
  element.style.webkitUserSelect = 'text';
  element.style.userSelect = 'text';
  element.style.webkitUserModify = 'read-write';

  // Let the browser handle cursor placement naturally (no preventDefault).
  // Only intervene for placeholder text — select all so the user can type immediately.
  const PLACEHOLDERS = new Set([
    'New Category', '💰 New Category', 'New Item',
    'New Expense Item', 'New Income Source', 'Sample Expense', 'Salary (after taxes)'
  ]);

  const text = element.textContent.trim();
  if (PLACEHOLDERS.has(text) && window.getSelection && document.createRange) {
    // Defer so the browser's own focus/click handling runs first
    setTimeout(() => {
      try {
        const range = document.createRange();
        range.selectNodeContents(element);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
      } catch (err) {
        console.log('Safari selection error:', err);
      }
    }, 0);
  }
}

// Safari-specific mousedown handler as backup
function handleSafariMousedown(e) {
  const element = e.target;
  if (!element.hasAttribute('contenteditable') || element.id === 'current-version-name') {
    return;
  }
  
  // Mark for delayed processing
  element.setAttribute('data-safari-mousedown', 'true');
  
  setTimeout(() => {
    if (element.hasAttribute('data-safari-mousedown')) {
      element.removeAttribute('data-safari-mousedown');
      
      // Force editable state
      element.setAttribute('contenteditable', 'true');
      element.style.webkitUserSelect = 'text';
      element.style.userSelect = 'text';
      element.focus();
    }
  }, 50);
}

// Safari-specific input handler  
function handleSafariInput(e) {
  const element = e.target;
  if (!element.hasAttribute('contenteditable') || element.id === 'current-version-name') {
    return;
  }
  
  // Ensure the element stays focusable and editable
  if (!element.hasAttribute('contenteditable')) {
    element.setAttribute('contenteditable', 'true');
  }
}

// Handle focus on contenteditable elements
function handleInlineEditFocus(e) {
  const element = e.target;
  if (!element.hasAttribute('contenteditable') || element.id === 'current-version-name') {
    return; // Skip non-contenteditable elements and budget name (handled separately)
  }
  
  // Store original text for restoration on escape/cancel
  element.setAttribute('data-original-text', element.textContent.trim());
  
  // Safari-specific: Ensure element is properly focused
  if (isSafari()) {
    element.style.outline = 'none';
    element.style.webkitUserSelect = 'text';
  }
  
  // Clear default placeholder text
  const text = element.textContent.trim();
  if (text === 'New Category' || text === '💰 New Category' || 
      text === 'New Item' || text === 'New Expense Item' || 
      text === 'New Income Source' || text === 'Sample Expense' ||
      text === 'Salary (after taxes)') {
    // Use setTimeout for Safari compatibility
    setTimeout(() => {
      element.textContent = '';
      
      // For Safari, ensure cursor is positioned correctly
      if (isSafari() && window.getSelection && document.createRange) {
        const range = document.createRange();
        const selection = window.getSelection();
        range.setStart(element, 0);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }, 0);
  }
}

// Handle blur on contenteditable elements  
function handleInlineEditBlur(e) {
  const element = e.target;
  if (!element.hasAttribute('contenteditable') || element.id === 'current-version-name') {
    return; // Skip non-contenteditable elements and budget name
  }
  
  const newText = element.textContent.trim();
  const originalText = element.getAttribute('data-original-text') || '';
  
  // If empty, restore appropriate placeholder text
  if (!newText) {
    if (element.tagName === 'H3') {
      // Category header
      element.textContent = originalText || 'New Category';
    } else if (element.tagName === 'LABEL') {
      // Item label
      element.textContent = originalText || 'New Item';
    }
  }
  
  // Clean up attribute
  element.removeAttribute('data-original-text');

  // Mark getting-started step when an expense line item name is changed
  if (element.tagName === 'LABEL' && newText && newText !== originalText) {
    if (typeof window.markGettingStartedStep === 'function') {
      window.markGettingStartedStep('enterExpenseDetails');
    }
  }

  // Save changes (debounced)
  debouncedSave();
}

// Handle keyboard shortcuts for contenteditable elements
function handleInlineEditKeydown(e) {
  const element = e.target;
  if (!element.hasAttribute('contenteditable') || element.id === 'current-version-name') {
    return; // Skip non-contenteditable elements and budget name
  }
  
  if (e.key === 'Enter') {
    e.preventDefault();
    element.blur(); // This will trigger the blur event and save
  } else if (e.key === 'Escape') {
    e.preventDefault();
    // Restore original text
    const originalText = element.getAttribute('data-original-text') || '';
    element.textContent = originalText;
    element.blur();
  }
}

// Perform the actual rename operation
function performRename(newName) {
  if (budgetVersions[newName]) {
    alert('A budget with that name already exists.');
    return;
  }
  
  // If this was the default version, update the default name
  if (defaultVersionName === currentVersionName) {
    defaultVersionName = newName;
  }
  
  // If there's a saved version, rename it
  if (budgetVersions[currentVersionName]) {
    budgetVersions[newName] = budgetVersions[currentVersionName];
    delete budgetVersions[currentVersionName];
  }
  
  currentVersionName = newName;
  updateVersionDisplay();
  saveUserData();
}

// Rename current version (legacy function - keeping for compatibility)
export function renameCurrentVersion() {
  const newName = prompt('Enter new name for this budget:', currentVersionName);
  if (!newName || newName === currentVersionName) return;
  
  if (budgetVersions[newName]) {
    alert('A budget with that name already exists.');
    return;
  }
  
  // If this was the default version, update the default name
  if (defaultVersionName === currentVersionName) {
    defaultVersionName = newName;
  }
  
  // If there's a saved version, rename it
  if (budgetVersions[currentVersionName]) {
    budgetVersions[newName] = budgetVersions[currentVersionName];
    delete budgetVersions[currentVersionName];
  }
  
  currentVersionName = newName;
  updateVersionDisplay();
  updateVersionsList();
  saveUserData();
}

// Toggle versions panel
export function toggleVersionsPanel() {
  const modal = document.getElementById('budget-manager-modal');
  if (!modal) return;
  if (modal.style.display === 'none' || !modal.style.display) {
    modal.style.display = 'flex';
    updateVersionsList();
    const searchInput = document.getElementById('budget-manager-search');
    if (searchInput) {
      searchInput.value = '';
      setTimeout(() => searchInput.focus(), 50);
    }
  } else {
    modal.style.display = 'none';
  }
}

// Backdrop click handler for budget manager modal
export function handleBudgetManagerBackdropClick(event) {
  if (event.target === document.getElementById('budget-manager-modal')) {
    toggleVersionsPanel();
  }
}

// Filter displayed budget versions by search query
export function filterBudgetVersions(query) {
  const q = query.trim().toLowerCase();
  const items = document.querySelectorAll('#version-list .version-item');
  items.forEach(item => {
    const name = item.querySelector('.version-name')?.textContent?.toLowerCase() || '';
    item.style.display = !q || name.includes(q) ? '' : 'none';
  });
}

// Set current version as default
export function setDefaultVersion(versionName = currentVersionName) {
  if (defaultVersionName === versionName) {
    // Clear default if it's already set
    defaultVersionName = null;
    alert('Default version cleared.');
  } else {
    defaultVersionName = versionName;
    alert(`"${versionName}" set as default budget.`);
  }
  
  updateVersionsList();
  updateDefaultButtonStyling();
  saveUserData();
}

// Toggle default version for a specific version
export function toggleDefaultVersion(versionName) {
  setDefaultVersion(versionName);
}


// Duplicate version
export function duplicateVersion(versionName) {
  if (!budgetVersions[versionName]) return;
  
  const newName = prompt('Enter name for the copy:', `${versionName} Copy`);
  if (!newName) return;
  
  if (budgetVersions[newName]) {
    alert('A budget with that name already exists.');
    return;
  }
  
  // Deep copy the version data
  budgetVersions[newName] = {
    data: JSON.parse(JSON.stringify(budgetVersions[versionName].data)),
    lastModified: new Date().toISOString(),
    isDefault: false
  };
  
  updateVersionsList();
  saveUserData();
  alert(`Budget copied as "${newName}"`);
}

// Delete version
export async function deleteVersion(versionName) {
  if (!confirm(`Delete "${versionName}"? This cannot be undone.`)) return;
  
  try {
    // Delete from local memory
    delete budgetVersions[versionName];
    
    // Clear default if we deleted the default version
    if (defaultVersionName === versionName) {
      defaultVersionName = null;
    }
    
    // Delete from Firestore collection, then persist updated metadata
    await deleteBudgetVersionFromCollection(versionName);
    await saveUserData();
    
    updateVersionsList();
    updateWelcomeScreen();
    alert(`"${versionName}" deleted.`);
  } catch (error) {
    console.error('Error deleting version:', error);
    alert(`Error deleting "${versionName}". Please try again.`);
  }
}

// Schedule auto-save
function scheduleAutoSave() {
  // Don't schedule during initial data load — no user changes to save
  if (isBudgetInitializing) return;

  const autoSaveFrequency = autoSaveFrequencySetting;

  // Immediately flag unsaved changes so the indicator turns yellow
  setSyncing('Unsaved changes...');

  // 'manual' means the user only wants to save explicitly — indicator stays
  // yellow until the user triggers a manual save
  if (autoSaveFrequency === 'manual') return;

  if (autoSaveTimer) {
    clearTimeout(autoSaveTimer);
  }

  // 'immediate' uses a short debounce; numeric values are in seconds
  const delayMs = autoSaveFrequency === 'immediate'
    ? 500
    : (parseInt(autoSaveFrequency) || 30) * 1000;

  autoSaveTimer = setTimeout(async () => {
    try {
      await saveCurrentBudget();
    } catch (error) {
      console.error('Auto-save failed:', error);
    }
  }, delayMs);
}

// Save user data to cloud
export async function saveUserData() {
  const currentUser = getCurrentUser();
  if (!currentUser) return;

  // Don't save if user has no actual budget data (new user)
  const hasBudgetData = Object.keys(budgetVersions).length > 0 || 
                       (currentVersionName && currentVersionName.trim() !== '');
  
  if (!hasBudgetData) {
    console.log('Skipping saveUserData - no budget data to save for new user');
    return;
  }

  try {
    setSyncing('Saving data...');
    
    const userId = isLinkedAccount ? linkedAccountId : currentUser.uid;
    // Only save metadata — budget version data lives in the users/{uid}/budgets collection
    const userData = {
      currentVersionName,
      defaultVersionName,
      trackedExpenses,
      lastModified: new Date().toISOString(),
      linkedAccountId: isLinkedAccount ? currentUser.uid : null
    };

    await setDoc(doc(db, 'users', userId), userData, { merge: true });
    
    setSynced('Data saved');
  } catch (error) {
    console.error('Error saving user data:', error);
    setSyncError('Failed to save data');
    throw error;
  }
}

// ===== NEW USER-SCOPED COLLECTION FUNCTIONS =====

// Save a budget version to user-scoped collection
export async function saveBudgetVersionToCollection(versionName, budgetData) {
  const currentUser = getCurrentUser();
  if (!currentUser) return;

  try {
    setSyncing('Saving budget version...');
    
    const userId = isLinkedAccount ? linkedAccountId : currentUser.uid;
    const budgetRef = doc(db, `users/${userId}/budgets`, versionName);
    
    const budgetDoc = {
      name: versionName,
      data: budgetData,
      lastModified: new Date(),
      isDefault: versionName === defaultVersionName,
      updatedAt: new Date()
    };

    await setDoc(budgetRef, budgetDoc);
    console.log(`Budget version '${versionName}' saved to user-scoped collection`);
    
    setSynced('Budget version saved');
  } catch (error) {
    console.error('Error saving budget version to collection:', error);
    setSyncError('Failed to save budget version');
    throw error;
  }
}

// Delete a budget version from user-scoped collection
export async function deleteBudgetVersionFromCollection(versionName) {
  const currentUser = getCurrentUser();
  if (!currentUser) return;

  try {
    setSyncing('Deleting budget version...');
    
    const userId = isLinkedAccount ? linkedAccountId : currentUser.uid;
    const budgetRef = doc(db, `users/${userId}/budgets`, versionName);
    
    await deleteDoc(budgetRef);
    console.log(`Budget version '${versionName}' deleted from user-scoped collection`);
    
    setSynced('Budget version deleted');
  } catch (error) {
    console.error('Error deleting budget version from collection:', error);
    setSyncError('Failed to delete budget version');
    throw error;
  }
}

// Load all budget versions from user-scoped collection
export async function loadBudgetVersionsFromCollection() {
  const currentUser = getCurrentUser();
  if (!currentUser) return {};

  try {
    setSyncing('Loading budget versions...');
    
    const userId = isLinkedAccount ? linkedAccountId : currentUser.uid;
    console.log('loadBudgetVersionsFromCollection - userId:', userId);
    console.log('loadBudgetVersionsFromCollection - isLinkedAccount:', isLinkedAccount);
    console.log('loadBudgetVersionsFromCollection - linkedAccountId:', linkedAccountId);
    
    const budgetsRef = collection(db, `users/${userId}/budgets`);
    console.log('loadBudgetVersionsFromCollection - collection path:', `users/${userId}/budgets`);
    
    const q = query(budgetsRef, orderBy('lastModified', 'desc'));
    const querySnapshot = await getDocs(q);
    
    console.log('loadBudgetVersionsFromCollection - found', querySnapshot.docs.length, 'documents');
    
    const versions = {};
    querySnapshot.docs.forEach(doc => {
      const data = doc.data();
      console.log('loadBudgetVersionsFromCollection - document:', doc.id, data);
      versions[doc.id] = {
        name: data.name,
        data: data.data,
        lastModified: data.lastModified?.toDate?.() || data.lastModified,
        isDefault: data.isDefault || false
      };
    });
    
    console.log(`Loaded ${Object.keys(versions).length} budget versions from user-scoped collection:`, Object.keys(versions));
    setSynced('Budget versions loaded');
    return versions;
  } catch (error) {
    console.error('Error loading budget versions from collection:', error);
    setSyncError('Failed to load budget versions');
    return {};
  }
}

// Load specific budget version from user-scoped collection
export async function loadBudgetVersionFromCollection(versionName) {
  const currentUser = getCurrentUser();
  if (!currentUser) return null;

  try {
    const userId = isLinkedAccount ? linkedAccountId : currentUser.uid;
    const budgetRef = doc(db, `users/${userId}/budgets`, versionName);
    const budgetSnap = await getDoc(budgetRef);
    
    if (budgetSnap.exists()) {
      const data = budgetSnap.data();
      return {
        name: data.name,
        data: data.data,
        lastModified: data.lastModified?.toDate?.() || data.lastModified,
        isDefault: data.isDefault || false
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error loading budget version from collection:', error);
    return null;
  }
}

// Migrate from old nested structure to new user-scoped collections
export async function migrateBudgetVersionsToCollections() {
  const currentUser = getCurrentUser();
  if (!currentUser) return;

  try {
    console.log('Migrating budget versions from nested structure to collections...');
    setSyncing('Migrating budget structure...');
    
    // If budgetVersions is empty, try loading from new structure
    if (Object.keys(budgetVersions).length === 0) {
      console.log('No budget versions in memory, trying to load from collections...');
      budgetVersions = await loadBudgetVersionsFromCollection();
      updateVersionsList();
      setSynced('Budget versions loaded from new structure');
      return;
    }
    
    // Migrate each version to the new collection structure
    const migrationPromises = Object.entries(budgetVersions).map(([versionName, versionData]) => {
      return saveBudgetVersionToCollection(versionName, versionData.data);
    });
    
    await Promise.all(migrationPromises);
    
    console.log(`Migrated ${Object.keys(budgetVersions).length} budget versions to collections`);
    setSynced('Budget versions migrated');
  } catch (error) {
    console.error('Error migrating budget versions:', error);
    setSyncError('Failed to migrate budget versions');
  }
}

// ===== END NEW FUNCTIONS =====

// Load user data from cloud
export async function loadUserData() {
  const currentUser = getCurrentUser();
  if (!currentUser) {
    console.log('No current user, skipping data load');
    return;
  }

  try {
    console.log('Loading user data for:', currentUser.email);
    setSyncing('Loading data...');
    
    // First check if this user has a linked account and initialize linking state
    const currentUserDoc = await getDoc(doc(db, 'users', currentUser.uid));
    console.log('Current user document data:', currentUserDoc.exists() ? currentUserDoc.data() : 'Document does not exist');
    
    if (currentUserDoc.exists() && currentUserDoc.data().linkedAccount) {
      console.log('User has linked account:', currentUserDoc.data().linkedAccount);
      linkedAccountId = currentUserDoc.data().linkedAccount;
      isLinkedAccount = true;
      
      // Import and call setLinkingState to sync with linkSharing.js
      try {
        const { setLinkingState } = await import('./linkSharing.js');
        await setLinkingState(linkedAccountId, true);
      } catch (error) {
        console.error('Error syncing linking state:', error);
      }
    } else {
      console.log('No linked account found, user will load their own data');
      linkedAccountId = null;
      isLinkedAccount = false;
      
      // Check if this user has any followers (i.e., they are a creator)
      try {
        const { checkForFollowers } = await import('./linkSharing.js');
        await checkForFollowers();
      } catch (error) {
        console.error('Error checking for followers:', error);
      }
    }
    
    const userId = isLinkedAccount ? linkedAccountId : currentUser.uid;
    console.log('Final userId for data loading:', userId);
    console.log('isLinkedAccount:', isLinkedAccount);
    console.log('linkedAccountId:', linkedAccountId);
    
    // First try loading from new user-scoped budget collections
    console.log('Attempting to load from user-scoped budget collections...');
    const collectionVersions = await loadBudgetVersionsFromCollection();
    
    if (Object.keys(collectionVersions).length > 0) {
      console.log('Found budget versions in user-scoped collections, using new structure');
      budgetVersions = collectionVersions;
      
      // Load other user settings from main document
      const docRef = doc(db, 'users', userId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        currentVersionName = data.currentVersionName || null;
        defaultVersionName = data.defaultVersionName || null;
        trackedExpenses = data.trackedExpenses || [];
        
        // Set default version if none specified
        if (!defaultVersionName && Object.keys(budgetVersions).length > 0) {
          defaultVersionName = Object.keys(budgetVersions)[0];
        }
      }
    } else {
      // Fallback to old nested structure in main document
      console.log('No data in user-scoped collections, trying legacy nested structure...');
      const docRef = doc(db, 'users', userId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        console.log('Loaded data from Firebase:', data);
        console.log('Raw budgetVersions from Firebase:', data.budgetVersions);
        
        budgetVersions = data.budgetVersions || {};
        currentVersionName = data.currentVersionName || null;
        defaultVersionName = data.defaultVersionName || null;
        trackedExpenses = data.trackedExpenses || [];
        
        console.log('Budget versions loaded:', Object.keys(budgetVersions));
        console.log('Current version:', currentVersionName);
        console.log('Default version:', defaultVersionName);
        
        // If no saved versions, set up a fresh budget
        if (Object.keys(budgetVersions).length === 0) {
          console.log('No saved versions found, checking for current budget data...');
          
          // Check if there's budget data stored directly in the document
          if (data.income || data.categories || data.budgetData) {
            console.log('Found legacy budget data, creating main budget version');
            const legacyData = data.budgetData || {
              income: data.income || [],
              categories: data.categories || []
            };
            
            budgetVersions['Main Budget'] = {
              data: legacyData,
              lastModified: data.lastModified || new Date().toISOString(),
              isDefault: true
            };
            
            currentVersionName = 'Main Budget';
            defaultVersionName = 'Main Budget';
            
            console.log('Created main budget from legacy data:', legacyData);
          } else {
            console.log('No existing budget data found. User can start creating a new budget.');
            // Don't create empty versions - let user create their first budget by using "Save" button
          }
        }
      } else {
        console.log('No user data document found, user can start fresh');
      }
    }
    
    // Common code for both new and legacy structures
    console.log('Final budget versions:', Object.keys(budgetVersions));
    console.log('Final current version:', currentVersionName);
    console.log('Final default version:', defaultVersionName);
    
    // Load default version if exists
    if (defaultVersionName && budgetVersions[defaultVersionName]) {
      console.log('Loading default version:', defaultVersionName);
      loadBudgetVersion(defaultVersionName, false); // Don't show confirmation
    } else if (Object.keys(budgetVersions).length > 0) {
      // If no default but versions exist, load the first one
      const firstVersion = Object.keys(budgetVersions)[0];
      console.log('No default version, loading first version:', firstVersion);
      loadBudgetVersion(firstVersion, false); // Don't show confirmation
    }
    
    updateVersionsList();
    updateWelcomeScreen();
    
    // Show appropriate message based on what was loaded
    if (Object.keys(budgetVersions).length === 0) {
      setSynced('Ready - Create your first budget');
    } else {
      setSynced('Data loaded');
    }
  } catch (error) {
    console.error('Error loading user data:', error);
    setSyncError('Failed to load data');
    throw error;
  }
}

// Load linked user data (alias for loadUserData for compatibility)
export async function loadLinkedUserData() {
  return loadUserData();
}

// Export getters for state
export function getBudgetVersions() {
  // Return array of versions with metadata for comparison view
  return Object.keys(budgetVersions).map(versionName => ({
    id: versionName,
    name: versionName,
    lastModified: budgetVersions[versionName].lastModified || Date.now(),
    isDefault: budgetVersions[versionName].isDefault || false
  }));
}

// Load budget version data without modifying current budget
export async function loadBudgetVersionData(versionId) {
  // First check in-memory budgetVersions
  if (budgetVersions[versionId]) {
    return budgetVersions[versionId].data;
  }
  
  // If not in memory, try loading from user-scoped collection
  try {
    const versionData = await loadBudgetVersionFromCollection(versionId);
    if (versionData) {
      // Update in-memory cache
      budgetVersions[versionId] = versionData;
      return versionData.data;
    }
  } catch (error) {
    console.error('Error loading budget version from collection:', error);
  }
  
  throw new Error(`Budget version "${versionId}" not found`);
}

// Save a specific version with provided data
export async function saveBudgetVersion(versionName, budgetData) {
  const currentUser = getCurrentUser();
  if (!currentUser) {
    throw new Error('No user logged in');
  }

  try {
    setSyncing('Saving budget version...');
    
    budgetVersions[versionName] = {
      data: budgetData,
      lastModified: new Date().toISOString(),
      isDefault: false
    };

    // Save to user-scoped collection (new structure)
    await saveBudgetVersionToCollection(versionName, budgetData);

    setSynced('Budget version saved');
    updateVersionsList();

    return true;
  } catch (error) {
    console.error('Error saving version:', error);
    setSyncError('Failed to save version');
    throw error;
  }
}

export function getCurrentVersionName() {
  return currentVersionName;
}

export function getTrackedExpenses() {
  return trackedExpenses;
}

export function setLinkedAccount(accountId, isLinked) {
  linkedAccountId = accountId;
  isLinkedAccount = isLinked;
}

// Add new income item
export function addIncomeItem() {
  const incomeContainer = document.getElementById('income-items');
  if (!incomeContainer) return;

  const itemHTML = `
    <div class="input-group" draggable="true">
      <div class="input-group-main">
        <span class="drag-handle">⋮⋮</span>
        <label contenteditable="true">New Income Source</label>
        <div class="input-controls">
          <input type="number" class="income-input" placeholder="0.00" step="0.01">
          <select class="frequency-select" onchange="calculateTotals()" style="display: none;">
            <option value="monthly">Monthly</option>
            <option value="weekly">Weekly</option>
            <option value="bi-weekly">Bi-weekly</option>
            <option value="quarterly">Quarterly</option>
            <option value="annual">Annual</option>
            <option value="one-time">One-time</option>
          </select>
          <button class="remove-item" onclick="removeItem(this)">×</button>
        </div>
      </div>
      <div class="input-options">
        <div class="tax-container">
          <button type="button" class="tax-indicator" onclick="toggleIncomeTaxPanel(this)" title="Income Tax Settings">
            <span class="tax-badge"><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 12h-5"/><path d="M15 8h-5"/><path d="M19 17V5a2 2 0 0 0-2-2H4"/><path d="M8 21h12a2 2 0 0 0 2-2v-1a1 1 0 0 0-1-1H11a1 1 0 0 0-1 1v1a2 2 0 1 1-4 0V5a2 2 0 1 0-4 0v2a1 1 0 0 0 1 1h3"/></svg></span>
          </button>
        </div>
        <div class="timing-container">
          <button type="button" class="timing-indicator" onclick="toggleIncomeTimingPanel(this)" title="Payment Schedule">
            <span class="timing-badge"><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2v4"/><path d="M16 2v4"/><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M3 10h18"/><path d="M8 14h.01"/><path d="M12 14h.01"/><path d="M16 14h.01"/><path d="M8 18h.01"/><path d="M12 18h.01"/><path d="M16 18h.01"/></svg></span>
          </button>
        </div>
        <div class="frequency-container">
          <button type="button" class="frequency-indicator" onclick="toggleIncomeFrequencyPanel(this)" title="Income Frequency Settings">
            <span class="frequency-badge">Mo</span>
          </button>
        </div>
      </div>
      <div class="income-frequency-panel" style="display: none;"></div>
      <div class="income-tax-panel" style="display: none;"></div>
      <div class="income-timing-panel" style="display: none;"></div>
    </div>
  `;
  
  incomeContainer.insertAdjacentHTML('beforeend', itemHTML);
  
  // Setup currency formatting for new input
  const newInput = incomeContainer.lastElementChild.querySelector('.income-input');
  setupCurrencyInput(newInput, calculateTotals);
  
  // Setup drag and drop for new element
  const newElement = incomeContainer.lastElementChild;
  newElement.draggable = true;
  newElement.addEventListener('dragstart', handleDragStart);
  
  // Focus on the new label for editing and select all text
  const newLabel = newElement.querySelector('label');
  newLabel.focus();
  
  // Select all text when focused
  newLabel.addEventListener('focus', function() {
    // Use setTimeout to ensure the selection happens after focus
    setTimeout(() => {
      if (window.getSelection && document.createRange) {
        const range = document.createRange();
        range.selectNodeContents(this);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }, 0);
  });
  
  // Clear default text when editing starts
  newLabel.addEventListener('keydown', function(e) {
    if (this.textContent === 'New Income Source') {
      // Clear the text on first keypress
      setTimeout(() => {
        if (e.key.length === 1 || e.key === 'Backspace' || e.key === 'Delete') {
          this.textContent = '';
        }
      }, 0);
    }
  });
}

// Add new expense category
export function addExpenseCategory() {
  const expenseContainer = document.getElementById('expense-items');
  if (!expenseContainer) return;

  // Generate unique IDs for this category's items
  const savingsId = generateUniqueId('savings');
  const taxId = generateUniqueId('tax');
  const recurringId = generateUniqueId('recurring');

  const categoryHTML = `
    <div class="category-section" draggable="true">
      <div class="category-header">
        <h3 contenteditable="true">💰 New Category</h3>
        <div class="category-controls">
          <button class="add-to-category" onclick="addItemToCategory(this)">+ Add Item</button>
          <button class="remove-category" onclick="removeCategory(this)">Remove</button>
        </div>
      </div>
      <div class="input-group" draggable="true">
        <div class="input-group-main">
          <span class="drag-handle">⋮⋮</span>
          <label contenteditable="true">New Expense Item</label>
          <div class="input-controls">
            <input type="number" class="expense-input" placeholder="0.00" step="0.01">
            <select class="frequency-select" onchange="calculateTotals()">
              <option value="monthly">Monthly</option>
              <option value="weekly">Weekly</option>
              <option value="annual">Annual</option>
              <option value="one-time">One-time</option>
            </select>
            <button class="remove-item" onclick="removeItem(this)">×</button>
          </div>
        </div>
        <div class="input-options">
          <div class="savings-container">
            <input type="checkbox" class="savings-checkbox" id="${savingsId}" onchange="calculateTotals()">
            <button type="button" class="savings-indicator" onclick="document.getElementById('${savingsId}').click()" title="Savings Item">
              <span class="savings-badge">$</span>
            </button>
          </div>
          <div class="frequency-container">
            <button type="button" class="frequency-indicator" onclick="toggleFrequencyPanel(this)" title="Frequency Settings">
              <span class="frequency-badge">Mo</span>
            </button>
          </div>
          <div class="tax-deductible-container">
            <input type="checkbox" class="tax-deductible-checkbox" id="${taxId}">
            <label for="${taxId}" class="tax-deductible-label" title="Tax Deductible">
              <span class="tax-icon"><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 12h-5"/><path d="M15 8h-5"/><path d="M19 17V5a2 2 0 0 0-2-2H4"/><path d="M8 21h12a2 2 0 0 0 2-2v-1a1 1 0 0 0-1-1H11a1 1 0 0 0-1 1v1a2 2 0 1 1-4 0V5a2 2 0 1 0-4 0v2a1 1 0 0 0 1 1h3"/></svg></span>
            </label>
          </div>
          <div class="recurring-container">
            <input type="checkbox" class="recurring-checkbox" id="${recurringId}" onchange="toggleRecurringPanel(this)">
            <label for="${recurringId}" class="recurring-label" title="Recurring Expense">
              <span class="recurring-icon"><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/><path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"/><path d="M16 16h5v5"/></svg></span>
            </label>
          </div>
        </div>
        <div class="frequency-panel" style="display: none;">
          <!-- Frequency configuration will be inserted here -->
        </div>
        <div class="recurring-panel" style="display: none;">
          <!-- Recurring configuration will be inserted here -->
        </div>
      </div>
      <div class="category-subtotal">
        <div class="category-subtotal-header">
          <span class="target-icon" onclick="openTargetModal(this)" title="Set Target"><svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg></span>
          <span class="category-total">$0.00</span><span class="category-target-summary" style="display: none;"> planned of <span class="category-target-display">$0</span> (<span class="category-difference">$0</span>)</span>
        </div>
        <div class="target-modal">
          <div class="target-modal-content">
            <div class="target-modal-header">Set Category Target</div>
            <div class="target-modal-controls">
            <select class="target-type-select" onchange="updateModalInputRow(this)">
              <option value="none">No Target</option>
              <option value="percentage">% of Income</option>
              <option value="fixed">Fixed Amount</option>
            </select>
            <div class="target-input-row" style="display: none;">
              <input type="text" class="target-input-field" placeholder="0">
              <span class="target-suffix-text">%</span>
            </div>
          </div>
          <div class="target-modal-buttons">
            <button class="target-modal-btn primary" onclick="saveTargetFromModal(this)">Save</button>
            <button class="target-modal-btn secondary" onclick="closeTargetModal(this)">Cancel</button>
          </div>
          </div>
        </div>
      </div>
    </div>
  `;
  
  expenseContainer.insertAdjacentHTML('beforeend', categoryHTML);
  
  // Setup currency formatting for new input
  const newCategory = expenseContainer.lastElementChild;
  const newInput = newCategory.querySelector('.expense-input');
  setupCurrencyInput(newInput, calculateTotals);
  
  // Setup tax deductible checkbox event listener
  const taxCheckbox = newCategory.querySelector('.tax-deductible-checkbox');
  if (taxCheckbox) {
    taxCheckbox.addEventListener('change', calculateTotals);
  }
  
  // Setup savings checkbox event listener
  const savingsCheckbox = newCategory.querySelector('.savings-checkbox');
  if (savingsCheckbox) {
    savingsCheckbox.addEventListener('change', calculateTotals);
  }
  
  // Setup drag and drop for new category and its items
  newCategory.draggable = true;
  newCategory.addEventListener('dragstart', handleDragStart);
  
  const newItem = newCategory.querySelector('.input-group');
  newItem.draggable = true;
  newItem.addEventListener('dragstart', handleDragStart);
  
  // Focus on the category name for editing
  const categoryTitle = newCategory.querySelector('h3');
  categoryTitle.focus();
}

// Add item to existing category
export function addItemToCategory(button) {
  const categorySection = button.closest('.category-section');
  if (!categorySection) return;

  // Generate unique IDs for this item
  const savingsId = generateUniqueId('savings');
  const taxId = generateUniqueId('tax');
  const recurringId = generateUniqueId('recurring');

  const itemHTML = `
    <div class="input-group" draggable="true">
      <div class="input-group-main">
        <span class="drag-handle">⋮⋮</span>
        <label contenteditable="true">New Item</label>
        <div class="input-controls">
          <input type="number" class="expense-input" placeholder="0.00" step="0.01">
          <select class="frequency-select" onchange="calculateTotals()">
            <option value="monthly">Monthly</option>
            <option value="weekly">Weekly</option>
            <option value="bi-weekly">Bi-weekly</option>
            <option value="quarterly">Quarterly</option>
            <option value="annual">Annual</option>
            <option value="one-time">One-time</option>
            <option value="plan-it">Plan It</option>
          </select>
          <button class="remove-item" onclick="removeItem(this)">×</button>
        </div>
      </div>
      <div class="input-options">
        <div class="savings-container">
          <input type="checkbox" class="savings-checkbox" id="${savingsId}" onchange="calculateTotals()">
          <button type="button" class="savings-indicator" onclick="document.getElementById('${savingsId}').click()" title="Savings Item">
            <span class="savings-badge">$</span>
          </button>
        </div>
        <div class="frequency-container">
          <button type="button" class="frequency-indicator" onclick="toggleFrequencyPanel(this)" title="Frequency Settings">
            <span class="frequency-badge">Mo</span>
          </button>
        </div>
        <div class="tax-deductible-container">
          <input type="checkbox" class="tax-deductible-checkbox" id="${taxId}">
          <label for="${taxId}" class="tax-deductible-label" title="Tax Deductible">
            <span class="tax-icon">🧾</span>
          </label>
        </div>
        <div class="recurring-container">
          <input type="checkbox" class="recurring-checkbox" id="${recurringId}" onchange="toggleRecurringPanel(this)">
          <label for="${recurringId}" class="recurring-label" title="Recurring Expense">
            <span class="recurring-icon"><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/><path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"/><path d="M16 16h5v5"/></svg></span>
          </label>
        </div>
      </div>
      <div class="frequency-panel" style="display: none;">
        <!-- Frequency configuration will be inserted here -->
      </div>
      <div class="recurring-panel" style="display: none;">
        <!-- Recurring configuration will be inserted here -->
      </div>
    </div>
  `;
  
  // Find the subtotal element and insert the new item before it
  const subtotalElement = categorySection.querySelector('.category-subtotal');
  if (subtotalElement) {
    subtotalElement.insertAdjacentHTML('beforebegin', itemHTML);
  } else {
    // Fallback: if no subtotal found, add to end
    categorySection.insertAdjacentHTML('beforeend', itemHTML);
  }
  
  // Setup currency formatting for new input
  const newInputGroup = subtotalElement ? 
    subtotalElement.previousElementSibling : 
    categorySection.lastElementChild;
  const newInput = newInputGroup.querySelector('.expense-input');
  setupCurrencyInput(newInput, calculateTotals);
  
  // Setup tax deductible checkbox event listener
  const taxCheckbox = newInputGroup.querySelector('.tax-deductible-checkbox');
  if (taxCheckbox) {
    taxCheckbox.addEventListener('change', calculateTotals);
  }
  
  // Setup savings checkbox event listener
  const savingsCheckbox = newInputGroup.querySelector('.savings-checkbox');
  if (savingsCheckbox) {
    savingsCheckbox.addEventListener('change', calculateTotals);
  }
  
  // Setup drag and drop for new item
  newInputGroup.draggable = true;
  newInputGroup.addEventListener('dragstart', handleDragStart);
  
  // Focus on the new label for editing
  const newLabel = newInputGroup.querySelector('label');
  if (newLabel) {
    // Add focus event listener to clear placeholder text
    newLabel.addEventListener('focus', function() {
      if (this.textContent.trim() === 'New Item') {
        this.textContent = '';
      }
    });
    
    // Add blur event listener to restore placeholder if empty
    newLabel.addEventListener('blur', function() {
      if (this.textContent.trim() === '') {
        this.textContent = 'New Item';
      }
    });
    
    newLabel.focus();
  }
}

// Remove item
export function removeItem(button) {
  const inputGroup = button.closest('.input-group');
  if (inputGroup && confirm('Remove this item?')) {
    inputGroup.remove();
    calculateTotals();
  }
}

// Remove category
export function removeCategory(button) {
  const categorySection = button.closest('.category-section');
  if (categorySection && confirm('Remove this entire category?')) {
    categorySection.remove();
    calculateTotals();
  }
}

// Setup drag and drop functionality
function setupDragAndDrop() {
  // Setup drag and drop for all draggable elements
  document.addEventListener('dragstart', handleDragStart);
  document.addEventListener('dragover', handleDragOver);
  document.addEventListener('drop', handleDrop);
  document.addEventListener('dragend', handleDragEnd);
  
  // Make sure all existing elements are properly configured
  updateDragAndDropElements();
}

// Update drag and drop elements (call after adding new items)
function updateDragAndDropElements() {
  // Setup category sections
  const categorySection = document.querySelectorAll('.category-section');
  categorySection.forEach(section => {
    section.draggable = true;
    section.addEventListener('dragstart', handleDragStart);
  });
  
  // Setup input groups (items)
  const inputGroups = document.querySelectorAll('.input-group');
  inputGroups.forEach(group => {
    group.draggable = true;
    group.addEventListener('dragstart', handleDragStart);
  });
}

let draggedElement = null;
let draggedType = null; // 'category' or 'item'
let dragSourceContainer = null;

// Check if a target is a valid drop zone for the current drag type
function isValidDropTarget(target) {
  if (!draggedElement || !draggedType) return false;
  
  if (draggedType === 'category') {
    // Categories can only be dropped on other categories or the expense container
    return target.classList.contains('category-section') || target.id === 'expense-items';
  } else if (draggedType === 'item') {
    // Items can be dropped on categories, other items, or their respective containers
    const draggedFromIncome = draggedElement.closest('#income-items');
    const draggedFromExpense = draggedElement.closest('#expense-items');
    
    if (draggedFromIncome) {
      // Income items can only be dropped on other income items or income container
      return target.classList.contains('input-group') && target.closest('#income-items') ||
             target.id === 'income-items';
    } else if (draggedFromExpense) {
      // Expense items can be dropped on categories, other expense items, or expense container
      return target.classList.contains('category-section') ||
             (target.classList.contains('input-group') && target.closest('#expense-items')) ||
             target.id === 'expense-items';
    }
  }
  
  return false;
}

function handleDragStart(e) {
  draggedElement = e.target;
  dragSourceContainer = draggedElement.parentNode;
  
  if (draggedElement.classList.contains('category-section')) {
    draggedType = 'category';
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', draggedElement.outerHTML);
    draggedElement.style.opacity = '0.5';
  } else if (draggedElement.classList.contains('input-group')) {
    draggedType = 'item';
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', draggedElement.outerHTML);
    draggedElement.style.opacity = '0.5';
  }
}

function handleDragOver(e) {
  e.preventDefault();
  e.dataTransfer.dropEffect = 'move';
  
  // Clear all previous indicators first
  document.querySelectorAll('.category-section, .input-group, #expense-items, #income-items').forEach(el => {
    el.style.borderTop = '';
  });
  
  // Add visual feedback only to valid drop targets
  const target = e.target.closest('.category-section, .input-group, #expense-items, #income-items');
  if (target && target !== draggedElement && isValidDropTarget(target)) {
    target.style.borderTop = '2px solid #3498db';
  }
}

function handleDrop(e) {
  e.preventDefault();
  e.stopPropagation();
  
  // Remove visual feedback
  document.querySelectorAll('.category-section, .input-group, #expense-items, #income-items').forEach(el => {
    el.style.borderTop = '';
  });
  
  if (!draggedElement) return;
  
  const dropTarget = e.target.closest('.category-section, .input-group, #expense-items, #income-items');
  
  // Only proceed if it's a valid drop target
  if (dropTarget && dropTarget !== draggedElement && isValidDropTarget(dropTarget)) {
    if (draggedType === 'category') {
      // Moving a category
      if (dropTarget.id === 'expense-items') {
        // Dropped on expense container - move to end
        dropTarget.appendChild(draggedElement);
      } else if (dropTarget.classList.contains('category-section')) {
        // Dropped on another category - insert before it
        dropTarget.parentNode.insertBefore(draggedElement, dropTarget);
      }
    } else if (draggedType === 'item') {
      // Moving an item
      const draggedFromIncome = draggedElement.closest('#income-items');
      
      if (draggedFromIncome) {
        // Income item logic
        if (dropTarget.classList.contains('input-group') && dropTarget.closest('#income-items')) {
          // Dropped on another income item - reorder
          dropTarget.parentNode.insertBefore(draggedElement, dropTarget);
        } else if (dropTarget.id === 'income-items') {
          // Dropped on income container - move to end
          dropTarget.appendChild(draggedElement);
        }
      } else {
        // Expense item logic
        if (dropTarget.classList.contains('category-section')) {
          // Dropped on a category - add to end of category
          dropTarget.appendChild(draggedElement);
        } else if (dropTarget.classList.contains('input-group')) {
          // Dropped on another item
          const targetCategory = dropTarget.closest('.category-section');
          const draggedCategory = draggedElement.closest('.category-section');
          
          if (targetCategory === draggedCategory) {
            // Same category - reorder
            dropTarget.parentNode.insertBefore(draggedElement, dropTarget);
          } else {
            // Different category - move to new category, insert before target item
            targetCategory.insertBefore(draggedElement, dropTarget);
          }
        } else if (dropTarget.id === 'expense-items') {
          // This shouldn't happen with expense items, but handle gracefully
          console.warn('Expense item dropped on expense container - ignoring');
        }
      }
    }
    
    // Recalculate totals after move
    calculateTotals();
    
    // Setup currency inputs for moved elements
    const movedInputs = draggedElement.querySelectorAll('.income-input, .expense-input');
    movedInputs.forEach(input => {
      setupCurrencyInput(input, calculateTotals);
    });
  }
}

function handleDragEnd(e) {
  // Reset opacity
  if (draggedElement) {
    draggedElement.style.opacity = '1';
  }
  
  // Clear drag state
  draggedElement = null;
  draggedType = null;
  dragSourceContainer = null;
  
  // Remove all visual feedback
  document.querySelectorAll('.category-section, .input-group, #expense-items, #income-items').forEach(el => {
    el.style.borderTop = '';
  });
}

// Parse CSV data into budget format
function parseCSV(csvText) {
  const lines = csvText.trim().split('\n');
  const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim());
  
  console.log('CSV Headers:', headers);
  
  const data = {
    income: [],
    categories: []
  };
  
  // Group items by category
  const categoryMap = new Map();
  
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    
    // Simple CSV parsing - handles quoted values
    const values = [];
    let currentValue = '';
    let insideQuotes = false;
    
    for (let j = 0; j < line.length; j++) {
      const char = line[j];
      if (char === '"') {
        insideQuotes = !insideQuotes;
      } else if (char === ',' && !insideQuotes) {
        values.push(currentValue.trim());
        currentValue = '';
      } else {
        currentValue += char;
      }
    }
    values.push(currentValue.trim()); // Add the last value
    
    if (values.length >= 3) {
      const category = values[0];
      const item = values[1];
      const amount = parseFloat(values[2]) || 0;
      
      console.log(`Parsed: ${category} -> ${item} = ${amount}`);
      
      if (category.toLowerCase() === 'income') {
        data.income.push({
          label: item,
          amount: amount
        });
      } else {
        // Group all non-income items by category
        if (!categoryMap.has(category)) {
          categoryMap.set(category, []);
        }
        categoryMap.get(category).push({
          label: item,
          amount: amount
        });
      }
    }
  }
  
  // Convert categoryMap to categories array
  for (const [categoryName, items] of categoryMap) {
    // Add appropriate emoji prefix based on category name
    let displayName = categoryName;
    const lowerName = categoryName.toLowerCase();
    
    if (lowerName.includes('housing') || lowerName.includes('home')) {
      displayName = `🏠 ${categoryName}`;
    } else if (lowerName.includes('transport') || lowerName.includes('car')) {
      displayName = `🚗 ${categoryName}`;
    } else if (lowerName.includes('living') || lowerName.includes('groceries') || lowerName.includes('food')) {
      displayName = `🛒 ${categoryName}`;
    } else if (lowerName.includes('health') || lowerName.includes('medical')) {
      displayName = `🏥 ${categoryName}`;
    } else if (lowerName.includes('entertainment') || lowerName.includes('fun')) {
      displayName = `🎬 ${categoryName}`;
    } else if (lowerName.includes('banking') || lowerName.includes('savings')) {
      displayName = `🏦 ${categoryName}`;
    } else if (lowerName.includes('pets') || lowerName.includes('pet')) {
      displayName = `🐕 ${categoryName}`;
    } else if (lowerName.includes('education') || lowerName.includes('learning')) {
      displayName = `📚 ${categoryName}`;
    } else {
      displayName = `💰 ${categoryName}`;
    }
    
    data.categories.push({
      name: displayName,
      items: items
    });
  }
  
  console.log('Parsed CSV into budget format:', data);
  return data;
}

// Import budget from file (JSON or CSV)
export function importBudgetFromFile() {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = '.json,.csv';
  input.onchange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    try {
      const text = await file.text();
      const fileExtension = file.name.toLowerCase().split('.').pop();
      
      let importedData;
      
      if (fileExtension === 'csv') {
        // Parse CSV file
        importedData = parseCSV(text);
        console.log('Imported CSV data:', importedData);
        
        // Create budget version from CSV
        const versionName = prompt('Enter a name for this imported budget:', file.name.replace('.csv', ''));
        if (!versionName) return;
        
        if (budgetVersions[versionName]) {
          alert('A budget with that name already exists.');
          return;
        }
        
        budgetVersions[versionName] = {
          data: importedData,
          lastModified: new Date().toISOString(),
          isDefault: false
        };

        await saveBudgetVersionToCollection(versionName, importedData);
        await saveUserData();
        updateVersionsList();

        alert(`Successfully imported "${versionName}"!`);

        // Load the imported budget
        if (confirm(`Load "${versionName}" now?`)) {
          loadBudgetVersion(versionName, false);
        }
        
      } else {
        // Parse JSON file (existing logic)
        importedData = JSON.parse(text);
        console.log('Imported JSON data:', importedData);
        
        // Check if this looks like a budget versions backup
        if (importedData.budgetVersions || importedData.versions) {
          const versions = importedData.budgetVersions || importedData.versions || importedData;
          
          if (confirm(`Import ${Object.keys(versions).length} budget versions from "${file.name}"? This will add to your existing budgets.`)) {
            // Merge with existing versions (don't overwrite), tracking resolved names
            const importedEntries = [];
            Object.keys(versions).forEach(versionName => {
              let finalName = versionName;
              let counter = 1;

              // Avoid name conflicts
              while (budgetVersions[finalName]) {
                finalName = `${versionName} (${counter})`;
                counter++;
              }

              const versionData = versions[versionName].data || versions[versionName];
              budgetVersions[finalName] = {
                data: versionData,
                lastModified: versions[versionName].lastModified || new Date().toISOString(),
                isDefault: false
              };
              importedEntries.push({ name: finalName, data: versionData });
            });

            // Save each imported version to the collection, then update metadata
            await Promise.all(importedEntries.map(({ name, data }) => saveBudgetVersionToCollection(name, data)));
            await saveUserData();
            updateVersionsList();

            alert(`Successfully imported ${Object.keys(versions).length} budget versions!`);
            console.log('Import completed. Current versions:', Object.keys(budgetVersions));
          }
        } else {
          // Single budget data
          const versionName = prompt('Enter a name for this imported budget:', 'Imported Budget');
          if (versionName && !budgetVersions[versionName]) {
            budgetVersions[versionName] = {
              data: importedData,
              lastModified: new Date().toISOString(),
              isDefault: false
            };

            await saveBudgetVersionToCollection(versionName, importedData);
            await saveUserData();
            updateVersionsList();

            alert(`Successfully imported "${versionName}"!`);
          } else if (budgetVersions[versionName]) {
            alert('A budget with that name already exists.');
          }
        }
      }
    } catch (error) {
      console.error('Error importing budget:', error);
      alert('Error reading file. Please make sure it\'s a valid JSON or CSV budget file.');
    }
  };
  
  input.click();
}

// Initialize budget management
export async function initializeBudget(user) {
  if (user) {
    console.log('Initializing budget for user:', user.email);

    try {
      isBudgetInitializing = true;

      // Load user data (including linked account detection) before continuing
      await loadUserData();
      console.log('User data loaded, budget versions:', Object.keys(budgetVersions));

      // Setup event listeners for existing budget inputs
      const inputs = document.querySelectorAll('.income-input, .expense-input');
      inputs.forEach(input => {
        setupCurrencyInput(input, calculateTotals);
      });

      // Setup drag and drop
      setupDragAndDrop();

      // Initial calculation
      calculateTotals();

      // Update displays
      updateVersionDisplay();

      // Initialize budget name editing
      initializeBudgetNameEditing();

      // Initialize inline editing for all contenteditable elements
      initializeInlineEditing();
    } catch (error) {
      console.error('Failed to initialize budget:', error);
    } finally {
      isBudgetInitializing = false;
    }
  }
}

/**
 * Category Target Functions
 */

// Open target modal
export function openTargetModal(iconElement) {
  const categorySection = iconElement.closest('.category-section');
  const modal = categorySection.querySelector('.target-modal');
  
  // If modal is already open, close it
  if (modal.classList.contains('active')) {
    modal.classList.remove('active');
    return;
  }
  
  // Close any other open modals first
  closeAllTargetModals();
  
  const select = modal.querySelector('.target-type-select');
  const inputRow = modal.querySelector('.target-input-row');
  const input = modal.querySelector('.target-input-field');
  const suffix = modal.querySelector('.target-suffix-text');
  
  // Load current values
  const currentType = categorySection.dataset.targetType || 'none';
  const currentValue = categorySection.dataset.targetValue || '';
  
  select.value = currentType;
  input.value = currentValue;
  
  // Setup the input row based on current type
  updateModalInputRow(select);
  
  // Show modal
  modal.classList.add('active');
  
  // Focus input if there's a target type selected
  if (currentType !== 'none') {
    setTimeout(() => input.focus(), 100);
  }
}

// Close target modal
export function closeTargetModal(buttonElement) {
  const modal = buttonElement.closest('.target-modal');
  modal.classList.remove('active');
}

// Close all target modals
function closeAllTargetModals() {
  const openModals = document.querySelectorAll('.target-modal.active');
  openModals.forEach(modal => modal.classList.remove('active'));
}

// Update modal input row based on selected type
export function updateModalInputRow(selectElement) {
  const modal = selectElement.closest('.target-modal');
  const inputRow = modal.querySelector('.target-input-row');
  const input = modal.querySelector('.target-input-field');
  const suffix = modal.querySelector('.target-suffix-text');
  
  const targetType = selectElement.value;
  
  if (targetType === 'none') {
    inputRow.style.display = 'none';
    input.value = '';
  } else {
    inputRow.style.display = 'flex';
    
    if (targetType === 'percentage') {
      suffix.textContent = '%';
      input.placeholder = '0';
    } else if (targetType === 'fixed') {
      suffix.textContent = '$';
      input.placeholder = '0.00';
    }
  }
}

// Save target from modal
export function saveTargetFromModal(buttonElement) {
  const modal = buttonElement.closest('.target-modal');
  const categorySection = modal.closest('.category-section');
  const select = modal.querySelector('.target-type-select');
  const input = modal.querySelector('.target-input-field');
  
  const targetType = select.value;
  const targetValue = parseFloat(input.value) || 0;
  
  // Update category data
  categorySection.dataset.targetType = targetType;
  categorySection.dataset.targetValue = targetValue;
  
  
  // Update icon opacity
  updateTargetIconState(categorySection);
  
  // Update the display
  updateCategoryTargetFromData(categorySection);
  
  // Trigger recalculation to update totals
  calculateTotals();
  
  // Auto-save the budget
  saveCurrentBudget();



  // Close modal
  closeTargetModal(buttonElement);
}

// Update target icon state based on whether target is set
function updateTargetIconState(categorySection) {
  const targetIcon = categorySection.querySelector('.target-icon');
  const targetType = categorySection.dataset.targetType;
  const targetValue = parseFloat(categorySection.dataset.targetValue) || 0;
  
  if (targetType && targetType !== 'none' && targetValue > 0) {
    targetIcon.classList.add('has-target');
  } else {
    targetIcon.classList.remove('has-target');
  }
}

// Update category target display from stored data
function updateCategoryTargetFromData(categorySection) {
  const targetType = categorySection.dataset.targetType;
  const targetValue = parseFloat(categorySection.dataset.targetValue) || 0;
  const actualTotal = getCategoryActualTotal(categorySection);
  
  const categoryTargetSummary = categorySection.querySelector('.category-target-summary');
  const categoryTargetDisplay = categorySection.querySelector('.category-target-display');
  const categoryDifference = categorySection.querySelector('.category-difference');
  
  if (!categoryTargetSummary || !categoryTargetDisplay || !categoryDifference) {
    return;
  }
  
  if (!targetType || targetType === 'none' || targetValue === 0) {
    categoryTargetSummary.style.display = 'none';
    return;
  }
  
  let targetAmount = 0;
  
  if (targetType === 'percentage') {
    const totalIncome = getTotalIncome();
    targetAmount = (totalIncome * targetValue) / 100;
  } else if (targetType === 'fixed') {
    targetAmount = targetValue;
  }
  
  // Update target display
  categoryTargetDisplay.textContent = formatCurrencyClean(targetAmount);
  
  // Calculate and display difference
  const difference = targetAmount - actualTotal;

  // Show the target summary
  categoryTargetSummary.style.display = 'inline';

  categoryDifference.style.display = 'inline';
  if (Math.abs(difference) < 1) {
    // Effectively zero — show dash
    categoryDifference.textContent = '-';
    categoryDifference.style.color = 'white';
  } else if (difference > 0) {
    // Under budget - show as negative (need more)
    categoryDifference.textContent = `-${formatCurrencyClean(Math.abs(difference))}`;
    categoryDifference.style.color = '#28a745'; // Green - under target is good
  } else {
    // Over budget - show as positive (over by)
    categoryDifference.textContent = `+${formatCurrencyClean(Math.abs(difference))}`;
    categoryDifference.style.color = '#dc3545'; // Red - over target is bad
  }
}

// Update category target amount
export function updateCategoryTarget(inputElement) {
  const categorySection = inputElement.closest('.category-section');
  const selectElement = categorySection.querySelector('.target-type');
  const targetAmountDisplay = categorySection.querySelector('.target-amount');
  const categoryTargetSummary = categorySection.querySelector('.category-target-summary');
  const categoryTargetDisplay = categorySection.querySelector('.category-target-display');
  const categoryDifference = categorySection.querySelector('.category-difference');
  
  const targetType = selectElement.value;
  const inputValue = parseFloat(inputElement.value) || 0;
  
  let targetAmount = 0;
  
  if (targetType === 'percentage' && inputValue > 0) {
    const totalIncome = getTotalIncome();
    targetAmount = (totalIncome * inputValue) / 100;
  } else if (targetType === 'fixed') {
    targetAmount = inputValue;
  }
  
  // Update target displays
  targetAmountDisplay.textContent = formatCurrency(targetAmount);
  categoryTargetDisplay.textContent = formatCurrencyClean(targetAmount);
  
  // Calculate difference (target - actual)
  const actualTotal = getCategoryActualTotal(categorySection);
  const difference = targetAmount - actualTotal;
  
  // Show/hide target summary based on whether target is set
  if (targetAmount > 0) {
    categoryTargetSummary.style.display = 'inline-block';
    
    categoryDifference.style.display = 'inline';
    if (Math.abs(difference) < 1) {
      // Effectively zero — show dash
      categoryDifference.textContent = '-';
      categoryDifference.style.color = 'white';
    } else if (difference > 0) {
      // Under budget - show as negative (need more)
      categoryDifference.textContent = `-${formatCurrencyClean(Math.abs(difference))}`;
      categoryDifference.style.color = '#28a745'; // Green - under target is good
    } else {
      // Over budget - show as positive (over by)
      categoryDifference.textContent = `+${formatCurrencyClean(Math.abs(difference))}`;
      categoryDifference.style.color = '#dc3545'; // Red - over target is bad
    }
  } else {
    categoryTargetSummary.style.display = 'none';
  }
  
  // Store target data on category element for persistence
  categorySection.dataset.targetType = targetType;
  categorySection.dataset.targetValue = inputValue;
}

// Get actual total for a category
function getCategoryActualTotal(categorySection) {
  let categoryTotal = 0;
  const expenseInputs = categorySection.querySelectorAll('.expense-input');
  
  expenseInputs.forEach(input => {
    const group = input.closest('.input-group');
    const frequencySelect = group.querySelector('.frequency-select');
    const frequency = frequencySelect ? frequencySelect.value : 'monthly';
    const amount = parseCurrencyInput(input.value);
    
    // Convert to monthly amount
    const monthlyAmount = convertToMonthlyAmount(amount, frequency, group);
    categoryTotal += monthlyAmount;
  });
  
  return categoryTotal;
}

// Update all category targets (called when income changes)
export function updateAllCategoryTargets() {
  const categoryTargetInputs = document.querySelectorAll('.target-input');
  categoryTargetInputs.forEach(input => {
    if (input.value) {
      updateCategoryTarget(input);
    }
  });
}

// Update category target display during calculateTotals
function updateCategoryTargetDisplay(categorySection, actualTotal) {
  const targetType = categorySection.dataset.targetType;
  const targetValue = parseFloat(categorySection.dataset.targetValue) || 0;
  
  const categoryTargetSummary = categorySection.querySelector('.category-target-summary');
  const categoryTargetDisplay = categorySection.querySelector('.category-target-display');
  const categoryDifference = categorySection.querySelector('.category-difference');
  
  if (!categoryTargetSummary || !categoryTargetDisplay || !categoryDifference) {
    return;
  }
  
  if (!targetType || targetType === 'none' || targetValue === 0) {
    categoryTargetSummary.style.display = 'none';
    return;
  }
  
  let targetAmount = 0;
  
  if (targetType === 'percentage') {
    const totalIncome = getTotalIncome();
    targetAmount = (totalIncome * targetValue) / 100;
  } else if (targetType === 'fixed') {
    targetAmount = targetValue;
  }
  
  // Update target display
  categoryTargetDisplay.textContent = formatCurrencyClean(targetAmount);
  
  // Calculate and display difference
  const difference = targetAmount - actualTotal;
  
  // Show the target summary
  categoryTargetSummary.style.display = 'inline-block';
  
  categoryDifference.style.display = 'inline';
  if (Math.abs(difference) < 1) {
    // Effectively zero — show dash
    categoryDifference.textContent = '-';
    categoryDifference.style.color = 'white';
  } else if (difference > 0) {
    // Under budget - show as negative (need more)
    categoryDifference.textContent = `-${formatCurrencyClean(Math.abs(difference))}`;
    categoryDifference.style.color = '#28a745'; // Green - under target is good
  } else {
    // Over budget - show as positive (over by)
    categoryDifference.textContent = `+${formatCurrencyClean(Math.abs(difference))}`;
    categoryDifference.style.color = '#dc3545'; // Red - over target is bad
  }
}

/**
 * Helper functions for expense tracker integration
 */

// Get current budget data for expense tracker
export function getCurrentBudgetData() {
  const currentVersion = budgetVersions[currentVersionName];
  return currentVersion ? currentVersion.data : null;
}

// Get current budget ID (using current version name as ID for now)
export function getCurrentBudgetId() {
  const user = getCurrentUser();
  if (!user) return null;
  
  // For linked accounts, use the target user's ID (owner's ID for followers)
  const targetUserId = isLinkedAccount ? linkedAccountId : user.uid;
  
  // Create a consistent budget ID from target user ID and version name
  return `${targetUserId}_${currentVersionName}`;
}

/**
 * Frequency Panel Functions
 */

// Toggle frequency panel visibility
export function toggleFrequencyPanel(button) {
  const inputGroup = button.closest('.input-group');
  const frequencyPanel = inputGroup.querySelector('.frequency-panel');
  const frequencySelect = inputGroup.querySelector('.frequency-select');
  
  if (frequencyPanel.style.display === 'none' || !frequencyPanel.style.display) {
    // Show the panel and populate it
    const currentFrequency = frequencySelect.value || 'monthly';
    const currentAmount = parseCurrencyInput(inputGroup.querySelector('.expense-input').value || '0');
    
    populateFrequencyPanel(frequencyPanel, currentFrequency, currentAmount, inputGroup);
    frequencyPanel.style.display = 'block';
  } else {
    // Hide the panel
    frequencyPanel.style.display = 'none';
  }
}

// Populate frequency panel with current settings and options
function populateFrequencyPanel(panel, currentFrequency, currentAmount, inputGroup) {
  const monthlyAmount = convertToMonthlyAmount(currentAmount, currentFrequency, inputGroup);
  
  // Get existing Plan It date if available
  const existingPlanItDate = inputGroup.getAttribute('data-plan-it-date');
  const planItDateValue = existingPlanItDate || getTodayPlusMonths(6);
  
  panel.innerHTML = `
    <div class="frequency-panel-header">
      <span>Frequency Settings</span>
      <span class="annual-calculation">Monthly: ${formatCurrency(monthlyAmount)}</span>
    </div>
    
    <div class="frequency-options">
      <div class="frequency-option ${currentFrequency === 'weekly' ? 'selected' : ''}" data-frequency="weekly">
        Weekly
      </div>
      <div class="frequency-option ${currentFrequency === 'bi-weekly' ? 'selected' : ''}" data-frequency="bi-weekly">
        Bi-weekly
      </div>
      <div class="frequency-option ${currentFrequency === 'monthly' ? 'selected' : ''}" data-frequency="monthly">
        Monthly
      </div>
      <div class="frequency-option ${currentFrequency === 'quarterly' ? 'selected' : ''}" data-frequency="quarterly">
        Quarterly
      </div>
      <div class="frequency-option ${currentFrequency === 'annual' ? 'selected' : ''}" data-frequency="annual">
        Annual
      </div>
      <div class="frequency-option ${currentFrequency === 'one-time' ? 'selected' : ''}" data-frequency="one-time">
        One-time
      </div>
      <div class="frequency-option ${currentFrequency === 'plan-it' ? 'selected' : ''}" data-frequency="plan-it">
        Plan It
      </div>
    </div>
    
    <div class="plan-it-section" style="display: ${currentFrequency === 'plan-it' ? 'block' : 'none'};">
      <h5>📅 Plan It Settings</h5>
      <div class="plan-it-inputs">
        <label>Target Date:</label>
        <input type="date" class="plan-it-date" value="${planItDateValue}">
      </div>
      <div class="plan-it-calculation">
        Saving <strong>${formatCurrency(0)}/month</strong> until target date
      </div>
    </div>
    
    <div class="frequency-panel-actions">
      <button class="panel-btn" onclick="cancelFrequencyConfig(this)">Cancel</button>
      <button class="panel-btn primary" onclick="saveFrequencyConfig(this)">Save</button>
    </div>
  `;
  
  // Add event listeners for frequency options
  const frequencyOptions = panel.querySelectorAll('.frequency-option');
  frequencyOptions.forEach(option => {
    option.addEventListener('click', () => {
      // Remove selected class from all options
      frequencyOptions.forEach(opt => opt.classList.remove('selected'));
      // Add selected class to clicked option
      option.classList.add('selected');
      
      const selectedFrequency = option.dataset.frequency;
      const planItSection = panel.querySelector('.plan-it-section');
      
      // Update monthly calculation for the new frequency
      updateFrequencyPanelCalculation(panel, currentAmount, selectedFrequency, inputGroup);
      
      if (selectedFrequency === 'plan-it') {
        planItSection.style.display = 'block';
        updatePlanItCalculation(panel, currentAmount);
      } else {
        planItSection.style.display = 'none';
      }
    });
  });
  
  // Add event listener for plan-it date changes
  const planItDate = panel.querySelector('.plan-it-date');
  if (planItDate) {
    planItDate.addEventListener('change', () => {
      updatePlanItCalculation(panel);
    });
  }
  
  // If currently set to Plan It, update the calculation immediately
  if (currentFrequency === 'plan-it') {
    updatePlanItCalculation(panel);
  }
}

// Calculate annual amount from current amount and frequency
function calculateAnnualAmount(amount, frequency) {
  const multipliers = {
    'weekly': 52,
    'bi-weekly': 26,
    'monthly': 12,
    'quarterly': 4,
    'annual': 1,
    'one-time': 0,
    'plan-it': 0
  };
  
  return amount * (multipliers[frequency] || 12);
}

// Update frequency panel monthly calculation
function updateFrequencyPanelCalculation(panel, currentAmount, selectedFrequency, inputGroup) {
  const calculationElement = panel.querySelector('.annual-calculation');
  if (!calculationElement) return;
  
  // Create temporary input group for calculation if needed for plan-it
  let tempInputGroup = inputGroup;
  if (selectedFrequency === 'plan-it') {
    const planItDate = panel.querySelector('.plan-it-date');
    if (planItDate && planItDate.value) {
      // Create a temporary element with the plan-it date for calculation
      tempInputGroup = document.createElement('div');
      tempInputGroup.setAttribute('data-plan-it-date', planItDate.value);
    }
  }
  
  const monthlyAmount = convertToMonthlyAmount(currentAmount, selectedFrequency, tempInputGroup);
  calculationElement.textContent = `Monthly: ${formatCurrency(monthlyAmount)}`;
}

// Update Plan It calculation based on target date
function updatePlanItCalculation(panel, totalAmount = null) {
  const dateInput = panel.querySelector('.plan-it-date');
  const calculation = panel.querySelector('.plan-it-calculation');
  
  if (!dateInput || !calculation) return;
  
  // Get current amount from the input field if not provided
  if (totalAmount === null) {
    const inputGroup = panel.closest('.input-group');
    const amountInput = inputGroup.querySelector('.expense-input');
    totalAmount = parseCurrencyInput(amountInput?.value || '0');
  }
  
  const targetDate = new Date(dateInput.value);
  const today = new Date();
  const monthsUntilTarget = Math.max(1, Math.ceil((targetDate - today) / (1000 * 60 * 60 * 24 * 30.44)));
  const monthlyAmount = totalAmount / monthsUntilTarget;
  
  calculation.innerHTML = `Saving <strong>${formatCurrency(monthlyAmount)}/month</strong> for ${monthsUntilTarget} months until ${targetDate.toLocaleDateString()}`;
  
  // Also update the main monthly calculation
  updateFrequencyPanelCalculation(panel, totalAmount, 'plan-it', { getAttribute: () => dateInput.value });
}

// Get today's date plus specified months (for default plan-it date)
function getTodayPlusMonths(months) {
  const date = new Date();
  date.setMonth(date.getMonth() + months);
  return date.toISOString().split('T')[0];
}

// Save frequency configuration
export function saveFrequencyConfig(button) {
  const panel = button.closest('.frequency-panel');
  const inputGroup = panel.closest('.input-group');
  const frequencySelect = inputGroup.querySelector('.frequency-select');
  const frequencyBadge = inputGroup.querySelector('.frequency-badge');
  
  const selectedOption = panel.querySelector('.frequency-option.selected');
  if (!selectedOption) return;
  
  const newFrequency = selectedOption.dataset.frequency;
  
  // Update the hidden select value
  frequencySelect.value = newFrequency;
  
  // Update the frequency badge
  updateFrequencyBadge(frequencyBadge, newFrequency);
  
  // Store plan-it settings if applicable
  if (newFrequency === 'plan-it') {
    const dateInput = panel.querySelector('.plan-it-date');
    const targetDate = dateInput.value;
    
    // Store plan-it data on the input group for later retrieval
    inputGroup.setAttribute('data-plan-it-date', targetDate);
  } else {
    inputGroup.removeAttribute('data-plan-it-date');
  }
  
  // Hide the panel
  panel.style.display = 'none';
  
  // Recalculate totals
  calculateTotals();
  
  // Save the budget to persist changes
  saveCurrentBudget();
}

// Cancel frequency configuration
export function cancelFrequencyConfig(button) {
  const panel = button.closest('.frequency-panel');
  panel.style.display = 'none';
}

// Toggle income frequency panel
export function toggleIncomeFrequencyPanel(button) {
  const inputGroup = button.closest('.input-group');
  const panel = inputGroup.querySelector('.income-frequency-panel');
  const frequencySelect = inputGroup.querySelector('.frequency-select');
  
  if (panel.style.display === 'none' || !panel.style.display) {
    // Close any other open income panels
    document.querySelectorAll('.income-frequency-panel').forEach(p => {
      if (p !== panel) p.style.display = 'none';
    });
    
    panel.style.display = 'block';
    populateIncomeFrequencyPanel(panel, frequencySelect);
  } else {
    panel.style.display = 'none';
  }
}

// Populate income frequency panel
function populateIncomeFrequencyPanel(panel, frequencySelect) {
  const currentFrequency = frequencySelect.value;
  const inputGroup = panel.closest('.input-group');
  const amountInput = inputGroup.querySelector('.income-input');
  const currentAmount = parseCurrencyInput(amountInput.value);
  const monthlyAmount = convertToMonthlyAmount(currentAmount, currentFrequency, inputGroup);
  
  panel.innerHTML = `
    <div class="frequency-panel-header">
      <span>Frequency Settings</span>
      <span class="annual-calculation">Monthly: ${formatCurrency(monthlyAmount)}</span>
    </div>
    
    <div class="frequency-options">
      <div class="frequency-option ${currentFrequency === 'weekly' ? 'selected' : ''}" data-frequency="weekly">
        Weekly
      </div>
      <div class="frequency-option ${currentFrequency === 'bi-weekly' ? 'selected' : ''}" data-frequency="bi-weekly">
        Bi-weekly
      </div>
      <div class="frequency-option ${currentFrequency === 'monthly' ? 'selected' : ''}" data-frequency="monthly">
        Monthly
      </div>
      <div class="frequency-option ${currentFrequency === 'quarterly' ? 'selected' : ''}" data-frequency="quarterly">
        Quarterly
      </div>
      <div class="frequency-option ${currentFrequency === 'annual' ? 'selected' : ''}" data-frequency="annual">
        Annual
      </div>
      <div class="frequency-option ${currentFrequency === 'one-time' ? 'selected' : ''}" data-frequency="one-time">
        One-time
      </div>
    </div>
    
    <div class="frequency-panel-actions">
      <button class="panel-btn" onclick="cancelIncomeFrequencyConfig(this)">Cancel</button>
      <button class="panel-btn primary" onclick="saveIncomeFrequencyConfig(this)">Save</button>
    </div>
  `;
  
  // Add event listeners for frequency options
  const frequencyOptions = panel.querySelectorAll('.frequency-option');
  frequencyOptions.forEach(option => {
    option.addEventListener('click', () => {
      // Remove selected class from all options
      frequencyOptions.forEach(opt => opt.classList.remove('selected'));
      // Add selected class to clicked option
      option.classList.add('selected');
    });
  });
}

// Save income frequency configuration
export function saveIncomeFrequencyConfig(button) {
  const panel = button.closest('.income-frequency-panel');
  const inputGroup = panel.closest('.input-group');
  const frequencySelect = inputGroup.querySelector('.frequency-select');
  const frequencyBadge = inputGroup.querySelector('.frequency-badge');
  const selectedOption = panel.querySelector('.frequency-option.selected');
  
  if (selectedOption) {
    const selectedFreq = selectedOption.dataset.frequency;
    frequencySelect.value = selectedFreq;
    updateFrequencyBadge(frequencyBadge, selectedFreq);
  }
  
  panel.style.display = 'none';
  
  // Recalculate totals
  calculateTotals();
  
  // Save the budget to persist changes
  saveCurrentBudget();
}

// Cancel income frequency configuration
export function cancelIncomeFrequencyConfig(button) {
  const panel = button.closest('.income-frequency-panel');
  panel.style.display = 'none';
}

// Toggle income tax panel
export function toggleIncomeTaxPanel(button) {
  const inputGroup = button.closest('.input-group');
  const panel = inputGroup.querySelector('.income-tax-panel');
  
  if (panel.style.display === 'none' || !panel.style.display) {
    // Close any other open income tax panels
    document.querySelectorAll('.income-tax-panel').forEach(p => {
      if (p !== panel) p.style.display = 'none';
    });
    
    panel.style.display = 'block';
    populateIncomeTaxPanel(panel);
  } else {
    panel.style.display = 'none';
  }
}

// Populate income tax panel
function populateIncomeTaxPanel(panel) {
  const inputGroup = panel.closest('.input-group');
  const amountInput = inputGroup.querySelector('.income-input');
  const currentAmount = parseCurrencyInput(amountInput.value);
  
  // Get current tax settings (if any stored)
  const taxType = inputGroup.getAttribute('data-tax-type') || 'post-tax';
  const federalRate = parseFloat(inputGroup.getAttribute('data-federal-tax')) || 22;
  const stateRate = parseFloat(inputGroup.getAttribute('data-state-tax')) || 5;
  const ficaRate = 7.65; // Fixed FICA rate
  
  const totalTaxRate = federalRate + stateRate + ficaRate;
  const netAmount = taxType === 'pre-tax' ? currentAmount * (1 - totalTaxRate / 100) : currentAmount;
  
  panel.innerHTML = `
    <div class="frequency-panel-header">
      <span>Tax Settings</span>
      <span class="annual-calculation">Net: ${formatCurrency(netAmount)}</span>
    </div>
    
    <div class="tax-type-options">
      <label>Income Type:</label>
      <div class="tax-type-buttons">
        <div class="tax-type-option ${taxType === 'post-tax' ? 'selected' : ''}" data-tax-type="post-tax">
          Post-Tax (Net)
        </div>
        <div class="tax-type-option ${taxType === 'pre-tax' ? 'selected' : ''}" data-tax-type="pre-tax">
          Pre-Tax (Gross)
        </div>
      </div>
    </div>
    
    <div class="tax-rates-section" style="display: ${taxType === 'pre-tax' ? 'block' : 'none'};">
      <label>Tax Rates:</label>
      <div class="tax-rate-inputs">
        <div class="tax-rate-input">
          <label>Federal Tax:</label>
          <input type="number" class="federal-tax-input" value="${federalRate}" min="0" max="50" step="0.1">%
        </div>
        <div class="tax-rate-input">
          <label>State Tax:</label>
          <input type="number" class="state-tax-input" value="${stateRate}" min="0" max="20" step="0.1">%
        </div>
        <div class="tax-rate-input">
          <label>FICA:</label>
          <input type="number" class="fica-tax-input" value="${ficaRate}" readonly>%
        </div>
      </div>
    </div>
    
    <div class="frequency-panel-actions">
      <button class="panel-btn" onclick="cancelIncomeTaxConfig(this)">Cancel</button>
      <button class="panel-btn primary" onclick="saveIncomeTaxConfig(this)">Save</button>
    </div>
  `;
  
  // Add event listeners for tax type options
  const taxTypeOptions = panel.querySelectorAll('.tax-type-option');
  taxTypeOptions.forEach(option => {
    option.addEventListener('click', function() {
      taxTypeOptions.forEach(opt => opt.classList.remove('selected'));
      this.classList.add('selected');
      
      const selectedType = this.dataset.taxType;
      const taxRatesSection = panel.querySelector('.tax-rates-section');
      taxRatesSection.style.display = selectedType === 'pre-tax' ? 'block' : 'none';
      
      // Update net calculation
      updateTaxCalculation(panel, currentAmount);
    });
  });
  
  // Add event listeners for tax rate inputs
  const taxRateInputs = panel.querySelectorAll('.tax-rate-input input');
  taxRateInputs.forEach(input => {
    input.addEventListener('input', () => updateTaxCalculation(panel, currentAmount));
  });
}

// Update tax calculation display
function updateTaxCalculation(panel, grossAmount) {
  const selectedType = panel.querySelector('.tax-type-option.selected')?.dataset.taxType || 'post-tax';
  const federalRate = parseFloat(panel.querySelector('.federal-tax-input')?.value) || 22;
  const stateRate = parseFloat(panel.querySelector('.state-tax-input')?.value) || 5;
  const ficaRate = parseFloat(panel.querySelector('.fica-tax-input')?.value) || 7.65;
  
  const totalTaxRate = federalRate + stateRate + ficaRate;
  const netAmount = selectedType === 'pre-tax' ? grossAmount * (1 - totalTaxRate / 100) : grossAmount;
  
  const calculationDisplay = panel.querySelector('.annual-calculation');
  if (calculationDisplay) {
    calculationDisplay.textContent = `Net: ${formatCurrency(netAmount)}`;
  }
}

// Save income tax configuration
export function saveIncomeTaxConfig(button) {
  const panel = button.closest('.income-tax-panel');
  const inputGroup = panel.closest('.input-group');
  const taxBadge = inputGroup.querySelector('.tax-badge');
  const selectedType = panel.querySelector('.tax-type-option.selected')?.dataset.taxType || 'post-tax';
  const federalRate = parseFloat(panel.querySelector('.federal-tax-input')?.value) || 22;
  const stateRate = parseFloat(panel.querySelector('.state-tax-input')?.value) || 5;
  
  // Store tax settings as data attributes
  inputGroup.setAttribute('data-tax-type', selectedType);
  inputGroup.setAttribute('data-federal-tax', federalRate);
  inputGroup.setAttribute('data-state-tax', stateRate);
  
  // Update badge appearance based on tax type
  if (selectedType === 'pre-tax') {
    taxBadge.style.opacity = '1';
    taxBadge.style.filter = 'none';
    taxBadge.style.color = 'var(--budgley-orange)';
    taxBadge.title = `Pre-tax income (${(federalRate + stateRate + 7.65).toFixed(1)}% total tax)`;
  } else {
    taxBadge.style.opacity = '0.3';
    taxBadge.style.filter = 'grayscale(1) opacity(0.5)';
    taxBadge.style.color = '';
    taxBadge.title = 'Post-tax income (net amount)';
  }
  
  panel.style.display = 'none';
  
  // Recalculate totals
  calculateTotals();
  
  // Save the budget to persist changes
  saveCurrentBudget();
}

// Cancel income tax configuration
export function cancelIncomeTaxConfig(button) {
  const panel = button.closest('.income-tax-panel');
  panel.style.display = 'none';
}

// Toggle income timing panel
export function toggleIncomeTimingPanel(button) {
  const inputGroup = button.closest('.input-group');
  const panel = inputGroup.querySelector('.income-timing-panel');
  
  if (panel.style.display === 'none' || !panel.style.display) {
    // Close any other open income timing panels
    document.querySelectorAll('.income-timing-panel').forEach(p => {
      if (p !== panel) p.style.display = 'none';
    });
    
    panel.style.display = 'block';
    populateIncomeTimingPanel(panel);
  } else {
    panel.style.display = 'none';
  }
}

// Populate income timing panel
function populateIncomeTimingPanel(panel) {
  const inputGroup = panel.closest('.input-group');
  const frequencySelect = inputGroup.querySelector('.frequency-select');
  const currentFrequency = frequencySelect ? frequencySelect.value : 'monthly';
  
  // Get current timing settings
  const pattern = inputGroup.getAttribute('data-timing-pattern') || 'day-of-month';
  const dayOfWeek = parseInt(inputGroup.getAttribute('data-day-of-week')) || 5; // Friday
  const dayOfMonth = inputGroup.getAttribute('data-day-of-month') || '15';
  const startDate = inputGroup.getAttribute('data-start-date') || '';
  const nextPayment = calculateNextPaymentDate(inputGroup);
  
  panel.innerHTML = `
    <div class="frequency-panel-header">
      <span>Payment Schedule</span>
      <span class="annual-calculation">Next: ${nextPayment}</span>
    </div>
    
    <div class="timing-frequency-sync">
      <label>Payment Frequency:</label>
      <div class="timing-frequency-display">
        ${FREQUENCY_LABELS[currentFrequency]?.label || 'Monthly'}
        <small>(synced with frequency setting)</small>
      </div>
    </div>
    
    <div class="timing-pattern-options">
      <label>Payment Pattern:</label>
      <div class="pattern-buttons">
        <div class="pattern-option ${pattern === 'day-of-week' ? 'selected' : ''}" data-pattern="day-of-week">
          Day of Week
        </div>
        <div class="pattern-option ${pattern === 'day-of-month' ? 'selected' : ''}" data-pattern="day-of-month">
          Day of Month
        </div>
      </div>
    </div>
    
    <div class="timing-details">
      <div class="day-of-week-section" style="display: ${pattern === 'day-of-week' ? 'block' : 'none'};">
        <label>Paid every ${currentFrequency === 'weekly' ? 'week' : currentFrequency === 'bi-weekly' ? '2 weeks' : 'period'} on:</label>
        <select class="day-of-week-select">
          <option value="1" ${dayOfWeek === 1 ? 'selected' : ''}>Monday</option>
          <option value="2" ${dayOfWeek === 2 ? 'selected' : ''}>Tuesday</option>
          <option value="3" ${dayOfWeek === 3 ? 'selected' : ''}>Wednesday</option>
          <option value="4" ${dayOfWeek === 4 ? 'selected' : ''}>Thursday</option>
          <option value="5" ${dayOfWeek === 5 ? 'selected' : ''}>Friday</option>
          <option value="6" ${dayOfWeek === 6 ? 'selected' : ''}>Saturday</option>
          <option value="7" ${dayOfWeek === 7 ? 'selected' : ''}>Sunday</option>
        </select>
        <div class="start-date-section">
          <label>Start Date (optional):</label>
          <input type="date" class="start-date-input" value="${startDate}" title="Start Date">
        </div>
      </div>
      
      <div class="day-of-month-section" style="display: ${pattern === 'day-of-month' ? 'block' : 'none'};">
        <label>Paid on the:</label>
        <select class="day-of-month-select">
          <option value="1" ${dayOfMonth === '1' ? 'selected' : ''}>1st</option>
          <option value="5" ${dayOfMonth === '5' ? 'selected' : ''}>5th</option>
          <option value="15" ${dayOfMonth === '15' ? 'selected' : ''}>15th</option>
          <option value="last" ${dayOfMonth === 'last' ? 'selected' : ''}>Last day</option>
          <option value="custom">Custom day...</option>
        </select>
        <div class="custom-day-input" style="display: ${!['1','5','15','last'].includes(dayOfMonth) ? 'block' : 'none'};">
          <input type="number" class="custom-day-number" min="1" max="31" value="${dayOfMonth}" placeholder="Day">
        </div>
      </div>
    </div>
    
    <div class="frequency-panel-actions">
      <button class="panel-btn" onclick="cancelIncomeTimingConfig(this)">Cancel</button>
      <button class="panel-btn primary" onclick="saveIncomeTimingConfig(this)">Save</button>
    </div>
  `;
  
  // Add event listeners for pattern options
  const patternOptions = panel.querySelectorAll('.pattern-option');
  patternOptions.forEach(option => {
    option.addEventListener('click', function() {
      patternOptions.forEach(opt => opt.classList.remove('selected'));
      this.classList.add('selected');
      
      const selectedPattern = this.dataset.pattern;
      const dayOfWeekSection = panel.querySelector('.day-of-week-section');
      const dayOfMonthSection = panel.querySelector('.day-of-month-section');
      
      dayOfWeekSection.style.display = selectedPattern === 'day-of-week' ? 'block' : 'none';
      dayOfMonthSection.style.display = selectedPattern === 'day-of-month' ? 'block' : 'none';
      
      updateNextPaymentDisplay(panel, inputGroup);
    });
  });
  
  // Add event listeners for timing changes
  const timingSelects = panel.querySelectorAll('select, input');
  timingSelects.forEach(element => {
    element.addEventListener('change', () => updateNextPaymentDisplay(panel, inputGroup));
  });
  
  // Handle custom day input
  const dayOfMonthSelect = panel.querySelector('.day-of-month-select');
  const customDayInput = panel.querySelector('.custom-day-input');
  dayOfMonthSelect.addEventListener('change', function() {
    customDayInput.style.display = this.value === 'custom' ? 'block' : 'none';
    updateNextPaymentDisplay(panel, inputGroup);
  });
}

// Calculate next payment date based on current settings
function calculateNextPaymentDate(inputGroup, tempSettings = null) {
  const frequency = inputGroup.querySelector('.frequency-select')?.value || 'monthly';
  const pattern = tempSettings?.pattern || inputGroup.getAttribute('data-timing-pattern') || 'day-of-month';
  const dayOfWeek = tempSettings?.dayOfWeek || parseInt(inputGroup.getAttribute('data-day-of-week')) || 5;
  const dayOfMonth = tempSettings?.dayOfMonth || inputGroup.getAttribute('data-day-of-month') || '15';
  const startDate = tempSettings?.startDate || inputGroup.getAttribute('data-start-date') || '';
  
  const today = new Date();
  let nextPayment = new Date(today);
  
  if (pattern === 'day-of-week') {
    // If start date is provided, use it as reference for day-of-week calculations
    if (startDate) {
      // Parse date as local date to avoid timezone issues
      const dateParts = startDate.split('-');
      const startDateObj = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
      
      // Compare dates only, not times
      const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const startDateOnly = new Date(startDateObj.getFullYear(), startDateObj.getMonth(), startDateObj.getDate());
      
      // If start date is today or in the future, use it as the next payment
      if (startDateOnly >= todayDate) {
        nextPayment = new Date(startDateOnly); // Use date-only version to avoid timezone issues
      } else {
        // Calculate next occurrence based on frequency from start date
        const daysDiff = Math.floor((todayDate - startDateOnly) / (24 * 60 * 60 * 1000));
        const intervalDays = frequency === 'weekly' ? 7 : 14; // bi-weekly
        const intervalsPassed = Math.floor(daysDiff / intervalDays);
        
        // Calculate the next payment date by adding intervals from start date
        nextPayment = new Date(startDateOnly.getTime()); // Use getTime() to avoid reference issues
        nextPayment.setDate(startDateOnly.getDate() + ((intervalsPassed + 1) * intervalDays));
      }
    } else {
      // Original logic without start date
      const targetDay = dayOfWeek; // 1 = Monday, 7 = Sunday
      const currentDay = today.getDay() || 7; // Convert Sunday (0) to 7
      const daysUntilTarget = targetDay >= currentDay ? targetDay - currentDay : 7 - currentDay + targetDay;
      
      nextPayment.setDate(today.getDate() + daysUntilTarget);
      if (daysUntilTarget === 0 && today.getHours() >= 9) {
        // If it's the payment day but after 9 AM, move to next occurrence
        nextPayment.setDate(nextPayment.getDate() + (frequency === 'weekly' ? 7 : 14));
      }
    }
  } else {
    // Day of month pattern
    const targetDay = dayOfMonth === 'last' ? getLastDayOfMonth(today) : parseInt(dayOfMonth);
    nextPayment.setDate(targetDay);
    
    // If we've passed this month's payment date, move to next month
    if (nextPayment <= today) {
      nextPayment.setMonth(nextPayment.getMonth() + 1);
      if (dayOfMonth === 'last') {
        nextPayment.setDate(getLastDayOfMonth(nextPayment));
      }
    }
  }
  
  return nextPayment.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    year: nextPayment.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
  });
}

// Helper function to get last day of month
function getLastDayOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
}

// Update next payment display in real time
function updateNextPaymentDisplay(panel, inputGroup) {
  const selectedPattern = panel.querySelector('.pattern-option.selected')?.dataset.pattern || 'day-of-month';
  const dayOfWeek = parseInt(panel.querySelector('.day-of-week-select')?.value) || 5;
  const dayOfMonthSelect = panel.querySelector('.day-of-month-select');
  const customDayInput = panel.querySelector('.custom-day-number');
  const startDateInput = panel.querySelector('.start-date-input');
  
  let dayOfMonth;
  if (dayOfMonthSelect.value === 'custom') {
    dayOfMonth = customDayInput.value || '15';
  } else {
    dayOfMonth = dayOfMonthSelect.value;
  }
  
  const startDate = startDateInput ? startDateInput.value : '';
  const tempSettings = { pattern: selectedPattern, dayOfWeek, dayOfMonth, startDate };
  const nextPayment = calculateNextPaymentDate(inputGroup, tempSettings);
  
  const calculationDisplay = panel.querySelector('.annual-calculation');
  if (calculationDisplay) {
    calculationDisplay.textContent = `Next: ${nextPayment}`;
  }
}

// Save income timing configuration
export function saveIncomeTimingConfig(button) {
  const panel = button.closest('.income-timing-panel');
  const inputGroup = panel.closest('.input-group');
  const timingBadge = inputGroup.querySelector('.timing-badge');
  
  const selectedPattern = panel.querySelector('.pattern-option.selected')?.dataset.pattern || 'day-of-month';
  const dayOfWeek = parseInt(panel.querySelector('.day-of-week-select')?.value) || 5;
  const dayOfMonthSelect = panel.querySelector('.day-of-month-select');
  const customDayInput = panel.querySelector('.custom-day-number');
  const startDateInput = panel.querySelector('.start-date-input');
  
  let dayOfMonth;
  if (dayOfMonthSelect.value === 'custom') {
    dayOfMonth = customDayInput.value || '15';
  } else {
    dayOfMonth = dayOfMonthSelect.value;
  }
  
  const startDate = startDateInput ? startDateInput.value : '';
  
  // Store timing settings as data attributes
  inputGroup.setAttribute('data-timing-pattern', selectedPattern);
  inputGroup.setAttribute('data-day-of-week', dayOfWeek);
  inputGroup.setAttribute('data-day-of-month', dayOfMonth);
  inputGroup.setAttribute('data-start-date', startDate);
  
  // Update badge appearance to show it's configured
  timingBadge.style.opacity = '1';
  timingBadge.style.filter = 'none';
  timingBadge.style.color = 'var(--budgley-orange)';
  timingBadge.title = `Payment schedule configured (Next: ${calculateNextPaymentDate(inputGroup)})`;
  
  panel.style.display = 'none';
  
  // Sync timing data back to income manager
  syncTimingDataToIncomeManager(inputGroup, selectedPattern, dayOfWeek, dayOfMonth, startDate);
  
  // Save the budget to persist changes
  saveCurrentBudget();
}

// Sync timing data from main budget back to income manager
function syncTimingDataToIncomeManager(inputGroup, timingPattern, dayOfWeek, dayOfMonth, startDate) {
  try {
    // Get the income source name from the input group
    const labelElement = inputGroup.querySelector('label');
    const sourceName = labelElement ? labelElement.textContent.trim() : null;
    
    if (!sourceName) return;
    
    // Find the corresponding income source in the manager
    const matchingSource = Array.from(incomeManager.incomeSources.values()).find(
      source => source.name === sourceName
    );
    
    if (matchingSource) {
      // Update the payment schedule
      matchingSource.paymentSchedule = matchingSource.paymentSchedule || {};
      matchingSource.paymentSchedule.timingPattern = timingPattern;
      matchingSource.paymentSchedule.dayOfWeek = dayOfWeek;
      matchingSource.paymentSchedule.dayOfMonth = dayOfMonth;
      matchingSource.paymentSchedule.startDate = startDate;
      
      // Save the updated income data
      incomeManager.saveData();
      
      console.log(`Synced timing data for income source: ${sourceName}`);
    } else {
      console.log(`No matching income source found for: ${sourceName}`);
    }
  } catch (error) {
    console.warn('Failed to sync timing data to income manager:', error);
  }
}

// Cancel income timing configuration
export function cancelIncomeTimingConfig(button) {
  const panel = button.closest('.income-timing-panel');
  panel.style.display = 'none';
}

// Update frequency badge display
function updateFrequencyBadge(badge, frequency) {
  const label = FREQUENCY_LABELS[frequency];
  if (label && badge) {
    badge.textContent = label.badge;
    
    // Update badge color based on frequency type
    badge.className = 'frequency-badge';
    if (frequency === 'monthly') {
      // Default monthly should match tax exempt unselected state (subtle)
      badge.style.background = 'var(--text-light)';
      badge.style.borderColor = 'var(--text-light)';
      badge.style.color = 'white';
      badge.style.opacity = '0.3'; // Match tax-deductible-label unselected opacity
    } else if (frequency === 'plan-it') {
      badge.style.background = 'var(--warning-color)';
      badge.style.borderColor = 'var(--warning-color)';
      badge.style.color = 'white';
      badge.style.opacity = '1';
    } else if (frequency === 'one-time') {
      badge.style.background = 'var(--text-light)';
      badge.style.borderColor = 'var(--text-light)';
      badge.style.color = 'white';
      badge.style.opacity = '1';
    } else {
      // Non-default frequencies get primary color (active state)
      badge.style.background = 'var(--primary-color)';
      badge.style.borderColor = 'var(--primary-color)';
      badge.style.color = 'white';
      badge.style.opacity = '1';
    }
  }
}

// Update all frequency badges when loading budget data
export function updateAllFrequencyBadges() {
  const inputGroups = document.querySelectorAll('.input-group');
  
  inputGroups.forEach(group => {
    const frequencySelect = group.querySelector('.frequency-select');
    const frequencyBadge = group.querySelector('.frequency-badge');
    
    if (frequencySelect && frequencyBadge) {
      const frequency = frequencySelect.value || 'monthly';
      updateFrequencyBadge(frequencyBadge, frequency);
    }
  });
}

// Update all timing badges when loading budget data
export function updateAllTimingBadges() {
  const inputGroups = document.querySelectorAll('.input-group');
  
  inputGroups.forEach(group => {
    const timingBadge = group.querySelector('.timing-badge');
    
    if (timingBadge) {
      // Check if this income item has timing configuration
      const timingPattern = group.getAttribute('data-timing-pattern');
      const dayOfWeek = group.getAttribute('data-day-of-week');
      const dayOfMonth = group.getAttribute('data-day-of-month');
      
      if (timingPattern && (dayOfWeek || dayOfMonth)) {
        // Has timing configuration - show as configured
        timingBadge.style.opacity = '1';
        timingBadge.style.filter = 'none';
        timingBadge.style.color = 'var(--budgley-orange)';
        timingBadge.title = `Payment schedule configured (Next: ${calculateNextPaymentDate(group)})`;
      } else {
        // No timing configuration - show as default
        timingBadge.style.opacity = '0.6';
        timingBadge.style.filter = 'grayscale(100%)';
        timingBadge.style.color = '';
        timingBadge.title = 'Payment Schedule';
      }
    }
  });
}

/**
 * Make frequency functions globally available for HTML onclick handlers
 */
if (typeof window !== 'undefined') {
  window.toggleFrequencyPanel = toggleFrequencyPanel;
  window.saveFrequencyConfig = saveFrequencyConfig;
  window.cancelFrequencyConfig = cancelFrequencyConfig;
}

// Create a new blank budget with a prompted name
export async function createBlankBudget() {
  const currentUser = getCurrentUser();
  if (!currentUser) return;

  const name = prompt('Enter a name for the new budget:', 'New Budget');
  if (!name || !name.trim()) return;

  const trimmedName = name.trim();
  if (budgetVersions[trimmedName]) {
    alert(`A budget named "${trimmedName}" already exists.`);
    return;
  }

  const emptyBudget = { income: [], categories: [] };
  await saveBudgetVersion(trimmedName, emptyBudget);
  loadBudgetVersion(trimmedName, false);
}

// Show add-budget dropdown menu anchored to the clicked button
export function showAddBudgetMenu(event) {
  const existing = document.getElementById('add-budget-menu');
  if (existing) {
    existing.remove();
    return;
  }

  const btn = event.currentTarget;
  const rect = btn.getBoundingClientRect();

  const menu = document.createElement('div');
  menu.id = 'add-budget-menu';
  menu.className = 'add-budget-menu';
  menu.innerHTML = `
    <button class="add-budget-menu-item" onclick="openIncomeWizard(); document.getElementById('add-budget-menu')?.remove()">
      <span class="add-budget-menu-icon">✨</span>
      <span class="add-budget-menu-text">
        <span class="add-budget-menu-label">Use Wizard</span>
        <span class="add-budget-menu-desc">Guided setup with templates</span>
      </span>
    </button>
    <button class="add-budget-menu-item" onclick="createBlankBudget(); document.getElementById('add-budget-menu')?.remove()">
      <span class="add-budget-menu-icon">📄</span>
      <span class="add-budget-menu-text">
        <span class="add-budget-menu-label">Blank Budget</span>
        <span class="add-budget-menu-desc">Start from scratch</span>
      </span>
    </button>
  `;

  menu.style.top = `${rect.bottom + 4}px`;
  menu.style.left = `${rect.left}px`;
  document.body.appendChild(menu);

  setTimeout(() => {
    document.addEventListener('click', function closeMenu(e) {
      if (!menu.contains(e.target)) {
        menu.remove();
        document.removeEventListener('click', closeMenu);
      }
    });
  }, 0);
}

if (typeof window !== 'undefined') {
  window.createBlankBudget = createBlankBudget;
  window.showAddBudgetMenu = showAddBudgetMenu;
}

// ===== RECURRING EXPENSE AUTO-LOGGING SYSTEM =====

/**
 * Auto-logging system for recurring expenses
 * Automatically logs fixed and relative date recurring expenses
 * Prompts for flexible date expenses when they enter their due window
 */

/**
 * Check for due recurring expenses and process them
 * Should be called on app startup and daily
 */
export async function processRecurringExpenses() {
  console.log('🔄 Processing recurring expenses...');
  
  try {
    const recurringExpenses = getRecurringExpenses();
    
    if (!recurringExpenses || recurringExpenses.length === 0) {
      console.log('No recurring expenses found');
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0); // Start of day

    for (const recurringExpense of recurringExpenses) {
      await processIndividualRecurring(recurringExpense, today);
    }

    console.log('✅ Recurring expense processing complete');
  } catch (error) {
    console.error('❌ Error processing recurring expenses:', error);
  }
}

/**
 * Process an individual recurring expense
 */
async function processIndividualRecurring(recurringExpense, today) {
  const config = recurringExpense.config;
  if (!config || !config.type) return;

  console.log(`Processing ${config.type} recurring expense: ${recurringExpense.label}`);

  switch (config.type) {
    case 'fixed':
    case 'relative':
      await processAutoLogRecurring(recurringExpense, today);
      break;
    case 'flexible':
      await processFlexibleRecurring(recurringExpense, today);
      break;
    default:
      console.warn('Unknown recurring type:', config.type);
  }
}

/**
 * Process auto-log recurring expenses (fixed and relative dates)
 */
async function processAutoLogRecurring(recurringExpense, today) {
  const config = recurringExpense.config;
  
  // Generate instances for current month
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  
  const instances = generateRecurringExpenseInstances(recurringExpense, startOfMonth, endOfMonth);
  
  for (const instance of instances) {
    const dueDate = new Date(instance.dueDate);
    dueDate.setHours(0, 0, 0, 0);
    
    // Check if this instance is due today
    if (dueDate.getTime() === today.getTime()) {
      // Check if already logged
      const alreadyLogged = await checkIfExpenseAlreadyLogged(recurringExpense, dueDate);
      
      if (!alreadyLogged) {
        await autoLogRecurringExpense(recurringExpense, instance, dueDate);
        console.log(`✅ Auto-logged: ${recurringExpense.label} for ${dueDate.toDateString()}`);
      } else {
        console.log(`ℹ️ Already logged: ${recurringExpense.label} for ${dueDate.toDateString()}`);
      }
    }
  }
}

/**
 * Process flexible date recurring expenses (prompt user)
 */
async function processFlexibleRecurring(recurringExpense, today) {
  const config = recurringExpense.config;
  const dueDay = parseInt(config.dueDay) || 1;
  const flexibility = parseInt(config.flexibility) || 1;
  
  // Calculate due window for current month
  const targetDate = new Date(today.getFullYear(), today.getMonth(), dueDay);
  const earliestDate = new Date(targetDate);
  earliestDate.setDate(targetDate.getDate() - flexibility);
  const latestDate = new Date(targetDate);
  latestDate.setDate(targetDate.getDate() + flexibility);
  
  // Check if today is within the flexible window
  if (today >= earliestDate && today <= latestDate) {
    // Check if already logged or prompted this month
    const alreadyHandled = await checkIfFlexibleExpenseHandled(recurringExpense, today);
    
    if (!alreadyHandled) {
      await showFlexibleExpensePrompt(recurringExpense, targetDate, earliestDate, latestDate);
    }
  }
}

/**
 * Check if a recurring expense has already been logged for a specific date
 */
/**
 * Get all logged recurring expenses for a date range in one batch query
 */
async function getLoggedRecurringExpensesForPeriod(startDate, endDate) {
  try {
    const user = getCurrentUser();
    if (!user) return [];

    // Validate date parameters
    if (!startDate || !endDate) {
      console.error('getLoggedRecurringExpensesForPeriod called with undefined dates:', { startDate, endDate });
      return [];
    }

    if (!(startDate instanceof Date) || !(endDate instanceof Date)) {
      console.error('getLoggedRecurringExpensesForPeriod called with invalid date objects:', { startDate, endDate });
      return [];
    }

    console.log(`🚀 [Batch Query Debug] Fetching all logged recurring expenses from ${startDate.toISOString()} to ${endDate.toISOString()}`);
    const batchStart = performance.now();

    const expensesRef = collection(db, `users/${user.uid}/expenses`);
    const expensesQuery = query(
      expensesRef,
      where('isRecurringPayment', '==', true),
      where('date', '>=', startDate),
      where('date', '<=', endDate),
      orderBy('date', 'asc')
    );

    const expensesSnapshot = await getDocs(expensesQuery);
    const loggedExpenses = [];
    
    expensesSnapshot.forEach(doc => {
      loggedExpenses.push({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date.toDate() // Convert Firestore timestamp to Date
      });
    });

    const batchEnd = performance.now();
    console.log(`🚀 [Batch Query Debug] Batch query took ${(batchEnd - batchStart).toFixed(2)}ms, found ${loggedExpenses.length} logged recurring expenses`);
    
    return loggedExpenses;
  } catch (error) {
    console.error('Error fetching logged recurring expenses:', error);
    return [];
  }
}

/**
 * Check if expense is already logged using pre-fetched batch data
 */
function checkIfExpenseAlreadyLoggedFromBatch(recurringExpense, dueDate, loggedExpenses) {
  const dateString = dueDate.toISOString().slice(0, 10); // YYYY-MM-DD format
  
  // Filter logged expenses for this specific recurring expense
  const matchingExpenses = loggedExpenses.filter(expense => 
    expense.lineItemName === recurringExpense.label &&
    expense.recurringId === recurringExpense.id
  );

  // Check if any expense matches the date (exact for fixed/relative, within window for flexible)
  for (const expense of matchingExpenses) {
    const expenseDate = new Date(expense.date);
    expenseDate.setHours(0, 0, 0, 0);
    
    // Create a copy of dueDate to avoid modifying the original
    const normalizedDueDate = new Date(dueDate);
    normalizedDueDate.setHours(0, 0, 0, 0);
    
    // For flexible recurring expenses, check if expense is within the flexible window
    if (recurringExpense.config && recurringExpense.config.type === 'flexible') {
      const flexibility = parseInt(recurringExpense.config.flexibility) || 1;
      const earliestDate = new Date(normalizedDueDate);
      earliestDate.setDate(earliestDate.getDate() - flexibility);
      const latestDate = new Date(normalizedDueDate);
      latestDate.setDate(latestDate.getDate() + flexibility);
      
      const withinWindow = expenseDate >= earliestDate && expenseDate <= latestDate;
      console.log(`[Flexible Debug] ${recurringExpense.label}: expense ${expenseDate.toDateString()}, window ${earliestDate.toDateString()} to ${latestDate.toDateString()}, withinWindow: ${withinWindow}`);
      
      if (withinWindow) {
        console.log(`Found flexible expense record for ${recurringExpense.label} on ${expense.date.toDateString()} (due ${dateString})`);
        return true;
      }
    } else {
      // For fixed/relative recurring expenses, require exact date match
      
      if (expenseDate.getTime() === normalizedDueDate.getTime()) {
        console.log(`Found exact expense record for ${recurringExpense.label} on ${dateString}`);
        return true;
      }
    }
  }
  
  return false;
}

async function checkIfExpenseAlreadyLogged(recurringExpense, dueDate) {
  try {
    const user = getCurrentUser();
    if (!user) return false;

    const month = dueDate.toISOString().slice(0, 7); // YYYY-MM format
    const dateString = dueDate.toISOString().slice(0, 10); // YYYY-MM-DD format
    
    // Check actual expenses - single source of truth
    const expensesRef = collection(db, `users/${user.uid}/expenses`);
    const expensesQuery = query(
      expensesRef,
      where('month', '==', month),
      where('lineItemName', '==', recurringExpense.label),
      where('isRecurringPayment', '==', true),
      where('recurringId', '==', recurringExpense.id)
    );

    const expensesSnapshot = await getDocs(expensesQuery);
    
    // Check if any expense matches the date (exact for fixed/relative, within window for flexible)
    for (const doc of expensesSnapshot.docs) {
      const expenseData = doc.data();
      const expenseDate = expenseData.date.toDate();
      expenseDate.setHours(0, 0, 0, 0);
      
      // Create a copy of dueDate to avoid modifying the original
      const normalizedDueDate = new Date(dueDate);
      normalizedDueDate.setHours(0, 0, 0, 0);
      
      // For flexible recurring expenses, check if expense is within the flexible window
      if (recurringExpense.config && recurringExpense.config.type === 'flexible') {
        const flexibility = parseInt(recurringExpense.config.flexibility) || 1;
        const earliestDate = new Date(normalizedDueDate);
        earliestDate.setDate(earliestDate.getDate() - flexibility);
        const latestDate = new Date(normalizedDueDate);
        latestDate.setDate(latestDate.getDate() + flexibility);
        
        const withinWindow = expenseDate >= earliestDate && expenseDate <= latestDate;
        console.log(`[Flexible Debug] ${recurringExpense.label}: expense ${expenseDate.toDateString()}, window ${earliestDate.toDateString()} to ${latestDate.toDateString()}, withinWindow: ${withinWindow}`);
        
        if (withinWindow) {
          console.log(`Found flexible expense record for ${recurringExpense.label} on ${expenseData.date.toDate().toDateString()} (due ${dateString})`);
          return true;
        }
      } else {
        // For fixed/relative recurring expenses, require exact date match
        
        if (expenseDate.getTime() === normalizedDueDate.getTime()) {
          console.log(`Found expense record for ${recurringExpense.label} on ${dateString}`);
          return true;
        }
      }
    }
    
    return false;
  } catch (error) {
    console.error('Error checking if expense already logged:', error);
    return false; // Assume not logged to be safe
  }
}

/**
 * Check if a flexible expense has been handled (logged or dismissed) this month
 */
async function checkIfFlexibleExpenseHandled(recurringExpense, today) {
  try {
    const user = getCurrentUser();
    if (!user) return false;

    const currentMonth = today.toISOString().slice(0, 7);

    // Only check the current month by default. Only expand to adjacent months when
    // the flexible window actually spans a month boundary (e.g., due on the 2nd
    // with ±3 days would have the window start in the previous month). Checking
    // adjacent months unconditionally caused monthly expenses logged in month N
    // to suppress the prompt in month N+1, breaking the feature permanently.
    const monthsToCheck = [currentMonth];

    const config = recurringExpense.config;
    if (config && config.type === 'flexible') {
      const dueDay = parseInt(config.dueDay) || 1;
      const flexibility = parseInt(config.flexibility) || 1;
      const targetDate = new Date(today.getFullYear(), today.getMonth(), dueDay);

      const earliestDate = new Date(targetDate);
      earliestDate.setDate(targetDate.getDate() - flexibility);

      const latestDate = new Date(targetDate);
      latestDate.setDate(targetDate.getDate() + flexibility);

      // Only check previous month if the window's start falls in the previous month
      if (earliestDate.getMonth() !== targetDate.getMonth() || earliestDate.getFullYear() !== targetDate.getFullYear()) {
        const prevMonth = new Date(today);
        prevMonth.setMonth(prevMonth.getMonth() - 1);
        monthsToCheck.push(prevMonth.toISOString().slice(0, 7));
      }

      // Only check next month if the window's end falls in the next month
      if (latestDate.getMonth() !== targetDate.getMonth() || latestDate.getFullYear() !== targetDate.getFullYear()) {
        const nextMonth = new Date(today);
        nextMonth.setMonth(nextMonth.getMonth() + 1);
        monthsToCheck.push(nextMonth.toISOString().slice(0, 7));
      }
    }
    
    // Check if already logged in any of these months
    const expensesRef = collection(db, `users/${user.uid}/expenses`);
    
    for (const month of monthsToCheck) {
      const expenseQuery = query(
        expensesRef,
        where('month', '==', month),
        where('lineItemName', '==', recurringExpense.label),
        where('isRecurringPayment', '==', true),
        where('recurringId', '==', recurringExpense.id)
      );

      const expenseSnapshot = await getDocs(expenseQuery);
      if (!expenseSnapshot.empty) {
        console.log(`✅ Found existing expense for ${recurringExpense.label} in month ${month}`);
        return true; // Already logged 
      }
    }

    // Check if already dismissed this month
    const dismissalsRef = collection(db, `users/${user.uid}/recurringDismissals`);
    const dismissalQuery = query(
      dismissalsRef,
      where('recurringId', '==', recurringExpense.id),
      where('month', '==', currentMonth)
    );

    const dismissalSnapshot = await getDocs(dismissalQuery);
    if (!dismissalSnapshot.empty) {
      console.log(`✅ Found dismissal for ${recurringExpense.label} in month ${currentMonth}`);
      return true;
    }
    
    return false;
    
  } catch (error) {
    console.error('Error checking if flexible expense handled:', error);
    return false;
  }
}

/**
 * Automatically log a recurring expense
 */
async function autoLogRecurringExpense(recurringExpense, instance, dueDate, isFlexible = false) {
  try {
    const user = getCurrentUser();
    if (!user) throw new Error('No user authenticated');

    const budgetId = getCurrentBudgetId();
    const now = new Date();
    
    // For flexible expenses (1-click), use current date. For auto-logging, use due date
    const expenseDateTime = isFlexible ? new Date(now) : new Date(dueDate);
    expenseDateTime.setHours(12, 0, 0, 0);

    // Different descriptions based on expense type
    let description;
    if (isFlexible) {
      description = '1-click';
    } else {
      description = 'auto-logged';
    }

    // Convert category name to numeric categoryId for compatibility with expense tracker
    const budgetData = getCurrentBudgetData();
    const categoryId = budgetData?.categories?.findIndex(cat => cat.name === recurringExpense.categoryName) ?? recurringExpense.categoryName;

    // Resolve the line item index within the category so remaining-amount queries can match it
    const category = categoryId >= 0 ? budgetData?.categories?.[categoryId] : null;
    const lineItemIndex = category?.items?.findIndex(item => item.label === recurringExpense.label) ?? -1;
    const lineItemId = lineItemIndex >= 0 ? String(lineItemIndex) : 'auto-recurring';

    const expenseData = {
      budgetId,
      categoryId: categoryId,
      lineItemId,
      lineItemName: recurringExpense.label,
      amount: recurringExpense.amount,
      description: description,
      date: Timestamp.fromDate(expenseDateTime),
      month: expenseDateTime.toISOString().slice(0, 7), // Use expense date for month grouping
      isUnplanned: false,
      isTaxDeductible: false, // Could be enhanced to use line item tax setting
      isRecurringPayment: true,
      recurringId: recurringExpense.id,
      recurringType: recurringExpense.config.type,
      autoLogged: !isFlexible, // False for 1-click, true for auto-logged
      createdAt: Timestamp.fromDate(now),
      updatedAt: Timestamp.fromDate(now)
    };

    // Use the existing expense submission system
    const expensesRef = collection(db, `users/${user.uid}/expenses`);
    await addDoc(expensesRef, expenseData);

    // Show notification to user
    showAutoLogNotification(recurringExpense, dueDate);

  } catch (error) {
    console.error('Error auto-logging recurring expense:', error);
    throw error;
  }
}

/**
 * Queue system for multiple flexible expense prompts
 */
window.flexibleExpenseQueue = window.flexibleExpenseQueue || [];
window.isShowingFlexibleModal = false;

/**
 * Show flexible expense prompt to user (with queue support)
 */
async function showFlexibleExpensePrompt(recurringExpense, targetDate, earliestDate, latestDate) {
  const promptData = {
    recurringExpense,
    targetDate,
    earliestDate,
    latestDate,
    amount: recurringExpense.amount
  };

  // Add to queue
  window.flexibleExpenseQueue.push(promptData);
  
  // Process queue if not already showing a modal
  if (!window.isShowingFlexibleModal) {
    showNextFlexibleExpenseFromQueue();
  }
}

/**
 * Show the next flexible expense from queue (without removing it)
 */
function showNextFlexibleExpenseFromQueue() {
  console.log('🔄 Showing next flexible expense from queue...');
  
  // If there are items in queue, show the first one
  if (window.flexibleExpenseQueue && window.flexibleExpenseQueue.length > 0) {
    const nextItem = window.flexibleExpenseQueue[0];
    console.log(`📋 Showing flexible expense: ${nextItem.recurringExpense.label}`);
    
    // Show the modal
    window.currentFlexiblePrompt = nextItem;
    window.isShowingFlexibleModal = true;
    showFlexibleExpenseModal(nextItem);
  }
}

/**
 * Show success notification for budget save
 */
function showSaveSuccessNotification() {
  // Create notification element
  const notification = document.createElement('div');
  notification.className = 'save-success-notification';
  notification.innerHTML = `
    <div class="notification-content">
      <div class="notification-icon">✅</div>
      <div class="notification-text">
        <strong>Saved Successfully</strong>
      </div>
    </div>
  `;

  // Add to page
  document.body.appendChild(notification);

  // Auto-remove after 2 seconds
  setTimeout(() => {
    if (notification.parentElement) {
      notification.remove();
    }
  }, 2000);
}

/**
 * Show notification for auto-logged expense
 */
function showAutoLogNotification(recurringExpense, dueDate, source = 'cloudFunction') {
  const sourceEmoji = source === 'cloudFunction' ? '☁️' : '💻';
  const sourceLabel = source === 'cloudFunction' ? 'Cloud' : 'Client';
  
  // Create notification element
  const notification = document.createElement('div');
  notification.className = 'auto-log-notification';
  notification.innerHTML = `
    <div class="notification-content">
      <div class="notification-icon">✅</div>
      <div class="notification-text">
        <strong>Auto-logged:</strong> ${recurringExpense.label}<br>
        <small>${formatCurrency(recurringExpense.amount)} on ${dueDate.toLocaleDateString()}</small><br>
        <small style="color: #666;">${sourceEmoji} ${sourceLabel} processed</small>
      </div>
      <button class="notification-close" onclick="this.parentElement.parentElement.remove()">×</button>
    </div>
  `;

  // Add to page
  document.body.appendChild(notification);

  // Auto-remove after 6 seconds (slightly longer for extra info)
  setTimeout(() => {
    if (notification.parentElement) {
      notification.remove();
    }
  }, 6000);
}

/**
 * Show flexible expense modal
 */
function showFlexibleExpenseModal(promptData) {
  const modal = document.getElementById('flexible-expense-modal');
  
  if (!modal) {
    createFlexibleExpenseModal();
    return showFlexibleExpenseModal(promptData);
  }

  const { recurringExpense, targetDate, earliestDate, latestDate } = promptData;

  // Update modal content
  document.getElementById('flexible-expense-title').textContent = recurringExpense.label;
  document.getElementById('flexible-expense-amount-input').value = recurringExpense.amount.toFixed(2);
  document.getElementById('flexible-expense-target-date').textContent = targetDate.toLocaleDateString();
  document.getElementById('flexible-expense-window').textContent = 
    `${earliestDate.toLocaleDateString()} - ${latestDate.toLocaleDateString()}`;

  // Update queue info (show remaining items after current one)
  const queueInfo = document.getElementById('queue-info');
  if (queueInfo) {
    const remaining = window.flexibleExpenseQueue.length - 1; // Subtract 1 for current item
    if (remaining > 0) {
      queueInfo.textContent = `${remaining} more expense${remaining > 1 ? 's' : ''} pending`;
      queueInfo.style.display = 'block';
      queueInfo.classList.add('multiple');
    } else {
      queueInfo.style.display = 'none';
      queueInfo.classList.remove('multiple');
    }
  }

  // Show modal with flex display for centering
  modal.style.display = 'flex';
}

/**
 * Create flexible expense modal if it doesn't exist
 */
function createFlexibleExpenseModal() {
  const modalHTML = `
    <div class="flexible-expense-modal" id="flexible-expense-modal">
      <div class="flexible-modal-overlay" onclick="hideFlexibleExpenseModal()"></div>
      <div class="modal-content">
        <div class="modal-header">
          <h3>💰 Recurring Expense Due</h3>
          <div id="queue-info" class="queue-info" style="display: none;"></div>
          <button class="modal-close" onclick="hideFlexibleExpenseModal()">×</button>
        </div>
        <div class="modal-body">
          <div class="expense-details">
            <h4 id="flexible-expense-title">Expense Name</h4>
            <div class="expense-amount-input">
              <label class="amount-label">Amount:</label>
              <input 
                type="number" 
                id="flexible-expense-amount-input" 
                class="amount-input" 
                step="0.01" 
                min="0"
                placeholder="0.00"
              />
            </div>
            <div class="expense-info">
              <div class="info-row">
                <span class="info-label">Target Date:</span>
                <span id="flexible-expense-target-date">-</span>
              </div>
              <div class="info-row">
                <span class="info-label">Due Window:</span>
                <span id="flexible-expense-window">-</span>
              </div>
            </div>
          </div>
          
          <div class="expense-actions">
            <button class="btn primary log-now-btn">
              💸 Log Now
            </button>
            <button class="btn secondary snooze-btn">
              ⏰ Remind Tomorrow
            </button>
            <button class="btn tertiary dismiss-btn">
              ❌ Skip This Month
            </button>
          </div>
        </div>
      </div>
    </div>
  `;

  document.body.insertAdjacentHTML('beforeend', modalHTML);
  
  // Set up event listeners for the action buttons
  const modal = document.getElementById('flexible-expense-modal');
  const logButton = modal.querySelector('.log-now-btn');
  const snoozeButton = modal.querySelector('.snooze-btn');
  const dismissButton = modal.querySelector('.dismiss-btn');
  
  if (logButton) {
    logButton.onclick = (e) => {
      e.preventDefault();
      if (window.currentFlexiblePrompt) {
        // Get the edited amount from the input field
        const amountInput = document.getElementById('flexible-expense-amount-input');
        const editedAmount = parseFloat(amountInput.value) || window.currentFlexiblePrompt.recurringExpense.amount;
        
        logFlexibleExpenseNow(
          window.currentFlexiblePrompt.recurringExpense,
          window.currentFlexiblePrompt.targetDate,
          editedAmount,
          true
        );
      }
    };
  }
  
  if (snoozeButton) {
    snoozeButton.onclick = (e) => {
      e.preventDefault();
      if (window.currentFlexiblePrompt) {
        snoozeFlexibleExpense(
          window.currentFlexiblePrompt.recurringExpense,
          window.currentFlexiblePrompt.targetDate,
          true
        );
      }
    };
  }
  
  if (dismissButton) {
    dismissButton.onclick = (e) => {
      e.preventDefault();
      if (window.currentFlexiblePrompt) {
        dismissFlexibleExpense(
          window.currentFlexiblePrompt.recurringExpense,
          window.currentFlexiblePrompt.targetDate,
          true
        );
      }
    };
  }
}

/**
 * Hide flexible expense modal (treat as "Remind Tomorrow")
 */
function hideFlexibleExpenseModal() {
  // If there's a current prompt, treat closing as "snooze until tomorrow"
  if (window.currentFlexiblePrompt) {
    console.log('🔄 Modal closed with X, treating as "Remind Tomorrow"...');
    snoozeFlexibleExpense(
      window.currentFlexiblePrompt.recurringExpense,
      window.currentFlexiblePrompt.targetDate,
      true // Process queue after snoozing
    );
  } else {
    // No current prompt, just hide modal
    const modal = document.getElementById('flexible-expense-modal');
    if (modal) {
      modal.style.display = 'none';
    }
    window.isShowingFlexibleModal = false;
  }
}


/**
 * Log flexible expense now (1-click action)
 */
async function logFlexibleExpenseNow(recurringExpense = null, dueDate = null, editedAmount = null, processQueue = false) {
  try {
    // Use passed parameters or fallback to current prompt
    let expense = recurringExpense;
    let targetDate = dueDate;
    let amount = editedAmount;
    
    if (!expense && window.currentFlexiblePrompt) {
      expense = window.currentFlexiblePrompt.recurringExpense;
      targetDate = new Date();
    }
    
    if (!expense) return;

    // Create a modified expense object with the edited amount if provided
    let expenseToLog = expense;
    if (amount !== null && amount !== expense.amount) {
      expenseToLog = {
        ...expense,
        amount: amount
      };
      console.log(`💰 Using edited amount: ${amount} (original: ${expense.amount})`);
    }

    await autoLogRecurringExpense(expenseToLog, null, targetDate || new Date(), true);
    
    // Process queue if requested
    if (processQueue) {
      processFlexibleExpenseQueue();
    } else {
      // Hide modal directly without calling hideFlexibleExpenseModal() to avoid loops
      const modal = document.getElementById('flexible-expense-modal');
      if (modal) {
        modal.style.display = 'none';
      }
      window.currentFlexiblePrompt = null;
      window.isShowingFlexibleModal = false;
    }
    
    // Refresh expense tracker if visible
    if (document.getElementById('expense-tracker-mode').style.display !== 'none') {
      // Trigger refresh of expense tracker data
      setTimeout(() => {
        if (typeof window.refreshExpenseTracker === 'function') {
          window.refreshExpenseTracker();
        }
      }, 100);
    }

  } catch (error) {
    console.error('Error logging flexible expense:', error);
    alert('Error logging expense. Please try again.');
  }
}

/**
 * Snooze flexible expense until tomorrow
 */
async function snoozeFlexibleExpense(recurringExpense = null, dueDate = null, processQueue = false) {
  try {
    // Use passed parameters or fallback to current prompt
    let expense = recurringExpense;
    
    if (!expense && window.currentFlexiblePrompt) {
      expense = window.currentFlexiblePrompt.recurringExpense;
    }
    
    if (!expense) return;
    
    // Store snooze until tomorrow in Firestore instead of localStorage
    const user = getCurrentUser();
    if (!user) return;
    
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const budgetId = getCurrentBudgetId();
    
    const snoozeData = {
      recurringExpenseId: expense.id,
      budgetId,
      snoozeUntil: Timestamp.fromDate(tomorrow),
      createdAt: Timestamp.fromDate(new Date()),
      userId: user.uid
    };
    
    const snoozeRef = collection(db, `users/${user.uid}/flexibleExpenseSnoozes`);
    await addDoc(snoozeRef, snoozeData);
    
    console.log(`Snoozed flexible expense ${expense.id} until ${tomorrow.toDateString()}`);
    
    // Process queue if requested
    if (processQueue) {
      processFlexibleExpenseQueue();
    } else {
      // Hide modal directly without calling hideFlexibleExpenseModal() to avoid loops
      const modal = document.getElementById('flexible-expense-modal');
      if (modal) {
        modal.style.display = 'none';
      }
      window.currentFlexiblePrompt = null;
      window.isShowingFlexibleModal = false;
    }
    
  } catch (error) {
    console.error('Error snoozing flexible expense:', error);
  }
}

/**
 * Dismiss flexible expense for this month
 */
async function dismissFlexibleExpense(recurringExpense = null, dueDate = null, processQueue = false) {
  try {
    // Use passed parameters or fallback to current prompt
    let expense = recurringExpense;
    
    if (!expense && window.currentFlexiblePrompt) {
      expense = window.currentFlexiblePrompt.recurringExpense;
    }
    
    if (!expense) return;

    const user = getCurrentUser();
    if (!user) return;

    const now = new Date();
    const month = now.toISOString().slice(0, 7);

    // Record dismissal in Firestore
    const dismissalsRef = collection(db, `users/${user.uid}/recurringDismissals`);
    await addDoc(dismissalsRef, {
      recurringId: expense.id,
      month,
      dismissedAt: Timestamp.fromDate(now),
      reason: 'userDismissed'
    });
    
    // Process queue if requested
    if (processQueue) {
      processFlexibleExpenseQueue();
    } else {
      // Hide modal directly without calling hideFlexibleExpenseModal() to avoid loops
      const modal = document.getElementById('flexible-expense-modal');
      if (modal) {
        modal.style.display = 'none';
      }
      window.currentFlexiblePrompt = null;
      window.isShowingFlexibleModal = false;
    }
    
  } catch (error) {
    console.error('Error dismissing flexible expense:', error);
    alert('Error dismissing expense. Please try again.');
  }
}

/**
 * Process the next item in the flexible expense queue
 */
function processFlexibleExpenseQueue() {
  console.log('🔄 Processing flexible expense queue...');
  
  // Remove the current item from queue if it exists
  if (window.flexibleExpenseQueue && window.flexibleExpenseQueue.length > 0) {
    window.flexibleExpenseQueue.shift(); // Remove first item
  }
  
  // If there are more items, show the next one
  if (window.flexibleExpenseQueue && window.flexibleExpenseQueue.length > 0) {
    const nextItem = window.flexibleExpenseQueue[0];
    console.log(`📋 Showing next item in queue: ${nextItem.recurringExpense.label}`);
    
    // Show the next modal
    window.currentFlexiblePrompt = nextItem;
    showFlexibleExpenseModal(nextItem);
  } else {
    // Queue is empty, hide modal and reset state
    console.log('✅ Queue processing complete');
    const modal = document.getElementById('flexible-expense-modal');
    if (modal) {
      modal.style.display = 'none';
    }
    window.currentFlexiblePrompt = null;
    window.isShowingFlexibleModal = false;
  }
}

/**
 * Initialize auto-logging system (hybrid approach)
 * Cloud Functions handle hourly processing, client handles missed items and new additions
 */
// Module-level handles so we can cancel on user switch
let _autoLogInterval = null;
let _autoLogTimeout = null;

export function initializeAutoLogging() {
  // Cancel any timers from a previous session before creating new ones
  stopAutoLogging();

  const user = getCurrentUser();
  if (!user) {
    console.warn('initializeAutoLogging called with no authenticated user — skipping');
    return;
  }
  const expectedUid = user.uid;
  console.log(`🚀 Initializing auto-logging for user ${expectedUid}`);

  // Helper: run processing only if the same user is still signed in
  function runIfSameUser(label) {
    const current = getCurrentUser();
    if (!current || current.uid !== expectedUid) {
      console.log(`⏭️ Skipping ${label} — user changed (expected ${expectedUid})`);
      return;
    }
    processRecurringExpensesClientSide().catch(error => {
      console.error(`❌ Error during ${label}:`, error);
    });
  }

  _autoLogTimeout = setTimeout(() => {
    runIfSameUser('startup recurring expense processing');
  }, 2000);

  const sixHours = 6 * 60 * 60 * 1000;
  _autoLogInterval = setInterval(() => {
    runIfSameUser('periodic recurring expense processing');
  }, sixHours);
}

export function stopAutoLogging() {
  if (_autoLogTimeout !== null) {
    clearTimeout(_autoLogTimeout);
    _autoLogTimeout = null;
  }
  if (_autoLogInterval !== null) {
    clearInterval(_autoLogInterval);
    _autoLogInterval = null;
  }
}

/**
 * Client-side processing for missed items and immediate needs
 * More conservative than cloud function - focuses on gaps and new items
 */
async function processRecurringExpensesClientSide() {
  console.log('🔄 Client-side processing recurring expenses...');

  const user = getCurrentUser();
  if (!user) {
    console.log('No authenticated user — skipping recurring expense processing');
    return;
  }
  const uid = user.uid;

  try {
    const recurringExpenses = getRecurringExpenses();

    if (!recurringExpenses || recurringExpenses.length === 0) {
      console.log('No recurring expenses found');
      return;
    }

    const now = new Date();
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);

    let processedCount = 0;
    let flexiblePrompts = 0;

    for (const recurringExpense of recurringExpenses) {
      // Re-check user hasn't changed mid-loop (e.g. very quick account switch)
      if (getCurrentUser()?.uid !== uid) {
        console.warn('User changed during processing — aborting');
        return;
      }
      const result = await processIndividualRecurringClientSide(recurringExpense, today);
      if (result.processed) processedCount++;
      if (result.flexiblePrompt) flexiblePrompts++;
    }

    console.log(`✅ Client-side processing complete: ${processedCount} expenses processed, ${flexiblePrompts} flexible prompts shown`);

    // Scope the timestamp to the user so it doesn't gate a different account
    localStorage.setItem(`lastClientRecurringProcess_${uid}`, now.toISOString());

  } catch (error) {
    console.error('❌ Error in client-side recurring expense processing:', error);
  }
}

/**
 * Enhanced client-side individual processing with gap detection
 */
async function processIndividualRecurringClientSide(recurringExpense, today) {
  const config = recurringExpense.config;
  if (!config || !config.type) return { processed: false, flexiblePrompt: false };

  console.log(`Processing ${config.type} recurring expense: ${recurringExpense.label}`);

  switch (config.type) {
    case 'fixed':
    case 'relative':
      return await processAutoLogRecurringClientSide(recurringExpense, today);
    case 'flexible':
      return await processFlexibleRecurringClientSide(recurringExpense, today);
    default:
      console.warn('Unknown recurring type:', config.type);
      return { processed: false, flexiblePrompt: false };
  }
}

/**
 * Client-side auto-log processing with gap detection and creation date filtering
 */
async function processAutoLogRecurringClientSide(recurringExpense, today) {
  // Get creation date of recurring expense
  const createdAt = recurringExpense.config.createdAt ? new Date(recurringExpense.config.createdAt) : null;
  
  // Check for gaps in the last 90 days
  const ninetyDaysAgo = new Date(today);
  ninetyDaysAgo.setDate(today.getDate() - 90);

  // Use creation date as start if it's more recent than 90 days ago
  let startDate = ninetyDaysAgo;
  if (createdAt && createdAt > ninetyDaysAgo) {
    startDate = createdAt;
    console.log(`📅 Using creation date ${createdAt.toDateString()} as start for ${recurringExpense.label}`);
  }
  
  const instances = generateRecurringExpenseInstances(recurringExpense, startDate, today);
  
  let processedAny = false;
  
  for (const instance of instances) {
    const dueDate = new Date(instance.dueDate);
    dueDate.setHours(0, 0, 0, 0);
    
    // Only process if due date is today or in the past, and AFTER creation date
    // Normalize creation date to start of day for fair comparison
    let createdAtNormalized = null;
    if (createdAt) {
      createdAtNormalized = new Date(createdAt);
      createdAtNormalized.setHours(0, 0, 0, 0);
    }
    
    if (dueDate <= today && (!createdAtNormalized || dueDate >= createdAtNormalized)) {
      // Check if already logged
      const alreadyLogged = await checkIfExpenseAlreadyLogged(recurringExpense, dueDate);
      
      if (!alreadyLogged) {
        await autoLogRecurringExpenseClientSide(recurringExpense, instance, dueDate);
        console.log(`✅ Client auto-logged: ${recurringExpense.label} for ${dueDate.toDateString()}`);
        processedAny = true;
      }
    } else if (createdAtNormalized && dueDate < createdAtNormalized) {
      console.log(`⏭️ Skipping ${recurringExpense.label} for ${dueDate.toDateString()} - before creation date ${createdAtNormalized.toDateString()}`);
    }
  }
  
  return { processed: processedAny, flexiblePrompt: false };
}

/**
 * Enhanced flexible processing with snooze handling and creation date filtering
 */
async function processFlexibleRecurringClientSide(recurringExpense, today) {
  // Check creation date - don't prompt if expense was created after the current month's due window
  const createdAt = recurringExpense.config.createdAt ? new Date(recurringExpense.config.createdAt) : null;
  
  const config = recurringExpense.config;
  const dueDay = parseInt(config.dueDay) || 1;
  const flexibility = parseInt(config.flexibility) || 1;
  
  // Calculate due window for current month
  const targetDate = new Date(today.getFullYear(), today.getMonth(), dueDay);
  const earliestDate = new Date(targetDate);
  earliestDate.setDate(targetDate.getDate() - flexibility);
  const latestDate = new Date(targetDate);
  latestDate.setDate(targetDate.getDate() + flexibility);
  
  // If created after the current month's due window ended, don't prompt this month
  if (createdAt && createdAt > latestDate) {
    console.log(`⏭️ Skipping flexible prompt for ${recurringExpense.label} - created after this month's due window`);
    return { processed: false, flexiblePrompt: false };
  }
  
  // Check for snooze in Firestore
  const user = getCurrentUser();
  if (user) {
    const snoozeRef = collection(db, `users/${user.uid}/flexibleExpenseSnoozes`);
    const snoozeQuery = query(
      snoozeRef,
      where('recurringExpenseId', '==', recurringExpense.id),
      where('snoozeUntil', '>', Timestamp.fromDate(today))
    );
    
    const snoozeSnapshot = await getDocs(snoozeQuery);
    if (!snoozeSnapshot.empty) {
      console.log(`Flexible expense ${recurringExpense.label} is still snoozed`);
      return { processed: false, flexiblePrompt: false }; // Still snoozed
    }
  }
  
  // Check if today is within the flexible window and after creation date
  // Normalize creation date to start of day for fair comparison
  let createdAtNormalized = null;
  if (createdAt) {
    createdAtNormalized = new Date(createdAt);
    createdAtNormalized.setHours(0, 0, 0, 0);
  }
  
  if (today >= earliestDate && today <= latestDate && (!createdAtNormalized || today >= createdAtNormalized)) {
    // Check if already handled this month
    const alreadyHandled = await checkIfFlexibleExpenseHandled(recurringExpense, today);
    
    if (!alreadyHandled) {
      await showFlexibleExpensePrompt(recurringExpense, targetDate, earliestDate, latestDate);
      return { processed: false, flexiblePrompt: true };
    }
  }
  
  return { processed: false, flexiblePrompt: false };
}

/**
 * Client-side auto-logging with enhanced metadata
 */
async function autoLogRecurringExpenseClientSide(recurringExpense, instance, dueDate) {
  try {
    const user = getCurrentUser();
    if (!user) throw new Error('No user authenticated');

    const budgetId = getCurrentBudgetId();
    const now = new Date();
    
    // Set expense time to 12:00 PM on the due date
    const expenseDateTime = new Date(dueDate);
    expenseDateTime.setHours(12, 0, 0, 0);

    // Convert category name to numeric categoryId for compatibility with expense tracker
    const budgetData = getCurrentBudgetData();
    const categoryId = budgetData?.categories?.findIndex(cat => cat.name === recurringExpense.categoryName) ?? recurringExpense.categoryName;

    // Resolve the line item index within the category so remaining-amount queries can match it
    const category = categoryId >= 0 ? budgetData?.categories?.[categoryId] : null;
    const lineItemIndex = category?.items?.findIndex(item => item.label === recurringExpense.label) ?? -1;
    const lineItemId = lineItemIndex >= 0 ? String(lineItemIndex) : 'auto-recurring';

    const expenseData = {
      budgetId,
      categoryId: categoryId,
      lineItemId,
      lineItemName: recurringExpense.label,
      amount: recurringExpense.amount,
      description: 'auto-logged',
      date: Timestamp.fromDate(expenseDateTime),
      month: dueDate.toISOString().slice(0, 7),
      isUnplanned: false,
      isTaxDeductible: false,
      isRecurringPayment: true,
      recurringId: recurringExpense.id,
      recurringType: recurringExpense.config.type,
      autoLogged: true,
      
      // Enhanced metadata for tracking
      autoLoggedBy: 'clientSide',
      autoLoggedAt: Timestamp.fromDate(now),
      processedDate: Timestamp.fromDate(now),
      
      // Gap detection metadata
      isGapFill: (now.getTime() - dueDate.getTime()) > (24 * 60 * 60 * 1000), // More than 1 day late
      
      // Original recurring configuration for reference
      originalRecurringConfig: recurringExpense.config,
      
      createdAt: Timestamp.fromDate(now),
      updatedAt: Timestamp.fromDate(now)
    };

    // Use the existing expense submission system
    const expensesRef = collection(db, `users/${user.uid}/expenses`);
    await addDoc(expensesRef, expenseData);

    // Show notification to user
    showAutoLogNotification(recurringExpense, dueDate, expenseData.autoLoggedBy);

  } catch (error) {
    console.error('Error auto-logging recurring expense on client-side:', error);
    throw error;
  }
}

/**
 * Manual trigger function for testing
 */
export async function manualProcessRecurringExpenses() {
  console.log('🔧 Manual recurring expense processing triggered...');
  try {
    await processRecurringExpensesClientSide();
    return { success: true };
  } catch (error) {
    console.error('Error in manual processing:', error);
    throw error;
  }
}

/**
 * Get processing status and statistics
 */
export function getRecurringExpenseStatus() {
  const lastClientProcess = localStorage.getItem('lastClientRecurringProcess');
  const recurringExpenses = getRecurringExpenses();
  
  return {
    totalRecurringExpenses: recurringExpenses.length,
    lastClientProcessing: lastClientProcess ? new Date(lastClientProcess) : null,
    cloudFunctionEnabled: false,
    recurringByType: recurringExpenses.reduce((acc, expense) => {
      const type = expense.config?.type || 'unknown';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {})
  };
}

/**
 * Make auto-logging functions globally available
 */
if (typeof window !== 'undefined') {
  window.hideFlexibleExpenseModal = hideFlexibleExpenseModal;
  window.logFlexibleExpenseNow = logFlexibleExpenseNow;
  window.snoozeFlexibleExpense = snoozeFlexibleExpense;
  window.dismissFlexibleExpense = dismissFlexibleExpense;
  window.processRecurringExpenses = processRecurringExpenses;
  window.generateRecurringExpenseInstances = generateRecurringExpenseInstances;
  window.checkIfExpenseAlreadyLogged = checkIfExpenseAlreadyLogged;
  window.getDaysInMonth = getDaysInMonth;
  window.debugCategoryIssue = debugCategoryIssue;
  window.debugExpenseTrackerCategory = debugExpenseTrackerCategory;
  window.navigateCalendar = navigateCalendar;
}

/**
 * Debug function to check category ID issues
 */
async function debugCategoryIssue() {
  try {
    const user = getCurrentUser();
    if (!user) {
      console.log('No user authenticated');
      return;
    }
    
    // Check current budget data categories
    const budgetData = getCurrentBudgetData();
    console.log('=== BUDGET CATEGORIES ===');
    if (budgetData && budgetData.categories) {
      budgetData.categories.forEach((cat, index) => {
        console.log(`Category ${index}: "${cat.name}"`);
      });
    }
    
    // Check recent auto-logged expenses
    console.log('\n=== RECENT AUTO-LOGGED EXPENSES ===');
    const expensesRef = collection(db, `users/${user.uid}/expenses`);
    const q = query(expensesRef, where('description', '==', 'auto-logged'), orderBy('createdAt', 'desc'), limit(3));
    const snapshot = await getDocs(q);
    
    snapshot.forEach(doc => {
      const expense = doc.data();
      console.log('Expense:', {
        categoryId: expense.categoryId,
        lineItemName: expense.lineItemName,
        amount: expense.amount,
        month: expense.month,
        date: expense.date.toDate().toDateString()
      });
    });
    
  } catch (error) {
    console.error('Debug error:', error);
  }
}

/**
 * Debug function specifically for expense tracker category issues
 */
async function debugExpenseTrackerCategory() {
  try {
    const user = getCurrentUser();
    const budgetId = getCurrentBudgetId();
    const categoryId = 0; // Housing
    const month = '2025-08';
    
    console.log('=== EXPENSE TRACKER CATEGORY DEBUG ===');
    console.log('Looking for expenses with:');
    console.log('- categoryId:', categoryId);
    console.log('- month:', month);
    console.log('- budgetId:', budgetId);
    
    // Check what expenses match the exact query used by getCategoryExpenseTotal
    const expensesRef = collection(db, `users/${user.uid}/expenses`);
    const q = query(
      expensesRef,
      where('budgetId', '==', budgetId),
      where('categoryId', '==', categoryId),
      where('month', '==', month)
    );
    
    const snapshot = await getDocs(q);
    console.log(`\n=== MATCHING EXPENSES (${snapshot.size} found) ===`);
    
    let total = 0;
    snapshot.forEach(doc => {
      const expense = doc.data();
      total += expense.amount || 0;
      console.log('Matching expense:', {
        lineItemName: expense.lineItemName,
        amount: expense.amount,
        description: expense.description,
        date: expense.date.toDate().toDateString()
      });
    });
    
    console.log('Total amount found:', total);
    
    // Also check all August expenses to see what might not be matching
    const allAugustQuery = query(expensesRef, where('month', '==', month));
    const allSnapshot = await getDocs(allAugustQuery);
    console.log(`\n=== ALL AUGUST EXPENSES (${allSnapshot.size} found) ===`);
    
    allSnapshot.forEach(doc => {
      const expense = doc.data();
      const budgetMatch = expense.budgetId === budgetId;
      const categoryMatch = expense.categoryId === categoryId;
      const monthMatch = expense.month === month;
      
      console.log('Expense:', {
        lineItemName: expense.lineItemName,
        budgetId: expense.budgetId,
        categoryId: expense.categoryId,
        month: expense.month,
        amount: expense.amount,
        budgetMatch,
        categoryMatch,
        monthMatch,
        allMatch: budgetMatch && categoryMatch && monthMatch
      });
    });
    
  } catch (error) {
    console.error('Debug error:', error);
  }
}

